import type { Transport } from "@empite/api-client";

/**
 * The Admin API's surface, as typed functions.
 *
 * HAND-WRITTEN, unlike every other client in this repo. scripts/contractgen
 * emits @empite/api-client from contracts/openapi, and that document
 * describes the customer and inference surfaces only — the admin realm has
 * no contract file, so there is nothing to generate from. This module is the
 * hand-maintained counterpart: same RequestSpec shape, same transport, same
 * errors, so the day an admin contract lands it can be deleted and replaced
 * by generated code without a single screen changing.
 *
 * Everything here targets /admin/v1. Nothing here may target /v1 or
 * /platform/v1: those are the customer realm, authenticated by a different
 * cookie, and a back office that can reach them with an operator's
 * credential has quietly become a privilege-escalation route.
 */

/** The prefix, in one place, so no call site can drift onto another realm. */
const ADMIN = "/admin/v1";

/**
 * The reason headers as this build knows them.
 *
 * They are also announced by GET /admin/v1/reason-codes, and the value the
 * server announces wins (see `Reason`): the vocabulary document is the
 * contract, these constants are only what to send before it has loaded.
 */
export const REASON_CODE_HEADER = "X-Admin-Reason-Code";
export const REASON_NOTE_HEADER = "X-Admin-Reason-Note";

/**
 * Reason is the justification a mutation carries.
 *
 * The server refuses any unsafe method without one (internal/admin's
 * RequireReason middleware guards the whole subtree), so this is not a UI
 * convention that a screen may opt out of — it is the request being
 * well-formed. The header names travel with the value so a server that
 * renames them keeps working without a portal release.
 */
export interface Reason {
  code: string;
  note?: string;
  codeHeader?: string;
  noteHeader?: string;
}

function reasonHeaders(reason: Reason): Record<string, string | undefined> {
  return {
    [reason.codeHeader ?? REASON_CODE_HEADER]: reason.code,
    // undefined rather than "": the transport drops undefined headers, and an
    // empty note header is a note the server would have to decide about.
    [reason.noteHeader ?? REASON_NOTE_HEADER]: reason.note?.trim() || undefined,
  };
}

// --------------------------------------------------------------------------
// The realm's own identity
// --------------------------------------------------------------------------

export interface Operator {
  id: string;
  email: string;
  display_name: string;
  status: string;
  roles: string[];
  /** Flattened from the operator's roles by the server. The portal renders
   *  controls from it; it never decides anything with it — see `can`. */
  permissions: string[];
  created_at: string;
  last_signed_in_at?: string;
  /** The tenant this operator belongs to. ABSENT for platform staff, whose
   *  reach is every tenant — the field's absence is the privilege, not a
   *  missing value. */
  tenant_id?: string;
  /** The tenant's slug and display name, absent alongside `tenant_id` for
   *  platform staff. Carried on the session so the shell can name the tenant
   *  on the first paint instead of fetching one to draw its own header. */
  tenant_slug?: string;
  tenant_name?: string;
}

export interface AdminSession {
  operator: Operator;
  session_id: string;
  expires_at: string;
  /** Present on GET /session only, and always "admin". Carried so a portal
   *  pointed at the wrong deployment fails loudly rather than plausibly. */
  realm?: string;
}

export interface SignInBody {
  email: string;
  password: string;
}

export interface ReasonVocabulary {
  reason_codes: string[];
  code_header: string;
  note_header: string;
  /** The codes that cannot be sent without a note. */
  note_required: string[];
  max_note_chars: number;
}

// --------------------------------------------------------------------------
// Self-signup and the tenant's own team
// --------------------------------------------------------------------------

export interface SignUpBody {
  company_name: string;
  email: string;
  /** Optional. Blank means the server names the operator after the local part
   *  of their address rather than storing an empty name. */
  display_name?: string;
  password: string;
}

/**
 * What a signup provisioned — a tenant, its owner, its suite and its free
 * tier — and deliberately NOT a session. POST /signup sets no cookie; the new
 * operator signs in through POST /session like everybody else, so the status
 * check, the roles check and the sign-in audit row have one implementation.
 */
export interface SignUpResult {
  operator: Operator;
  tenant: { id: string; slug: string; name: string; status: string };
  suite: { id: string; slug: string; name: string };
  subscription: {
    id: string;
    plan_slug: string;
    status: string;
    current_period_end: string;
  };
}

/** A seat this tenant has offered. Carries NO token: the raw value exists
 *  exactly once, in the 201 that created it, and the table holds only its
 *  keyed hash — see InviteResult. */
export interface TeamInvitation {
  id: string;
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  email: string;
  role: string;
  status: string;
  /** Status AND expiry, resolved server-side. The portal must not compare
   *  expires_at against the browser's clock: a machine with the wrong time
   *  would call an invitation dead that the server would still accept. */
  live: boolean;
  invited_by: string;
  invited_by_email?: string;
  expires_at: string;
  created_at: string;
  accepted_at?: string;
  revoked_at?: string;
}

export interface NewInvitation {
  email: string;
  role: string;
  /** Only for platform staff, who belong to no tenant and must name the one
   *  they are inviting into. A tenant operator sending it is refused rather
   *  than obeyed, so it cannot move a seat across the boundary. */
  tenant_id?: string;
}

/** The 201 from POST /tenant/invites — the row, plus the one and only time
 *  the raw token is readable, so the inviter can pass on a link when mail is
 *  not working. Every later read returns the row without it. */
export interface InvitationCreated {
  invitation: TeamInvitation;
  invite_token: string;
}

/**
 * One of the tenant's API keys — the credential its OWN backend presents in
 * `X-Tenant-Api-Key` to /v1/tenant. Never the key: the table holds a digest,
 * and `key_prefix` is the only part of the plaintext any read returns.
 */
export interface TenantAPIKey {
  id: string;
  tenant_id: string;
  name: string;
  key_prefix: string;
  status: string;
  /** Status AND expiry, resolved server-side — see TeamInvitation.live. */
  live: boolean;
  created_by?: string;
  created_at: string;
  expires_at?: string;
  last_used_at?: string;
  revoked_at?: string;
}

export interface NewTenantAPIKey {
  name: string;
  /** Zero or absent means the key never expires; at most a year. */
  expires_in_seconds?: number;
  /** Platform staff only — see NewInvitation.tenant_id. */
  tenant_id?: string;
}

export interface TenantAPIKeyCreated {
  api_key: TenantAPIKey;
  /** The plaintext, returned here and nowhere else. */
  key: string;
}

export interface AcceptInviteBody {
  token: string;
  display_name?: string;
  password: string;
}

// --------------------------------------------------------------------------
// Applications, OAuth clients, entitlements
// --------------------------------------------------------------------------

export type EnabledStatus = "active" | "disabled";

export interface ApplicationSummary {
  id: string;
  slug: string;
  name: string;
  /**
   * The operator-facing note. `null` means nobody has ever written one; `""`
   * means somebody cleared it. The two are kept apart so the edit form can show
   * a placeholder for the first and an empty box for the second — see migration
   * 0031. It is never shown to customers; `name` is the customer-facing label.
   */
  description: string | null;
  status: EnabledStatus;
  suite_id: string;
  suite_slug: string;
  suite_name: string;
  /**
   * The owning company's handle — what a desktop client sends as `tenant`.
   *
   * Always this APPLICATION's tenant and never the reading operator's:
   * `client_id` is unique per tenant, so the guide has to name the namespace
   * the client was registered in, and platform staff reading a tenant's
   * application have no handle of their own to offer.
   */
  tenant_slug: string;
  oauth_client_count: number;
  entitlement_count: number;
  created_at: string;
}

export interface OAuthClient {
  id: string;
  client_id: string;
  name: string;
  redirect_uris: string[];
  allowed_scopes: string[];
  status: EnabledStatus;
  require_pkce: boolean;
  token_endpoint_auth_method: string;
  created_at: string;
}

export interface ApplicationEntitlement {
  id: string;
  subject_type: "user" | "organization";
  subject_id: string;
  subject_label: string;
  scopes: string[];
  status: string;
  /**
   * Which release ring this subject is in for this application.
   *
   * It is one half of the per-application model availability: this says which
   * ring a PERSON is in, and a routing profile's model audiences say which ring
   * an ALIAS needs. `live` is the default and means an ordinary member of the
   * public. Always present — the column is NOT NULL — so there is no "unset"
   * for a screen to have to interpret.
   */
  tier: AudienceTier;
  granted_at: string;
  expires_at?: string;
}

/**
 * The release rings, MOST PRIVILEGED FIRST, and they are NESTED rather than
 * parallel: internal ⊇ tester ⊇ live. An internal caller sees the tester ring
 * and the public one as well as their own.
 *
 * The order of this union is the order the console offers them in, and it is
 * also the order the server's `auth.Audiences` declares — a control that
 * offered them in a different order would read as three unrelated choices
 * rather than as a ladder.
 */
export type AudienceTier = "internal" | "tester" | "live";

/** Every ring, in ladder order, for a control that has to offer all three. */
export const AUDIENCE_TIERS: readonly AudienceTier[] = [
  "internal",
  "tester",
  "live",
] as const;

/**
 * One alias's release ring within one routing profile.
 *
 * Keyed on the ALIAS and not on a model policy id, because one alias is several
 * policy rows — one per provider in the fallback chain — and the release is a
 * decision about the alias the caller names, not about which upstream happens
 * to serve it.
 */
export interface ModelAudience {
  model: string;
  audience: AudienceTier;
}

/**
 * One model a provider says it offers, read live from the vendor.
 *
 * Every optional field is `| null` and NOT optional: the server always sends
 * the key, and null means "the vendor did not say". A form prefilling from this
 * has to tell that apart from a stated zero, and a key that vanished would
 * collapse the two.
 *
 * How much comes back varies enormously by vendor. OpenRouter states a context
 * length and what parameters a model accepts; Gemini states token limits;
 * Anthropic states an id and a display name; OpenAI states barely an id. The
 * picker's job is to remove the typo in the model id — everything past that is
 * a bonus the vendor either grants or does not.
 */
export interface DiscoveredModel {
  model: string;
  display_name: string;
  family: string;
  context_window: number | null;
  max_input_tokens: number | null;
  max_output_tokens: number | null;
  /** Only what the vendor POSITIVELY states. A capability absent here is
   *  unrecorded, never "we checked and it cannot". */
  capabilities: string[];
  /** True when routing.provider_models already holds this (provider, model).
   *  The picker marks it rather than hiding it: an operator looking for a model
   *  they added last week needs to see it, and to see that it is already
   *  there. */
  catalogued: boolean;
  /** The catalogue row's status when `catalogued`, empty otherwise. */
  status: string;
}

/**
 * The two deployment origins an integrating desktop client talks to. Neither
 * is the admin API's own: this console is simply where an operator is standing
 * when they need them.
 */
export interface IntegrationEndpoints {
  platform_base_url: string;
  customer_portal_url: string;
}

export interface ApplicationDetail {
  application: ApplicationSummary;
  oauth_clients: OAuthClient[];
  entitlements: ApplicationEntitlement[];
  integration: IntegrationEndpoints;
}

/**
 * The register-an-application form.
 *
 * `slug` is the lowercase handle the seed, the smoke gate and every runbook
 * name this product by — lowercase letters, digits and single hyphens — and it
 * is IMMUTABLE once written. A duplicate is refused 409; the index is global
 * rather than per suite.
 *
 * `suite_id` is optional and normally omitted: a tenant has one suite and the
 * server resolves it from the operator's own tenant. Platform staff carry no
 * tenant and a tenant may grow a second suite; in both cases the server stops
 * guessing and requires the field, because an application filed under the wrong
 * tenant is invisible until somebody notices the wrong SSO scope.
 *
 * The application is created DISABLED, against the column's own default. It has
 * no OAuth client yet, so nothing can authorise against it; enabling it is a
 * separate reason-carrying act by an operator who has finished the job. Use
 * setApplicationStatus.
 */
export interface NewApplication {
  slug: string;
  name: string;
  description?: string;
  suite_id?: string;
}

/**
 * The edit form. Omitting a field leaves the column alone.
 *
 * `slug` may be SENT — a form that round-trips the whole row does — but not
 * CHANGED: echoing the current value is a success, moving it is a 409 carrying
 * `field`, `current_value` and `submitted_value`. There is no `suite_id` here
 * at all; moving an application between tenants is not an edit.
 *
 * Clearing the description is `description: ""`, not `null`. `null` reads as
 * "leave it", because the column keeps `null` for "nobody has ever written one"
 * and the empty string for "somebody cleared it".
 */
export interface ApplicationEdit {
  slug?: string;
  name?: string;
  description?: string;
}

/**
 * The register-a-client form.
 *
 * There is no status, no `require_pkce` and no `token_endpoint_auth_method`:
 * every client here is a PUBLIC client with no secret and PKCE required, which
 * the schema spells as a CHECK rather than a default so it cannot be turned off
 * later either. The client is created ACTIVE — unlike its application, it is
 * complete at birth — though its application's own status still gates it.
 *
 * `client_id` is unique platform-wide (a collision is 409) and immutable, since
 * every installed copy of the desktop app sends it on every authorize call.
 *
 * `redirect_uris` must each be absolute and either `https` or `http` on a
 * loopback host (`127.0.0.0/8`, `[::1]`, `localhost`), with no fragment and no
 * wildcard; a failure is a 400 naming the offending uri in a `redirect_uri`
 * extension member. Two spellings of one endpoint within the list are a 400
 * too. A NON-loopback uri already registered to another client is a 409 naming
 * the owner — loopback callbacks are exempt, because RFC 8252 §7.3 makes the
 * port ephemeral and two desktop apps legitimately share one.
 *
 * `allowed_scopes` may not be empty: an authorize call naming no scope is
 * granted the client's whole allow-list, so a client with an empty one signs a
 * user in and then holds nothing.
 */
export interface NewOAuthClient {
  client_id: string;
  name: string;
  allowed_scopes: string[];
  redirect_uris: string[];
}

/**
 * The client edit form. `allowed_scopes` REPLACES the list whole rather than
 * adding to it, so two operators editing one client from two tabs cannot each
 * apply a delta to a list neither last read.
 *
 * `client_id` may be echoed but not changed (409, as for an application slug).
 * `redirect_uris` is not here — replacing a callback list is its own route,
 * because it is the edit that can lock a fleet out of signing in and it wants
 * its own audit action carrying the before and after lists.
 */
export interface OAuthClientEdit {
  client_id?: string;
  name?: string;
  allowed_scopes?: string[];
}

// --------------------------------------------------------------------------
// Users, devices, bindings, sessions
// --------------------------------------------------------------------------

export type UserStatus = "active" | "suspended" | "deleted";

export interface UserSummary {
  id: string;
  display_name: string;
  primary_email: string;
  status: UserStatus;
  registration_state: string;
  /** The last digits, and only the last digits. The platform stores the
   *  number encrypted and never returns a readable one, here or anywhere. */
  phone_last_digits: string;
  created_at: string;
  last_login_at?: string;
  /** The Empite-developer marker. Not optional: the Admin API sends the key
   *  even when the answer is false, and a `boolean | undefined` here would let
   *  a screen render "not a developer" for a response that never said. */
  is_empite_developer: boolean;
}

export interface UserEmail {
  id: string;
  address: string;
  is_primary: boolean;
  verified: boolean;
}

export interface UserPhone {
  last_digits: string;
  country_code: string;
  verified: boolean;
}

/**
 * One INSTALLATION as the back office sees it.
 *
 * Two independent facts live here and conflating them is the mistake this
 * comment exists to prevent:
 *
 * - `status` describes the INSTALLATION. "active" means it may be bound;
 *   "revoked" means it was BLOCKED and can never bind again, because
 *   installation_id is globally unique. It says nothing about who is on it.
 * - `signed_in` / `occupant_*` describe the OCCUPANT — whoever currently holds
 *   a live binding. A device is nobody's until somebody signs in on it.
 *
 * The list a device appears in is "installations this user has ever been bound
 * to", revoked bindings included, so a row can perfectly well be "this user
 * used it, someone else is on it now". `occupant_is_user` is the server's
 * answer to which of those a row is; do not re-derive it by comparing ids.
 */
export interface UserDevice {
  id: string;
  installation_id: string;
  platform: string;
  device_name: string;
  os_version: string;
  app_version: string;
  status: string;
  first_seen_at: string;
  last_seen_at: string;
  /** Somebody holds a live binding on this installation right now. */
  signed_in: boolean;
  /** The occupant is the user whose page this is (false when it is somebody
   *  else, and when nobody is signed in at all). */
  occupant_is_user: boolean;
  /** Absent when nobody is signed in. */
  occupant_user_id?: string;
  /** Why the installation is dead, when it is. `user_retired_device` is the
   *  customer's own — restorable by them and by an operator; anything else,
   *  `admin_revoke` above all, is final for everybody. Absent on an active
   *  installation and on rows retired before the reason was recorded. */
  revoked_reason?: string;
  /** The occupant's primary address. Operator-only: this is a support tool and
   *  "who else is on that laptop" is the question it is opened to answer. The
   *  customer-facing device list must never carry it. */
  occupant_email?: string;
  occupied_since?: string;
  /** The observed environment (Tier 3 device telemetry: re-read on every
   *  registration and every proof use, never part of the device checksum).
   *
   *  `null` and `""` are DIFFERENT and the server keeps them apart on purpose:
   *  `null` means no client has ever reported the field — render "not
   *  recorded" — while `""` means one reported a blank. Do not `??` them
   *  together, and do not treat a falsy value as missing. */
  hardware_model: string | null;
  cpu_architecture: string | null;
  hostname: string | null;
  locale: string | null;
  timezone: string | null;
  /** The address the LAST request from this installation arrived from, not the
   *  one it registered from. */
  last_seen_ip: string | null;
  last_seen_user_agent: string | null;
  /** The installation's signing-key history, newest first. Always present,
   *  possibly empty. */
  keys: DevicePublicKey[];
  /** The "was that you?" prompts raised for this INSTALLATION — for whoever
   *  was signing in on it, which is not necessarily the user whose page this
   *  is. Newest first, always present, possibly empty. */
  approvals: DeviceApproval[];
}

/**
 * One entry of an installation's signing-key history.
 *
 * There is no field for the key material, deliberately: the fingerprint is what
 * every other surface identifies a key by and what an operator compares against
 * a value read off a machine.
 *
 * Revoked keys are kept in the list rather than filtered out — "this device
 * rotated its key an hour before the incident" is one of the few things this
 * history can tell an agent.
 */
export interface DevicePublicKey {
  id: string;
  /** `ed25519` or `es256`. */
  algorithm: string;
  fingerprint: string;
  /** `active` or `revoked`. At most one key per device is active. */
  status: string;
  created_at: string;
  /** Absent on an active key. */
  revoked_at?: string;
  /** Separates a key superseded by a rotation from one revoked with its
   *  device. Absent on an active key and on rows retired before the reason was
   *  recorded. */
  revoked_reason?: string;
}

/**
 * One "was that you?" approval prompt raised for an installation.
 */
export interface DeviceApproval {
  id: string;
  application_id: string;
  application_name: string;
  /** `pending`, `approved`, `denied` or `expired`. */
  status: string;
  /** `low`, `normal` or `high`. */
  risk_level: string;
  /** When the prompt was raised. */
  requested_at: string;
  /** Absent while the prompt is pending, and on one that expired unanswered. */
  decided_at?: string;
  expires_at: string;
}

export interface UserBinding {
  id: string;
  application_id: string;
  application_name: string;
  device_id: string;
  status: string;
  approved_at?: string;
  last_used_at?: string;
  revoked_at?: string;
}

export interface UserAppSession {
  id: string;
  application_id: string;
  application_name: string;
  /** Absent for a session that is not bound to an installation — a browser
   *  sign-in has no device. Optional here because the server omits it. */
  device_id?: string;
  scopes: string[];
  ip?: string;
  user_agent?: string;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  revoked_at?: string;
}

export interface UserEntitlement {
  id: string;
  application_id: string;
  application_name: string;
  scopes: string[];
  status: string;
  granted_at: string;
}

export interface UserDetail {
  user: UserSummary;
  emails: UserEmail[];
  /** null for an account that has never had a number on file. */
  phone: UserPhone | null;
  devices: UserDevice[];
  bindings: UserBinding[];
  sessions: UserAppSession[];
  entitlements: UserEntitlement[];
}

/**
 * One row of the customer realm's authentication and device trail
 * (auth.security_events), as the operator's user page renders it.
 *
 * Every subject is OPTIONAL because every subject column is nullable and
 * ON DELETE SET NULL: an OTP failure names no application, a registration
 * refusal names no device, and an event that outlived the installation it
 * describes names an application and nothing else. Render the absence; do not
 * substitute a zero uuid.
 */
export interface SecurityEvent {
  id: string;
  /** RFC 3339 with sub-second precision, deliberately: one sign-in writes
   *  several rows inside one transaction at one now(), and a second-precision
   *  render would show them as simultaneous. */
  occurred_at: string;
  event_type: string;
  outcome: SecurityEventOutcome;
  application_id?: string;
  application_name?: string;
  device_id?: string;
  device_name?: string;
  installation_id?: string;
  session_id?: string;
  ip?: string;
  user_agent?: string;
  request_id?: string;
  /** The event's own payload — the refusal reason on a rejected token, the
   *  generation on a refresh. It is where an investigation starts, which is
   *  why the Admin API returns it rather than dropping it. Shape varies by
   *  event_type; there is no schema for it. */
  detail?: Record<string, unknown>;
}

/** The three values auth.security_events.outcome's CHECK allows. */
export type SecurityEventOutcome = "success" | "failure" | "denied";

export interface SecurityEventFilter {
  /**
   * CLAMPED into [1, 200] by the server rather than refused, so a paging
   * control that computed a silly number gets a sane page instead of a 400.
   * A value that is not a number at all IS refused — that is a caller asking
   * for something this API cannot answer.
   */
  limit?: number;
  /** Anything outside SecurityEventOutcome is a 400, not an empty trail:
   *  "nothing happened to this account" is the worst possible wrong answer
   *  here. */
  outcome?: SecurityEventOutcome;
  /** An EXACT match. The column has no CHECK — the vocabulary is whatever
   *  the platform has written — so `token` does not match `token.rejected`. */
  event_type?: string;
  /** Inclusive. RFC 3339 with an offset; a local-time string is refused. */
  since?: string;
  /** Exclusive, so paging a day at a time never shows an event twice. */
  until?: string;
}

// --------------------------------------------------------------------------
// What an installation says about itself
// --------------------------------------------------------------------------

/**
 * One deduplicated report out of auth.device_app_metadata: the arbitrary
 * self-description a shipped client posts about the machine it runs on,
 * stored verbatim and deduplicated on (device, source, payload hash).
 *
 * Every row is self-describing about its MACHINE on both routes. On the
 * per-account list that is the point — the reports come off several
 * installations — and on the per-device list it is redundant with the id in
 * the path and carried anyway, so one row shape serves both. A row that meant
 * something different depending on which route returned it is what produces a
 * screen showing the wrong machine's licence key.
 */
export interface AppMetadataReport {
  id: string;
  device_id: string;
  device_name: string;
  installation_id: string;
  platform: string;
  /**
   * The MOST RECENT account seen reporting this document, and null once that
   * account is deleted. NOT the first reporter: a report describes the
   * machine, and the account is the attribution on it, so a document
   * re-reported by a colleague on a shared installation moves to them.
   */
  user_id: string | null;
  /**
   * The application the FIRST report of this document arrived through, never
   * updated on a repeat. Do not render it as "which app is reporting this
   * now" — it is history, and there is deliberately no filter on it.
   */
  application_id: string;
  application_name: string;
  /** The namespace the CLIENT chose — `plugins`, `licence`, `gpu.inventory`.
   *  Lowercase letters, digits, dot, dash and underscore only. */
  source: string;
  /** The client's own version stamp for the payload's shape, or null when it
   *  does not stamp them — which is a real answer, not a missing one. */
  schema_version: string | null;
  /**
   * The size of the document AS SENT, reported whether or not the document
   * itself is. It is the number the elision rule is applied to, so
   * `payload_truncated` is exactly `payload_bytes > payload_inline_limit_bytes`
   * and a screen can explain the absence rather than merely show it.
   */
  payload_bytes: number;
  /** How many times this exact document has arrived from this machine under
   *  this source. The difference between "reported once during an install"
   *  and "reported every thirty seconds by a loop". */
  report_count: number;
  /** When this document was first seen. It is the ROW's age; `user_id` is not
   *  the account that produced it at that moment. */
  first_seen_at: string;
  /** When it was last confirmed. A report is the current state of a machine,
   *  not an event, so this moves and `first_seen_at` does not. */
  last_seen_at: string;
  /**
   * ABSENT when the payload was elided for size — never null, and never `{}`.
   * `{}` is a document a client genuinely sends (the inventory of a machine
   * that has none of the thing), so a withheld payload rendered as an empty
   * object would be indistinguishable from a real one.
   */
  payload?: Record<string, unknown>;
  /** Always present. True exactly when `payload` is absent. */
  payload_truncated: boolean;
}

export interface AppMetadataPage {
  /** Never null. An empty list is "this installation has never reported",
   *  which is a different fact from a 404 and from a report of `{}`. */
  reports: AppMetadataReport[];
  /** The RESOLVED page size after clamping, so a screen can title itself from
   *  what it received rather than from what it sent. Under `full` the ceiling
   *  is far lower — see AppMetadataFilter. */
  limit: number;
  /** The applied filter, or null for every source. */
  source: string | null;
  /** Whether whole payloads were served. The per-account route can never
   *  report true. */
  full: boolean;
  /** Reported even when false: "fifty reports" and "at least fifty reports"
   *  must not be one answer. */
  truncated: boolean;
  /** The rule the response is obeying, so `payload_truncated` is checkable
   *  against `payload_bytes` rather than taken on trust. */
  payload_inline_limit_bytes: number;
}

export interface AppMetadataFilter {
  /**
   * CLAMPED by the server rather than refused, and the page reports what it
   * settled on. A value that is not a number at all IS refused. The ceiling
   * drops sharply under `full`, because that flag removes the only thing
   * bounding a row's size.
   */
  limit?: number;
  /**
   * An EXACT match on the client-chosen namespace. Anything the column's
   * CHECK could not have stored is a 400 rather than an empty list — a filter
   * no row can match would otherwise answer "this device reports no Plugins"
   * when what happened is that the namespace is lowercase.
   */
  source?: string;
  /**
   * Serve payloads whole instead of eliding the large ones. Available on the
   * single-device route ONLY; sending it to the per-account route is a 400,
   * deliberately, because a silently dropped `full` leaves an operator
   * believing they are reading whole documents.
   */
  full?: boolean;
}

export interface UserFilter {
  q?: string;
  status?: string;
  /**
   * Tri-state, which is why it is optional rather than a plain boolean:
   * undefined means "do not filter" and returns both kinds. A `boolean`
   * defaulting to false would turn every unfiltered search into
   * "non-developers only" and hide the flagged accounts from a search that
   * never mentioned them.
   */
  developer?: boolean;
  limit?: number;
}

// --------------------------------------------------------------------------
// What a revocation reports
// --------------------------------------------------------------------------

/**
 * The counts every revoking route answers with, as an optional set.
 *
 * Optional because each route reports the blast radius it actually has: a
 * session revoke ends one session, a device revoke takes its bindings and
 * refresh families with it. The screens render whichever counts came back
 * rather than a flat "done" — an operator who cannot see that suspending an
 * account also ended eleven sessions has no idea what they just did.
 */
export interface RevocationCounts {
  bindings_revoked?: number;
  sessions_ended?: number;
  sso_sessions_ended?: number;
  refresh_families_revoked?: number;
}

export interface UserStatusResult extends RevocationCounts {
  user_id: string;
  status: UserStatus;
}

export interface DeviceRevokeResult extends RevocationCounts {
  device_id: string;
}

/**
 * The sign-out answers in the same shape as the block, deliberately: both end
 * bindings, sessions and refresh families, and the portal renders both with the
 * same "2 sessions ended" summary. What differs is on the server — the
 * sign-out leaves the device row and its key active, so the same person can
 * sign in again on the same machine.
 */
export type DeviceSignOutResult = DeviceRevokeResult;

/**
 * A restore ends nothing, so it shares no shape with the two above. It reports
 * the key it reopened and nothing else: bindings do not come back here, the
 * next sign-in on the machine re-establishes them.
 */
export interface DeviceRestoreResult {
  device_id: string;
  keys_restored: number;
}

export interface BindingRevokeResult extends RevocationCounts {
  binding_id: string;
}

export interface SessionRevokeResult extends RevocationCounts {
  session_id: string;
}

export interface ApplicationStatusResult {
  application_id: string;
  status: EnabledStatus;
}

/**
 * What a redirect URI replacement answers with.
 *
 * `previous_redirect_uris` is reported by the server from inside the
 * transaction that overwrote it, so it is what the list actually was rather
 * than what the page last read.
 *
 * `denied_authorization_requests` counts the in-flight authorization codes the
 * removal burned. It is not an error and not zero-by-default noise: each one is
 * an installation that was mid-sign-in against a callback that is no longer
 * registered, and an operator who has just stranded three of them should learn
 * it from this screen rather than from a support ticket. `denied_reason` is the
 * same string /authorize writes when it refuses an unregistered callback, so
 * the two are greppable together.
 */
export interface RedirectURIReplacement {
  oauth_client: OAuthClient;
  previous_redirect_uris: string[];
  denied_authorization_requests: number;
  denied_reason: string;
}

export interface OAuthClientStatusResult {
  oauth_client_id: string;
  status: EnabledStatus;
}

export interface EntitlementRevokeResult {
  entitlement_id: string;
  status: string;
}

/**
 * What the developer-flag route answers with.
 *
 * `changed` is false when the operator saved a state the account already held.
 * The server writes no audit row for that replay, so a screen that reports
 * "marked as a developer" either way would claim a trail entry exists when it
 * does not — the caller is expected to read this and say which happened.
 */
export interface UserDeveloperFlagResult {
  user_id: string;
  is_empite_developer: boolean;
  changed: boolean;
}

// --------------------------------------------------------------------------
// Provider accounts and their credentials
// --------------------------------------------------------------------------

export type ProviderEnvironment = "dev" | "staging" | "prod";
export type ProviderAccountStatus = "active" | "suspended" | "disabled";

/** "unknown" is the truthful state of an account nothing has called yet, and
 *  is deliberately not "healthy": a green badge nobody earned is worse than
 *  no badge at all. */
export type ProviderHealth = "unknown" | "healthy" | "degraded" | "down";

export interface ProviderAccountSummary {
  id: string;
  provider: string;
  environment: ProviderEnvironment;
  label: string;
  status: ProviderAccountStatus;
  health: ProviderHealth;
  /** Empty when the provider has no notion of one, which is why these two are
   *  `string` and not `string | undefined`: the server sends "". */
  region: string;
  base_url: string;
  /** Null means "no cap configured", which is a different fact from zero and
   *  has to survive the trip as one. */
  monthly_budget_micros: number | null;
  rate_limit_rpm: number | null;
  credential_count: number;
  active_credential_count: number;
  last_error_at?: string;
  created_at: string;
  updated_at: string;
}

export type ProviderCredentialStatus = "active" | "rotating" | "revoked";
export type CredentialValidationState = "unvalidated" | "valid" | "invalid";

/**
 * A credential as the back office is allowed to know it.
 *
 * There is no field for the key here and there never will be one. The
 * platform holds upstream secrets encrypted, every admin route answers with
 * last four, a fingerprint and timestamps, and rotate answers with exactly
 * this same shape — so "reveal the key" is not an operation this console is
 * missing, it is an operation the system does not have. A field added here
 * would be the first half of building it.
 */
export interface ProviderCredential {
  id: string;
  account_id: string;
  provider: string;
  environment: ProviderEnvironment;
  label: string;
  status: ProviderCredentialStatus;
  validation_state: CredentialValidationState;
  /** The last four characters, and only those. Enough to match a key against
   *  what the provider's own console shows, useless to anyone who steals it. */
  last_four: string;
  /** A hash of the key. Two accounts sharing a fingerprint are two accounts
   *  sharing a key, which is the finding this column exists to surface. */
  fingerprint: string;
  priority: number;
  created_at: string;
  rotated_at?: string;
  last_used_at?: string;
}

/** A routing profile seen from the account it draws on: enough to answer
 *  "who breaks if I suspend this", which is the only question the account
 *  page asks about profiles. */
export interface ProviderProfileRef {
  id: string;
  slug: string;
  name: string;
  application_id: string;
  application_name: string;
  status: string;
  priority: number;
  enabled: boolean;
  weight: number;
}

export interface ProviderAccountDetail {
  provider_account: ProviderAccountSummary;
  credentials: ProviderCredential[];
  routing_profiles: ProviderProfileRef[];
}

export interface ProviderAccountStatusResult {
  provider_account_id: string;
  status: ProviderAccountStatus;
}

// --------------------------------------------------------------------------
// Provider templates — the platform's definition of a provider
// --------------------------------------------------------------------------

/**
 * A template is what the PLATFORM says a provider is: where it lives, and in
 * which header a key is presented to it. There is no field for a credential
 * here and there must never be one — a key on a template would be one key
 * shared by every tenant on the platform, which is precisely the coupling
 * tenant-owned accounts exist to remove.
 *
 * `draft` is written but not offered, `published` is the only status an
 * operator may instantiate, and `retired` closes the picker without touching
 * the accounts already made from it.
 */
export type ProviderTemplateStatus = "draft" | "published" | "retired";

/** Which header the tenant's own key travels in. The style is named rather
 *  than the header spelled out at each call site, so an adapter and a template
 *  cannot disagree about what "the key header" is. */
export type ProviderAuthStyle = "bearer" | "x-api-key" | "x-goog-api-key";

export interface ProviderTemplate {
  id: string;
  provider: string;
  display_name: string;
  base_url: string;
  auth_style: ProviderAuthStyle;
  /** Non-secret header material only — a version pin, a referer. The API
   *  refuses the names a credential travels under. */
  default_headers: Record<string, string>;
  status: ProviderTemplateStatus;
  created_at: string;
  updated_at: string;
}

export interface NewProviderTemplate {
  provider: string;
  display_name: string;
  base_url: string;
  auth_style: ProviderAuthStyle;
  default_headers?: Record<string, string>;
  /** Omitted is `published`. `retired` is refused at create: a definition
   *  nobody could ever select is not a state worth authoring. */
  status?: Exclude<ProviderTemplateStatus, "retired">;
}

/**
 * A correction to a definition. PATCH semantics: an absent key is a field left
 * alone, so this carries only what the form actually changed.
 *
 * There is no `provider` here although the API accepts one. The provider key
 * is what already-instantiated accounts join on, so moving it would silently
 * re-address them — the API answers a body that tries with a 409, and this
 * console does not offer the box.
 */
export interface ProviderTemplateEdit {
  display_name?: string;
  base_url?: string;
  auth_style?: ProviderAuthStyle;
  default_headers?: Record<string, string>;
}

/**
 * What an operator sends to instantiate a template into their OWN account.
 *
 * There is no tenant here, deliberately: the row is filed under the session's
 * tenant, so no request shape this console can build puts a key in another
 * company's pool. `secret` is the operator's own upstream key, held sealed by
 * the platform and never shown again — it is the second and last place in this
 * portal where a plaintext key exists at all.
 */
export interface NewProviderAccount {
  template_id: string;
  environment: ProviderEnvironment;
  label: string;
  /** Blank takes the template's endpoint. An override is for a private one —
   *  a tenant's own gateway, or the local mock. */
  base_url?: string;
  credential_label?: string;
  secret: string;
}

/** The 201 from add-provider: the account and its first pool slot, read back
 *  through the same view every other route uses, which is what makes it
 *  structurally incapable of echoing the key it just sealed. */
export interface AddedProviderAccount {
  provider_account: ProviderAccountSummary;
  credentials: ProviderCredential[];
}

// --------------------------------------------------------------------------
// Models
// --------------------------------------------------------------------------

/**
 * The retirement ladder, in the order it descends.
 *
 * `disabled` is an operator's reversible off switch and is NOT a lesser
 * `retired`: the model still exists upstream and is expected back, the platform
 * has just stopped sending it traffic. It refuses a caller like `retired` does
 * and carries advice like `deprecated` does, which is exactly the combination
 * neither of the other two could express.
 */
export type ProviderModelStatus =
  "available" | "deprecated" | "disabled" | "retired";

/**
 * One capability, stated either way.
 *
 * `supported: false` is a positive claim — "this model does not do vision" —
 * and is not the same as a capability the catalogue says nothing about. The
 * screens render the two differently for that reason.
 */
export interface ModelCapability {
  capability: string;
  supported: boolean;
}

export interface ProviderModel {
  id: string;
  provider: string;
  model: string;
  /**
   * What a CLIENT sends for this model, and what a routing policy is written
   * with when the row is assigned to an application.
   *
   * It lives on the model so that assigning one to an application is a tick
   * rather than a naming exercise. Deliberately not unique across the
   * catalogue: two rows sharing an alias are one model served by two
   * providers, which is the fallback chain.
   */
  alias: string;
  display_name: string;
  family: string;
  status: ProviderModelStatus;
  context_window: number | null;
  max_input_tokens: number | null;
  max_output_tokens: number | null;
  capabilities: ModelCapability[];
  /**
   * What a deprecated or retired model tells its callers, and where to send
   * them. Both are `| null` rather than optional: the server always sends the
   * keys, and null means "nothing was written", which is a different fact from
   * a key that is missing. An available model has null in both — the database
   * refuses any other combination.
   */
  deprecation_message: string | null;
  /** A client-facing ALIAS, not a provider model id: it is the vocabulary the
   *  caller speaks, so it can be sent back verbatim on the next request. */
  replacement_alias: string | null;
  /**
   * Every application some routing profile permits this row for, by name.
   *
   * Computed by the server rather than assembled here, because assembling it
   * on the client means reading every routing profile and every policy to
   * render one column. Empty is a normal state: a model just added routes
   * nowhere until a policy names it.
   */
  applications: string[];
  /**
   * When this model is planned to stop answering, RFC 3339 in UTC, or null when
   * no date has been set.
   *
   * It is the half of a deprecation that makes somebody act — "this is going
   * away" is a fact to file, "it stops on the 14th" is a deadline — and it
   * reaches callers ahead of the refusal, in the `X-Empite-Model-Sunset` header
   * and on `GET /v1/models`. Permitted only on a model that is on its way out;
   * reinstating one to available clears it.
   */
  sunset_at: string | null;
  created_at: string;
}

/**
 * One model's traffic over a window, keyed by BOTH readings.
 *
 * `alias` is what the client asked for — who has to be told to move off a
 * deprecated model — and `(provider, provider_model)` is what actually served
 * the request, which is the catalogue row being retired and the thing being
 * paid for. One alias can be served by several provider models through
 * fallback, and one provider model can serve several aliases, so neither half
 * substitutes for the other. A screen wanting one reading sums these rows over
 * the other half of the key.
 */
export interface ModelUsage {
  alias: string;
  provider: string;
  provider_model: string;
  requests: number;
  /**
   * An UPPER BOUND, not a distinct count, and the name says so on purpose.
   * The server stores a true distinct-user count per DAY; this is the sum of
   * those, so a user who came back on Tuesday is counted twice. It answers
   * "at most how many people would a deprecation notice have to reach", which
   * is the number the decision needs, and it is not "how many people use this".
   */
  distinct_users_daily_sum: number;
  input_tokens: number;
  output_tokens: number;
  cost_micros: number;
  /** How many of the window's days this row appears on — the divisor that makes
   *  a model launched last week comparable with one up all month. */
  days_with_traffic: number;
  /** Inclusive ISO dates (YYYY-MM-DD), not timestamps: the underlying bucket is
   *  a UTC day and rendering it as an instant shifts it in local time. */
  first_day: string;
  last_day: string;
}

/** The window the server actually used, which is not always the one that was
 *  asked for — `window_days` is clamped, and `until` is YESTERDAY because the
 *  rollup sweep only writes finished days. Label columns from this, not from
 *  the request. */
export interface ModelUsageWindow {
  days: number;
  since: string;
  until: string;
}

export interface ModelUsageReport {
  window: ModelUsageWindow;
  /** True when the server cut the tail off its list. Always present, so
   *  "absent" never has to be read as "complete". */
  truncated: boolean;
  /** Empty means no traffic, never "not found" — and no traffic is a different
   *  fact from a model the rollup has never measured, which is why a screen
   *  showing these renders a missing row as a dash rather than a zero. */
  model_usage: ModelUsage[];
}

/** The read's parameters. `window_days` is clamped into [1, 365] by the server;
 *  anything that is not an integer is refused with a 400. */
export interface ModelUsageFilter {
  window_days?: number;
  /** Exact match on the requested alias, never on the provider's model id. */
  alias?: string;
}

// --------------------------------------------------------------------------
// Analytics
// --------------------------------------------------------------------------

/**
 * The window an analytics answer was actually computed over.
 *
 * Label columns and headings from THIS, never from what was asked for: the
 * server clamps a span longer than 90 days and moves an end that ran into
 * today, and a screen that titled itself from its own request would put a
 * quarter's figure under a heading that says "a year".
 */
export interface AnalyticsWindow {
  /** The INCLUSIVE span of since..until. Different from a `days` that was sent
   *  whenever the ask was clamped. */
  days: number;
  /** Inclusive ISO dates (YYYY-MM-DD). */
  since: string;
  until: string;
  /**
   * The last day the rollups can be complete for — yesterday in UTC — which is
   * a fact about the PLATFORM's clock rather than about this window. Equal to
   * `until` for the usual "last N days" ask, later than it whenever the caller
   * asked about the past.
   */
  through: string;
  /** True when at least one bound was moved. Reported so a shortened answer
   *  cannot be read as the one that was asked for. */
  clamped: boolean;
}

/**
 * One period's measures — the headline totals, the comparison period, or a
 * single day of the series. The same shape for all three, so a measure cannot
 * mean one thing in a tile and another in the chart under it.
 *
 * The four money fields are ABSENT, not zero, for an operator without
 * `wallets:read`; `money_visible` on the enclosing document says which it is. A
 * withheld figure rendered as 0 is a number a screen cannot tell from a real
 * one, and "this application earned nothing" is not something to read off a
 * permission boundary.
 */
export interface AnalyticsTotals {
  requests: number;
  failed_requests: number;
  /** A FRACTION in [0, 1], not a percentage. Zero for a period with no
   *  requests: nothing failed. */
  failure_rate: number;

  input_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  /** The sum of the five above, computed by the server so two screens cannot
   *  disagree about whether cache writes and reasoning count. */
  total_tokens: number;

  /** An EXACT distinct count over the period, not a sum of daily counts: a
   *  person who came back on Tuesday is one person. */
  distinct_users: number;
  /**
   * An UPPER BOUND, and the name says so. No rollup carries device ids, only
   * per-day counts, so a device seen on two days or in two applications is
   * counted twice. Render it as "up to N", never as "N devices".
   */
  distinct_devices_upper_bound: number;

  /** SETTLED inference revenue and the provider cost of the same requests, in
   *  micros. Not cash collected — top-ups and subscription charges are money
   *  moving into a wallet, and the revenue screen is where they live. */
  revenue_micros?: number;
  cost_micros?: number;
  /** Sell minus cost, computed by the server. NEGATIVE is a real reading: a
   *  written-off day costs money that was never charged for. */
  margin_micros?: number;
}

/**
 * Each measure's percentage change against the comparison window.
 *
 * A value of `null` means the change is UNDEFINED — the baseline was zero and
 * the current figure is not — and it must be rendered as "new" or as nothing.
 * There is no percentage that describes growth from nothing, and every number
 * a client might substitute is one it would then draw as a real figure: 0
 * reads as unchanged, 100 as doubled.
 *
 * `null` is different from an ABSENT money key, which means the operator may
 * not see the figure at all.
 */
export type AnalyticsDelta = {
  [K in keyof AnalyticsTotals]?: number | null;
};

/** One day of the series. Always present for every day of the window at or
 *  after `available_from`, including the quiet ones, which arrive as explicit
 *  zeros — a chart handed a gap closes it, and the flat line it invents runs
 *  through days that never happened. */
export interface AnalyticsSeriesPoint extends AnalyticsTotals {
  /** An inclusive ISO date (YYYY-MM-DD). The bucket IS a UTC day; rendering it
   *  as an instant moves it in the reader's local time. */
  day: string;
}

/** One platform's share of the LIVE device fleet. */
export interface AnalyticsFleetPlatform {
  platform: string;
  active: number;
}

/** The device population: a snapshot of what is live, and what moved inside
 *  the window. */
export interface AnalyticsFleet {
  /** Every platform with at least one live device, ordered by name so a
   *  chart's colours do not reshuffle between two loads of the same page.
   *  Sums to `active_total`. */
  by_platform: AnalyticsFleetPlatform[];
  active_total: number;
  /** Movements INSIDE the window: devices first seen, and devices revoked. */
  registered: number;
  revoked: number;
}

/** The user population, on the same snapshot/movement split as the fleet. */
export interface AnalyticsAccounts {
  /** The registration EVENT, counted inside the window. An account created in
   *  it and deleted since still counts. */
  registered: number;
  /** Current state, deleted accounts excluded. `developers` is the staff flag
   *  that takes an account off revenue reporting. */
  active: number;
  suspended: number;
  developers: number;
}

/** The platform dashboard's first paint, in one call. */
export interface AnalyticsOverview {
  window: AnalyticsWindow;
  /** The equal-length window immediately before `window`, which `comparison`
   *  is computed over. Derived by the server, so "the previous 30 days" cannot
   *  come to mean two different things on two screens. */
  comparison_window: AnalyticsWindow;
  /**
   * The first day the rollups reach, or `null` when they reach nothing yet.
   * `series` starts here: before it a zero would be an invention rather than a
   * measurement, because "nobody used the platform" and "we were not measuring
   * yet" are different facts.
   */
  available_from: string | null;

  totals: AnalyticsTotals;
  comparison: {
    totals: AnalyticsTotals;
    delta_percent: AnalyticsDelta;
  };
  /** Ascending, one element per day. See AnalyticsSeriesPoint. */
  series: AnalyticsSeriesPoint[];

  fleet: AnalyticsFleet;
  accounts: AnalyticsAccounts;

  /** The unit the money figures are in, and every unit that appears in the
   *  window. Absent — like the figures themselves — without `wallets:read`.
   *  A second entry in `currencies` means the totals describe ONE of them. */
  currency?: string;
  currencies?: string[];

  /** Always present, even when true, so a client can rely on the keys existing
   *  rather than reading an absent flag as permission. `money_visible` follows
   *  `wallets:read` and `identity_visible` follows `users:read`. */
  money_visible: boolean;
  identity_visible: boolean;
}

/**
 * The window to ask for. Every field is optional and the server has its own
 * defaults; send AT MOST one of `since`/`until` together with `days`, because
 * two bounds and a length over-determine a window and the server refuses the
 * combination with a 400 rather than picking one to drop.
 *
 * `days` is CLAMPED into [1, 90]; a value that is not a number is refused.
 */
export interface AnalyticsWindowQuery {
  /** Inclusive ISO dates (YYYY-MM-DD). */
  since?: string;
  until?: string;
  days?: number;
}

/** An application named on an analytics row: enough to render it and enough
 *  to link to it. */
export interface AnalyticsApplicationRef {
  id: string;
  slug: string;
  name: string;
}

/**
 * One account's usage across the window.
 *
 * `email` and `display_name` are ABSENT — not blank — for an operator without
 * `users:read`, and the four money fields are absent without `wallets:read`;
 * `identity_visible` and `money_visible` on the enclosing document say which it
 * is. `status` and `is_developer` are NOT gated: they say how to READ the
 * figures beside them (a suspended account's flat tail, a staff account's load
 * test) rather than naming the person.
 */
export interface AnalyticsTopUser {
  user_id: string;
  email?: string;
  display_name?: string;
  /** The account's own status, INCLUDING "deleted": a soft-deleted account's
   *  rollup rows survive the deletion, and dropping them would make this
   *  leaderboard disagree with the overview for no visible reason. */
  status: string;
  /** The staff flag that takes an account off revenue reporting. */
  is_developer: boolean;

  requests: number;
  failed_requests: number;

  input_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  /** The sum of the five above, and the default sort. */
  total_tokens: number;

  /**
   * UPPER BOUNDS, and the names say so. The rollup stores a per-day distinct
   * count and never the ids, so a model used on ten days contributes ten.
   * Render as "up to N", never as "N models".
   */
  distinct_models_upper_bound: number;
  distinct_devices_upper_bound: number;

  /** Every application this account used inside the window, ordered by slug. */
  applications: AnalyticsApplicationRef[];

  /** RFC 3339 instants bounding the account's activity INSIDE the window, or
   *  null when the rollup recorded no times. The time of day is the part a
   *  "it stopped working this afternoon" ticket turns on. */
  first_request_at: string | null;
  last_request_at: string | null;

  /** Provider cost and what the account was charged, in micros, summed over
   *  the reporting currency's rows only. `margin_micros` is sell minus cost,
   *  computed by the server; NEGATIVE is a real reading. */
  cost_micros?: number;
  sell_micros?: number;
  margin_micros?: number;
}

/**
 * What to rank by.
 *
 * `cost`, `revenue` and `margin` need `wallets:read`: an ordering by revenue IS
 * the revenue in ordinal form, so the server answers 403 rather than serving
 * the ranking with the figures blanked out. An unknown value is a 400 naming
 * the accepted set, never a silent fall back to the default.
 */
export type AnalyticsUserSort =
  "requests" | "tokens" | "cost" | "revenue" | "margin" | "last_seen";

/** The leaderboard's query. Extends the window query; every field optional. */
export interface AnalyticsTopUsersQuery extends AnalyticsWindowQuery {
  /** An application SLUG. A slug no application has is a 404, not an empty
   *  leaderboard: a typo must not render as "nobody uses this". */
  application?: string;
  /** Defaults to `tokens` — a leaderboard exists to find spend, and one
   *  account streaming a million tokens costs more than a thousand accounts
   *  asking one question each. */
  sort?: AnalyticsUserSort;
  /** Defaults to `desc`. Accounts with no recorded time sort LAST in both
   *  directions: unmeasured is not the same as dormant. */
  order?: "asc" | "desc";
  /** Defaults to 50 and is CLAMPED to 200 by the server, which reports what it
   *  resolved to. */
  limit?: number;
}

/** Who is behind the overview's headline. */
export interface AnalyticsTopUsers {
  window: AnalyticsWindow;
  /** The RESOLVED ordering and page size. Label the table from THESE: a limit
   *  past the cap comes back clamped. */
  sort: AnalyticsUserSort;
  order: "asc" | "desc";
  limit: number;
  /** True when the page cut the tail off. Always present, so "absent" is never
   *  read as "complete" — an operator cannot otherwise tell an account that is
   *  missing from the table from one that is quiet. */
  truncated: boolean;
  /** The resolved `application` filter, or null for every application. */
  application: AnalyticsApplicationRef | null;

  /** Ranked by `sort`. Empty when nothing used the platform in the window,
   *  which is a fact rather than a missing resource. */
  users: AnalyticsTopUser[];

  /** The unit the money columns are in, and every unit that appears in the
   *  window. A second entry means the figures describe ONE of them — and that
   *  an account billed in the other reads as zero money. Absent without
   *  `wallets:read`. */
  currency?: string;
  currencies?: string[];

  money_visible: boolean;
  identity_visible: boolean;
}

/**
 * One slice of an account's usage: the window as a whole, one day of it, or one
 * application inside it. The same shape for all three, so a measure cannot mean
 * one thing in a tile and another in the chart under it.
 *
 * The three money fields are ABSENT, not zero, without `wallets:read`;
 * `money_visible` on the enclosing document says which it is.
 */
export interface AnalyticsUserMeasures {
  requests: number;
  failed_requests: number;
  /** Failures over requests in [0, 1]. A slice with no requests has a rate of
   *  zero — nothing failed — rather than an undefined one. */
  failure_rate: number;

  input_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  /** The sum of the five above. */
  total_tokens: number;

  /** UPPER BOUNDS, and the names say so: the rollup stores per-day distinct
   *  counts and never the ids, so a model used on ten days contributes ten.
   *  Render as "up to N", never as "N models". */
  distinct_models_upper_bound: number;
  distinct_devices_upper_bound: number;

  cost_micros?: number;
  sell_micros?: number;
  /** Sell minus cost, computed by the server. NEGATIVE is a real reading. */
  margin_micros?: number;
}

/** One day of the account's series. `day` is an ISO date (YYYY-MM-DD): it is a
 *  rollup bucket, so placing it in the browser's timezone would shift the whole
 *  chart by a day. */
export interface AnalyticsUserDay extends AnalyticsUserMeasures {
  day: string;
}

/** One application's share of the account's window. */
export interface AnalyticsUserApplication extends AnalyticsUserMeasures {
  application: AnalyticsApplicationRef;
  /** RFC 3339 instants bounding this account's activity in this application
   *  INSIDE the window, or null when the rollup recorded no times. */
  first_request_at: string | null;
  last_request_at: string | null;
}

/**
 * One requested ALIAS's share of the account's window.
 *
 * Computed at READ TIME from the request log — the one place the analytics
 * surface does that — filtered to this account over the bounded window, so it
 * rides the per-user index rather than aggregating the platform. It therefore
 * carries no distinct-count fields: those exist only in the rollups.
 *
 * `alias` is what the CLIENT asked for. The provider model a fallback ended up
 * on is a routing fact and lives on the models surface.
 */
export interface AnalyticsUserModel {
  alias: string;
  requests: number;
  failed_requests: number;

  input_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  total_tokens: number;

  first_request_at: string | null;
  last_request_at: string | null;

  cost_micros?: number;
  sell_micros?: number;
  margin_micros?: number;
}

/**
 * One installation this account has been bound to.
 *
 * A SNAPSHOT, not a window measure: "which machines is this person on" is a
 * fact about now, and the windowed answer to "how many did they use" is
 * `distinct_devices_upper_bound`. There are no per-device usage figures because
 * no rollup carries device ids — only counts.
 */
export interface AnalyticsUserDevice {
  device_id: string;
  installation_id: string;
  platform: string;
  device_name: string;
  status: string;
  first_seen_at: string;
  last_seen_at: string;
  /** Non-null only for a blocked installation. */
  revoked_at: string | null;
}

/**
 * One wallet this account is charged against, with its DERIVED balance.
 *
 * Every figure is summed off the ledger, never read from the wallet's cache —
 * the same rule the treasury screens follow, so the two cannot disagree about
 * one customer's money. `cache_stale` should never be true; when it is, the
 * figure beside it is a reconciliation job rather than a number to quote.
 */
export interface AnalyticsUserWallet {
  wallet_id: string;
  application: AnalyticsApplicationRef;
  currency: string;
  status: string;
  /** `available_micros` is the two buckets added. They ride beside it because
   *  they behave differently: the subscription bucket is burned at the cycle
   *  reset and the top-up bucket never expires, so a total quoted on Thursday
   *  can be wrong on Friday. */
  available_micros: number;
  subscription_micros: number;
  top_up_micros: number;
  reserved_micros: number;
  cache_stale: boolean;
}

/** Who the drill-down is about. */
export interface AnalyticsUserAccount {
  user_id: string;
  /** ABSENT without `users:read` — which cannot happen on this route, since it
   *  requires that permission. */
  email?: string;
  display_name?: string;
  /** Account state, not identity: it says how to READ the figures beside it. */
  status: string;
  is_developer: boolean;
  created_at: string;
  /** Non-null for a soft-deleted account, whose rollup rows survive the
   *  deletion. Render the page rather than treating it as missing. */
  deleted_at: string | null;
}

/**
 * One account's usage broken down by day, application and model.
 *
 * AGGREGATES ONLY. There is no request list, no prompt, no completion and no
 * message count anywhere in this document, and there is not going to be one.
 */
export interface AnalyticsUserDetail {
  window: AnalyticsWindow;
  user: AnalyticsUserAccount;
  totals: AnalyticsUserMeasures;

  /** RFC 3339 instants bounding the account's activity inside the window, or
   *  null when no rollup row carried a time. */
  first_request_at: string | null;
  last_request_at: string | null;

  /** The first day the rollups reach, or null when they reach nothing. `series`
   *  starts there: before it a zero would be an invention rather than a
   *  measurement. The TRAILING edge is not trimmed, because a quiet day
   *  produces no row and cutting to the last one with traffic would delete a
   *  genuinely idle weekend from the right-hand edge of the chart. */
  available_from: string | null;
  /** One entry per day of the window at or after `available_from`, ascending,
   *  with a quiet day carried as an explicit ZERO. Never draw a line across a
   *  gap: there are none, on purpose. */
  series: AnalyticsUserDay[];

  /** Every application the account used in the window, ordered by slug — a
   *  stable order, so an operator can scan the same list twice. */
  applications: AnalyticsUserApplication[];

  /** Ordered by total tokens descending. `models_truncated` is always present,
   *  so "absent" is never read as "complete". */
  models: AnalyticsUserModel[];
  models_truncated: boolean;

  devices: AnalyticsUserDevice[];
  devices_truncated: boolean;

  /** Absent without `wallets:read`: a wallet balance is nothing but money, so
   *  the whole list is withheld rather than served with its amounts blanked. */
  wallets?: AnalyticsUserWallet[];
  wallets_truncated?: boolean;

  /** The unit the money fields are in, and every unit the account's window
   *  carries. Absent without `wallets:read`. The empty string is the honest
   *  answer for an account that spent nothing. */
  currency?: string;
  currencies?: string[];

  money_visible: boolean;
  identity_visible: boolean;
}

/**
 * How a latency figure was arrived at.
 *
 * `measured` is a percentile the rollup computed over one day's own requests.
 * `daily_percentiles_weighted_by_requests` is the traffic-weighted MEAN of the
 * daily percentiles across a window — it is NOT the window's percentile, which
 * cannot be recovered once the observations are gone. Label a window figure as
 * "typical day" rather than as "p95 over the month".
 */
export type AnalyticsLatencyBasis =
  "measured" | "daily_percentiles_weighted_by_requests";

/**
 * The four latency figures for one slice of usage.
 *
 * EVERY FIGURE IS NULLABLE AND NULL IS A REAL ANSWER: "no request in this slice
 * reached that milestone". Render it as "not measured", never as 0 — zero
 * milliseconds is a number a chart draws as an excellent result.
 */
export interface AnalyticsLatency {
  basis: AnalyticsLatencyBasis;
  p50_first_token_ms: number | null;
  p95_first_token_ms: number | null;
  p50_total_ms: number | null;
  p95_total_ms: number | null;
  /** How many days carried a figure. The two pairs are taken over DIFFERENT
   *  populations — a request that failed still finished — so either can be
   *  empty while the other is not. This is the honest denominator of a
   *  weighted mean: 1 of 30 days is one busy Tuesday, not a month. */
  first_token_days: number;
  total_days: number;
}

/**
 * One slice of an application's usage: the window as a whole, or one day of it.
 * The same shape for both, so a measure cannot mean one thing in a tile and
 * another in the chart under it.
 *
 * The three money fields are ABSENT, not zero, without `wallets:read`;
 * `money_visible` on the enclosing document says which it is.
 */
export interface AnalyticsApplicationMeasures {
  requests: number;
  failed_requests: number;
  /** Failures over requests in [0, 1]. A slice with no requests has a rate of
   *  zero — nothing failed — rather than an undefined one. */
  failure_rate: number;

  input_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  /** The sum of the five above. */
  total_tokens: number;

  /** The counts ride beside the rates derived from them, so a share can be
   *  checked rather than trusted. A high `fallback_rate` is a primary provider
   *  in trouble; `stream_share` is the context the first-token percentiles are
   *  read in, since a p95 over a population that barely streams is a figure
   *  about very few requests. */
  fallback_requests: number;
  stream_requests: number;
  fallback_rate: number;
  stream_share: number;

  /** UPPER BOUNDS across a window: the rollup stores a per-day distinct count
   *  and never the ids, so a person who used the app on ten days contributes
   *  ten. Tight for a single day of the series, and named the same way there
   *  anyway. Render as "up to N", never as "N people". */
  distinct_users_upper_bound: number;
  distinct_devices_upper_bound: number;

  latency: AnalyticsLatency;

  /** The BILLED figures off the usage rollup — what the sweep priced the
   *  traffic at. The SETTLED ones are under `revenue` on the enclosing row and
   *  can differ. `margin_micros` is sell minus cost; NEGATIVE is a real
   *  reading and the one this screen exists to surface. */
  cost_micros?: number;
  sell_micros?: number;
  margin_micros?: number;
}

/** One day of an application's series. `day` is an ISO date (YYYY-MM-DD): it is
 *  a rollup bucket, so placing it in the browser's timezone would shift the
 *  whole chart by a day. */
export interface AnalyticsApplicationDay extends AnalyticsApplicationMeasures {
  day: string;
}

/**
 * What one application actually took in over the window, read from the revenue
 * rollup and therefore SETTLED — unlike `sell_micros` beside it, which is what
 * the sweep priced the traffic at. Both are reported because the gap between
 * them is itself worth seeing.
 *
 * The deep breakdown (per day, per payment type, subscription lifecycle) is
 * `analyticsRevenue`'s. What is here is the per-application headline.
 */
export interface AnalyticsApplicationRevenue {
  settled_sell_micros: number;
  settled_cost_micros: number;
  settled_margin_micros: number;

  /** Money in by route. `refund_micros` is a POSITIVE magnitude and is already
   *  SUBTRACTED in `net_payments_micros`, so nothing downstream has to decide
   *  the sign for itself. */
  topup_micros: number;
  subscription_charge_micros: number;
  refund_micros: number;
  net_payments_micros: number;

  /** An UPPER BOUND: the rollup stores a per-day count, so a customer who paid
   *  twice in the window is counted twice. */
  paying_users_upper_bound: number;
}

/** One application's window, with its own daily series under it. */
export interface AnalyticsApplicationUsage extends AnalyticsApplicationMeasures {
  application: AnalyticsApplicationRef;
  /** "active" or "disabled". NOT a reason to hide the row: it says how to READ
   *  the figures beside it — a disabled application's collapsing tail is a
   *  measurement of the disabling rather than of the product. */
  status: string;
  /** One entry per day of the window at or after `available_from`, ascending,
   *  with a quiet day carried as an explicit ZERO whose latency is null. Never
   *  draw a line across a gap: there are none, on purpose. */
  series: AnalyticsApplicationDay[];

  /** Absent without `wallets:read`. */
  revenue?: AnalyticsApplicationRevenue;
  /** This application's OWN unit, and every unit its traffic carried. The
   *  figures above are summed in the REPORT's currency, so an application
   *  billing in another one reads as zero money — and this field is the only
   *  thing on the row that tells that from "earned nothing". Empty string when
   *  it settled nothing at all. Absent without `wallets:read`. */
  currency?: string;
  currencies?: string[];
}

/** The per-application query. Extends the window query; every field optional. */
export interface AnalyticsApplicationsQuery extends AnalyticsWindowQuery {
  /** An application SLUG, for the application detail page. A slug no
   *  application has is a 404, not an empty list: a typo must not render as
   *  "nobody uses this product". */
  application?: string;
}

/** Which product earns and which one costs. */
export interface AnalyticsApplications {
  window: AnalyticsWindow;
  /** The first day the usage rollup reaches, or null when it reaches nothing.
   *  Every series starts there: before it a zero would be an invention rather
   *  than a measurement. */
  available_from: string | null;
  /** The resolved `application` filter, or null for every application. */
  application: AnalyticsApplicationRef | null;

  /**
   * One row per application in the CATALOGUE — including every application
   * with no traffic at all, which is the point of the endpoint: a product that
   * earned nothing produces no rollup row, and a list built from the rollup
   * would show exactly the applications that are working.
   *
   * Ordered by requests descending, ties broken by slug. There is no `limit`
   * and no `sort`: the catalogue is a handful of rows an operator reads whole,
   * and a server-side ranking by a money column would hand its shape to a
   * caller the omission rule is withholding the figures from.
   */
  applications: AnalyticsApplicationUsage[];

  /** The unit the money columns are summed in, and every unit that appears in
   *  the window across both rollups. Absent without `wallets:read`. */
  currency?: string;
  currencies?: string[];

  money_visible: boolean;
  identity_visible: boolean;
}

/**
 * ONE CURRENCY'S money picture over a window, or over one day of it.
 *
 * Every monetary field is an {@link Amount} rather than a bare `*_micros`
 * number, unlike the other analytics documents: those pick a single reporting
 * currency and state it once at the top, whereas each slice here already
 * carries its own unit, so every figure travels with the currency and the scale
 * it is denominated in. Nothing on this route may be added to anything on
 * another entry.
 */
export interface AnalyticsRevenueMeasures {
  /** MONEY IN, by route, each amount beside its count. The rollup CHECKs that
   *  the two agree about zero in both directions, so a count with no money is
   *  a bug rather than a free payment. */
  topup_revenue: Amount;
  topup_count: number;
  subscription_revenue: Amount;
  subscription_charge_count: number;
  /** A count with no companion amount, deliberately: a failed payment moved no
   *  money, and an amount here would eventually be added to a revenue total. A
   *  rising one is a payment provider in trouble. */
  topup_failed_count: number;

  /** MONEY OUT, as a POSITIVE magnitude. It is ALREADY SUBTRACTED in
   *  `net_revenue`; adding it is the mistake this shape exists to prevent. */
  refunds: Amount;

  /** Payments only — `gross_revenue` is top-ups plus subscription charges, and
   *  deliberately NOT the inference figures below, which are what that money
   *  was spent on. `net_revenue` is gross less refunds and is NEGATIVE in a
   *  month whose refunds settle against an earlier month's payments. */
  gross_revenue: Amount;
  net_revenue: Amount;

  /** SETTLED inference money, read through the settlements. Not expected to
   *  equal the usage rollup's billed `sell_micros` for the same day, which
   *  includes usage whose settlement is still open. `margin` is sell less
   *  cost; NEGATIVE is a real reading and the one this screen exists for. */
  inference_sell: Amount;
  inference_cost: Amount;
  margin: Amount;

  /** `net_revenue` over `paying_users_upper_bound`, or NULL when nobody paid —
   *  never 0, which a screen cannot tell from a real average. The denominator
   *  OVER-counts, so this figure is closer to zero than the true one. */
  arpu: Amount | null;

  /** An UPPER BOUND in two directions: the rollup stores a per-(day,
   *  application, currency) distinct count, so a person who paid on ten days is
   *  counted ten times and one who paid in two applications on one day twice.
   *  Render as "up to N", never as "N people". */
  paying_users_upper_bound: number;

  /** Deliberately unrelated to each other and to the charge count above: a
   *  trial that started, a cancellation before the first renewal and a silent
   *  renewal are all ordinary days. */
  new_subscriptions: number;
  cancelled_subscriptions: number;
}

/** One day of one currency's series. `day` is an ISO date (YYYY-MM-DD): it is a
 *  rollup bucket, so placing it in the browser's timezone would shift the whole
 *  chart by a day. */
export interface AnalyticsRevenueDay extends AnalyticsRevenueMeasures {
  day: string;
}

/** Everything the report has to say about ONE unit. */
export interface AnalyticsRevenueCurrency {
  currency: string;
  totals: AnalyticsRevenueMeasures;
  /** One entry per day of the window at or after `available_from`, ascending,
   *  with a day that moved no money carried as an explicit ZERO in this
   *  currency. Every entry of the list shares the same x-axis. */
  series: AnalyticsRevenueDay[];
}

/**
 * The money picture over time.
 *
 * There is NO grand total and no reporting currency: `currencies` is a list
 * keyed by code, and no figure in one entry may be added to a figure in
 * another. Adding a euro to a dollar does not produce a number in any unit, and
 * this platform records no exchange rate that would let it.
 *
 * The list is ordered ALPHABETICALLY by code and not by size — ranking one
 * currency against another by magnitude is the same forbidden comparison. A
 * screen that wants a different order has every figure it needs to choose one.
 */
export interface AnalyticsRevenue {
  window: AnalyticsWindow;
  /** The first day the revenue rollup reaches, or null when it reaches
   *  nothing. Every series starts there: before it a zero would be an
   *  invention rather than a measurement. */
  available_from: string | null;
  currencies: AnalyticsRevenueCurrency[];

  /** Always true here — the route demands `wallets:read` at the door — and
   *  stamped anyway, so one shape reads off any analytics response. */
  money_visible: boolean;
  identity_visible: boolean;
}

// --------------------------------------------------------------------------
// Usage drift — where the provider's own accounting disagrees with ours
// --------------------------------------------------------------------------
//
// GET /admin/v1/analytics/drift, behind `analytics:read` — the EXISTING
// analytics grant and not one of its own. The cost fields inside need
// `wallets:read` on top and are ABSENT rather than zeroed without it, the way
// every money field on the analytics surface is.
//
// READ ONLY, and there is no writing counterpart. A drift record is EVIDENCE
// FOR A HUMAN: settlement is final, and no settled request is ever repriced
// because a provider's report disagreed with it afterwards. The server sends
// that sentence down as `note` so every client says the same thing.

/**
 * The verdict on one (account, model, day), and it is THREE facts rather than
 * two.
 *
 *   `agrees`       — compared, inside every bound.
 *   `drifted`      — compared, outside a bound. The only one that alerts.
 *   `not_reported` — the provider told us NOTHING about this bucket. There is
 *                    no comparison on the row at all, and every provider-side
 *                    figure is null.
 *
 * SILENCE IS NOT AGREEMENT. A client that draws `not_reported` like `agrees`
 * is reporting an absence as a confirmation, which is the single most
 * dangerous thing this surface can say.
 */
export type UsageDriftStatus = "agrees" | "drifted" | "not_reported";

/** The canonical five, in the order a bill prices them. `total` is deliberately
 *  not a member: a total that drifts is just the dimensions that drifted,
 *  counted a second time. */
export type UsageDriftDimensionName =
  | "input_tokens"
  | "cached_input_tokens"
  | "cache_write_tokens"
  | "output_tokens"
  | "reasoning_tokens";

/** Which provider account a verdict belongs to. The label and the environment
 *  ride on every row because a tenant routinely holds several accounts with one
 *  provider — "openai drifted" is not an actionable sentence when three
 *  accounts could have. */
export interface UsageDriftAccount {
  id: string;
  label: string;
  environment: string;
}

/**
 * One dimension of one verdict.
 *
 * `provider_tokens` is null exactly when `reported` is false, and the flag is
 * sent explicitly so no client has to infer the absence from the null and get
 * it backwards. NEVER render a null as 0 or as a dash that reads like one: ours
 * is always known, theirs frequently is not, and the difference between "they
 * counted nothing" and "they said nothing" is the whole point of the row.
 *
 * `drift_abs` is signed — positive means the provider counted MORE than we
 * billed, the direction that costs the platform money. `drift_bps` is the same
 * difference as a ratio of OUR figure and is null when that ratio is undefined
 * (our figure is zero), which is not a drift of zero and is exactly why the
 * absolute figure sits beside it.
 */
export interface UsageDriftDimension {
  dimension: UsageDriftDimensionName;
  local_tokens: number;
  provider_tokens: number | null;
  reported: boolean;
  drift_abs: number | null;
  drift_bps: number | null;
}

/**
 * One bucket's verdict.
 *
 * `worst_drift_abs` and `worst_drift_bps` are the headline pair, denormalised
 * from the dimensions so the server can order worst-first without an aggregate
 * per row. Both are reported because either alone misleads: a 100% drift on
 * three tokens is noise, and a 1% drift on forty million is not.
 *
 * `alert_id` and `failure_id` are the trail to what was DONE about it. Both
 * null for `agrees`, for `not_reported`, and for a difference inside the
 * alerting bounds — an absence means nobody was woken, not that nobody looked.
 *
 * The three `*_cost_micros` fields are MONEY and are absent entirely without
 * `wallets:read`; read `money_visible` on the envelope before drawing them.
 */
export interface UsageDrift {
  id: string;
  /** A DATE (`YYYY-MM-DD`), not an instant. It is a UTC day bucket, and
   *  parsing it into a local timezone shifts a whole table by a day. */
  day: string;
  provider: string;
  /** The model as the PROVIDER names it, never a client's alias. */
  model: string;
  status: UsageDriftStatus;
  account: UsageDriftAccount;

  worst_drift_bps: number | null;
  worst_drift_abs: number | null;

  local_requests: number;
  provider_requests: number | null;

  dimensions: UsageDriftDimension[];

  alert_id: string | null;
  failure_id: string | null;
  computed_at: string;

  local_cost_micros?: number;
  provider_cost_micros?: number | null;
  cost_drift_micros?: number | null;
}

/** What the list may be narrowed by. `from` and `to` are inclusive `YYYY-MM-DD`
 *  days and an unreadable one is REFUSED with a 400; `limit` is a quantity and
 *  is CLAMPED (100 by default, 500 at most).
 *
 *  `drifted` defaults to FALSE and should usually be left there: a screen that
 *  only ever shows drift cannot show an operator that a provider has STOPPED
 *  REPORTING, which is the failure this sweep is most likely to suffer and
 *  least likely to notice. */
export interface UsageDriftQuery {
  from?: string;
  to?: string;
  provider?: string;
  drifted?: boolean;
  limit?: number;
}

/** The page. `drift` is never null — no verdicts means the sweep has found
 *  nothing to compare yet, which is NOT the same fact as every provider
 *  agreeing. */
export interface UsageDriftReport {
  drift: UsageDrift[];
  /** The server's own sentence about what a row does and does not authorise.
   *  Printed, not paraphrased. */
  note: string;
  /** The RESOLVED filter, so a heading can say whether a short list is quiet or
   *  narrowed. */
  drifted_only: boolean;

  money_visible: boolean;
  identity_visible: boolean;
}

export interface ModelStatusResult {
  model_id: string;
  status: ProviderModelStatus;
  /** The row as it now reads, echoed so a screen does not have to re-fetch the
   *  catalogue to show what it just wrote. Null when unset. */
  deprecation_message: string | null;
  replacement_alias: string | null;
  sunset_at: string | null;
}

/**
 * The advice that travels with a move to `deprecated`, `disabled` or
 * `retired`.
 *
 * All three fields are optional and all three are refused outright on a move back
 * to `available` (400): reinstating a model clears them, because there is
 * nothing to move off and so nowhere to send anybody, and no date to count down
 * to.
 *
 * `deprecation_message` is bounded at 512 CHARACTERS and REFUSED when longer,
 * never truncated — it goes out to callers verbatim in a response header and an
 * SSE frame, and half a sentence is worse than none.
 *
 * `replacement_alias` is validated against the routing profiles, not against a
 * list of names: it must be permitted by at least one profile that also permits
 * the model being deprecated, and it must be served there by some catalogue row
 * OTHER than the one being deprecated. A 409 comes back with a `problem`
 * extension member saying which of the three ways it failed —
 * `model_unrouted`, `replacement_not_permitted` or `replacement_circular`.
 */
export interface ModelDeprecationAdvice {
  deprecation_message?: string;
  replacement_alias?: string;
  /**
   * When the model stops answering, RFC 3339 in UTC. An INSTANT and not a date:
   * the value is shown to callers in every timezone the desktop app ships to,
   * and only they can render it. A value that is not one earns a 400 naming the
   * format.
   */
  sunset_at?: string;
}

/**
 * The capability vocabulary the catalogue records, which is 0006's CHECK on
 * routing.model_capabilities. It is a union rather than `string` so a typo in
 * a form reaches the compiler instead of the server's 400.
 */
export type ModelCapabilityName =
  | "streaming"
  | "tools"
  | "vision"
  | "prompt_caching"
  | "reasoning"
  | "structured_output"
  | "count_tokens";

/**
 * One claim on the way IN.
 *
 * `supported` is required, unlike a form field that could sensibly default:
 * `false` is the positive claim "we checked, this model cannot", and a
 * capability nobody has recorded is left OUT of the array entirely. The two are
 * different states and the server refuses an entry that does not choose.
 */
export interface ModelCapabilityInput {
  capability: ModelCapabilityName;
  supported: boolean;
}

/**
 * The create form. `provider` must be an adapter this build carries — the
 * server names the set in its refusal — and it is fixed forever once written,
 * along with `model`: the two are the natural key that model policies and the
 * price book both join on.
 *
 * There is no `status`: a model is born available, and the retirement ladder
 * belongs to setProviderModelStatus.
 */
export interface NewProviderModel {
  /** What customers will call it. Omit to use the provider's own id, which is
   *  right for most of the catalogue and wrong for the ids carrying a release
   *  date, a -preview suffix or a vendor namespace. */
  alias?: string;
  provider: string;
  model: string;
  display_name: string;
  family?: string;
  context_window?: number | null;
  max_input_tokens?: number | null;
  max_output_tokens?: number | null;
  capabilities: ModelCapabilityInput[];
}

/**
 * The edit form. Every field is optional and OMITTING one leaves the column
 * alone; sending an explicit `null` for a ceiling CLEARS it. The two are
 * different requests, which is why this is a PATCH and not a PUT.
 *
 * `capabilities` REPLACES the recorded set rather than merging into it, so a
 * capability left out of the array stops being recorded at all — the only way
 * to withdraw a claim.
 *
 * `provider` and `model` may be echoed back unchanged (an edit form that round
 * trips the whole row does exactly that) but may not be MOVED: the server
 * answers 409 with `field` and `current_value` extension members naming which
 * half was aimed at.
 */
export interface ProviderModelEdit {
  /** Editable only while no routing profile serves the old value. A name
   *  customers are sending is identity, not description, so renaming one that
   *  is in use is refused 409 with `problem: "alias_in_use"`. */
  alias?: string;
  provider?: string;
  model?: string;
  display_name?: string;
  family?: string;
  context_window?: number | null;
  max_input_tokens?: number | null;
  max_output_tokens?: number | null;
  capabilities?: ModelCapabilityInput[];
}

// --------------------------------------------------------------------------
// Routing profiles
// --------------------------------------------------------------------------

export interface RoutingProfileSummary {
  id: string;
  application_id: string;
  application_name: string;
  /** Absent on the application-wide default. Optional rather than nullable
   *  because that is how the server says it: the field is not sent. */
  organization_id?: string;
  slug: string;
  name: string;
  status: EnabledStatus;
  markup_plan_id?: string;
  markup_plan_name?: string;
  max_input_tokens: number | null;
  max_output_tokens: number | null;
  per_request_limit_micros: number | null;
  daily_limit_micros: number | null;
  monthly_limit_micros: number | null;
  fallback_enabled: boolean;
  edge_enabled: boolean;
  data_retention_days: number;
  provider_count: number;
  model_policy_count: number;
  created_at: string;
}

/** A provider account in a profile's chain. `priority` is the order the
 *  router tries them in; `weight` splits traffic between equals. */
export interface RoutingProfileProvider {
  id: string;
  provider_account_id: string;
  provider: string;
  environment: ProviderEnvironment;
  label: string;
  account_status: ProviderAccountStatus;
  health: ProviderHealth;
  priority: number;
  enabled: boolean;
  weight: number;
}

/**
 * What an attach actually decides, which is very little.
 *
 * The RANK is not here: the server appends. The account carries its own
 * provider, environment and credential, so the only two things left are
 * whether the entry is live and how it splits a tie — and both have a default
 * the server applies when the field is omitted.
 */
export interface NewProfileProvider {
  provider_account_id: string;
  /** Omitted is true. Attaching an upstream nobody wants tried is what the
   *  status route is for. */
  enabled?: boolean;
  /** Omitted takes the column default of 100. It breaks ties between equally
   *  ranked candidates, so demanding it would be asking for a number the
   *  operator has no basis to choose. */
  weight?: number;
}

export interface ModelPolicy {
  id: string;
  model: string;
  provider: string;
  provider_model: string;
  priority: number;
  allowed: boolean;
  max_input_tokens: number | null;
  max_output_tokens: number | null;
  max_thinking_tokens: number | null;
  allow_tools: boolean;
  allow_vision: boolean;
  allow_prompt_caching: boolean;
  allow_reasoning: boolean;
}

export interface RoutingProfileDetail {
  routing_profile: RoutingProfileSummary;
  providers: RoutingProfileProvider[];
  model_policies: ModelPolicy[];
  /**
   * Only the aliases somebody has RESTRICTED.
   *
   * An alias absent from this list is released to everybody — which is exactly
   * what an absent `routing.model_audiences` row means to the resolver, so a
   * screen must render absence as `live` rather than as unknown.
   */
  model_audiences: ModelAudience[];
}

/**
 * What one prompt to one model produced.
 *
 * Both optional halves are `| null` and never absent: a console has to tell
 * "there was no quote" from "the quote was zero", and the first means the pair
 * has no live price version — which is the finding an operator most needs
 * before a customer runs into it.
 */
export interface ModelTestResult {
  /** `connectivity` went straight through the adapter on the account's key;
   *  `routing` resolved the alias through a profile and priced the answer. */
  mode: "connectivity" | "routing";
  /** Whose key answered, always reported — including when the platform chose
   *  it, because "which credential was that" is usually the question. */
  provider_account_id: string;
  provider: string;
  provider_model: string;
  /** Routing mode only: the alias resolved and the chain it would walk. */
  alias?: string;
  chain?: string[];
  /** The upstream round trip alone, not the platform's own work. */
  latency_ms: number;
  text: string;
  input_tokens: number;
  output_tokens: number;
  /** What this exchange would have cost a wallet, as a decimal string. Null
   *  when the pair has no live price version. */
  sell_price: string | null;
  /** The deprecation advice a real caller of this alias would receive, composed
   *  by the same code the chat path uses. */
  notice: {
    status: string;
    message: string;
    replacement_alias?: string;
    sunset_at: string | null;
  } | null;
  /** The vendor's own refusal. A model that answers "no" has answered the
   *  question, so this arrives with a 200 rather than an error. */
  failure: {
    type: string;
    message: string;
    retryable: boolean;
  } | null;
}

export interface ModelTestRun {
  prompt: string;
  max_tokens?: number;
  /** Routing mode. Omit for a plain connectivity check. */
  routing_profile_id?: string;
  /** Whose key to ask through, in connectivity mode. Omit to let the platform
   *  pick any account of the provider that holds a usable key. */
  provider_account_id?: string;
  /** Whose eyes to resolve under, in routing mode — so an operator can ask
   *  "would a tester be able to run this" and get the real answer. */
  audience?: AudienceTier;
}

/**
 * What became of one ticked model.
 *
 * `skipped` carries a reason and is never silent: an operator who ticks six
 * models and gets four must be told which two did not land and why, or they
 * will believe all six are assigned.
 */
export interface ModelAssignment {
  model_id: string;
  provider: string;
  provider_model: string;
  alias: string;
  outcome: "assigned" | "already_assigned" | "skipped";
  reason?: string;
}

export interface ModelAudienceResult {
  routing_profile_id: string;
  model_audience: ModelAudience;
}

export interface RoutingProfileStatusResult {
  routing_profile_id: string;
  status: EnabledStatus;
}

/**
 * The create form.
 *
 * `application_id` and `slug` are fixed forever once written: the application
 * is what fences one application's spend off from every other one's, and the
 * slug is the handle a runbook names this profile by.
 *
 * `organization_id` empty is the application-wide default — "every subject of
 * this application" — which is a different profile from one scoped to an
 * organization, not a missing value. `markup_plan_id` empty takes the platform
 * default plan.
 *
 * Every money limit is an INTEGER number of micros. Not a decimal string like
 * pricing's rates, because these are ceilings compared against a number
 * billing already holds in micros rather than rates the money package has to
 * parse — but never a float either: a `1500000.5` is refused with the field
 * named rather than truncated.
 *
 * There is no `status`. A profile is born DISABLED, so a replacement can be
 * built beside a profile that is currently serving traffic and the two swapped
 * with setRoutingProfileStatus; creating straight into active would mean
 * disabling the live one first and leaving the application with no route in
 * between. Activating a second application-wide profile answers 409.
 */
export interface NewRoutingProfile {
  application_id: string;
  organization_id?: string;
  slug: string;
  name: string;
  markup_plan_id?: string;
  max_input_tokens?: number | null;
  max_output_tokens?: number | null;
  per_request_limit_micros?: number | null;
  daily_limit_micros?: number | null;
  monthly_limit_micros?: number | null;
  /** Omitted takes 0006's defaults: true, true and 30. */
  fallback_enabled?: boolean;
  edge_enabled?: boolean;
  data_retention_days?: number;
}

/**
 * The edit form. Every field is optional and OMITTING one leaves the column
 * alone; sending an explicit `null` for a limit CLEARS it, which is a
 * different request and the reason this is a PATCH.
 *
 * `application_id` and `slug` may be echoed back unchanged — an edit form that
 * round-trips the whole profile does exactly that — but may not be MOVED: the
 * server answers 409 with `field` and `current_value` extension members naming
 * which one was aimed at.
 *
 * `organization_id` IS editable, in both directions: narrowing a profile to
 * one organization or widening it back to the application default is a scope
 * decision. A collision with the profile already holding the active slot comes
 * back as a 409.
 */
export interface RoutingProfileEdit {
  application_id?: string;
  slug?: string;
  name?: string;
  organization_id?: string | null;
  markup_plan_id?: string | null;
  max_input_tokens?: number | null;
  max_output_tokens?: number | null;
  per_request_limit_micros?: number | null;
  daily_limit_micros?: number | null;
  monthly_limit_micros?: number | null;
  fallback_enabled?: boolean;
  edge_enabled?: boolean;
  data_retention_days?: number;
}

/**
 * A route as the add-a-policy form states it.
 *
 * `model` is the ALIAS a client names; `provider` and `provider_model` are the
 * catalogue row that serves it. The two strings routinely differ — one alias is
 * `anthropic/claude-opus-5-20260514` on Anthropic and `anthropic/claude-opus-5`
 * on OpenRouter — and that indirection is the whole reason this table exists.
 *
 * `priority` omitted APPENDS the policy after the alias's current last one.
 * Leave it out unless you are deliberately inserting a new PRIMARY route: the
 * server computes the next rank inside the write, which a page cannot do
 * without racing anybody else adding a policy at the same moment.
 *
 * Resolution orders by `mp.priority` then `rpp.priority`, so this rank is the
 * primary sort key and the profile's provider chain only breaks ties between
 * policies that already share one. Reordering the chain
 * (reorderRoutingProfileProviders) renumbers these ranks on purpose.
 *
 * The four allow flags are optional and OMITTED means true — the schema's own
 * default. Sending `false` is a decision: "this route may not carry images even
 * though the model can".
 *
 * A policy whose provider model has no current price version is refused 409,
 * with `provider` and `model` extension members naming the pair to price. A
 * model that routes but has no price is admitted and then fails to reserve once
 * a real request arrives, which is worse than not being offered at all.
 */
export interface NewModelPolicy {
  model: string;
  provider: string;
  provider_model: string;
  priority?: number;
  max_input_tokens?: number | null;
  max_output_tokens?: number | null;
  max_thinking_tokens?: number | null;
  allow_tools?: boolean;
  allow_vision?: boolean;
  allow_prompt_caching?: boolean;
  allow_reasoning?: boolean;
}

/**
 * The edit form. Omitting a field leaves the column alone; sending an explicit
 * `null` for a ceiling CLEARS it, which is a different request — a cleared
 * ceiling falls through to the profile's, while a zero would admit nothing.
 *
 * There is no `model`, `provider`, `provider_model` or `allowed` here, and the
 * server refuses a body carrying one by name. The first three ARE the route, so
 * moving any of them replaces this policy with a different one — add the new
 * route and archive the old, and both decisions stay in the audit trail with
 * the rank each was given. `allowed` moves on setModelPolicyStatus, so
 * withdrawing an alias is one audited verb of its own rather than a field
 * somebody could flip inside an edit that was ostensibly about a token ceiling.
 */
export interface ModelPolicyEdit {
  priority?: number;
  max_input_tokens?: number | null;
  max_output_tokens?: number | null;
  max_thinking_tokens?: number | null;
  allow_tools?: boolean;
  allow_vision?: boolean;
  allow_prompt_caching?: boolean;
  allow_reasoning?: boolean;
}

// --------------------------------------------------------------------------
// Pricing
// --------------------------------------------------------------------------

/**
 * Every money field below is a DECIMAL STRING in major units — "3.00",
 * "0.000015" — on the way in and on the way out.
 *
 * They are typed as `string` and carried verbatim. The server parses them
 * with internal/money, which is the only component in the system allowed to
 * decide what a rate means; a portal that multiplied by 100 to "help" would
 * be inventing a hundredfold price change, and a portal that parsed
 * "0.000015" into a float would lose the digits that make a per-million rate
 * a rate at all.
 */
export interface PriceVersion {
  id: string;
  provider: string;
  model: string;
  currency: string;
  input_per_million: string;
  cached_input_per_million: string;
  cache_write_per_million: string;
  output_per_million: string;
  reasoning_per_million: string;
  request_fee: string;
  effective_from: string;
  /** Absent while this is the version in force. Set when a newer one closes
   *  it — nothing is ever edited in place. */
  effective_to?: string;
  source: string;
  verified_at?: string;
  /** WHY the row is or is not in force, which `in_force` cannot say: a closed
   *  version was either replaced or withdrawn. Never disagrees with
   *  `in_force` — the two columns are coupled by a database CHECK. */
  status: PriceVersionStatus;
  in_force: boolean;
  /** The PLATFORM's row showing through, not this tenant's own. A tenant reads
   *  the platform's price list because its routing prices against it, and may
   *  not edit it — so this is what decides whether a row offers a Clone or an
   *  edit. Always false for platform staff, who are in no tenant. */
  inherited: boolean;
}

/**
 * `active` is the open row. `superseded` closed because a newer version for the
 * same (provider, model, currency) took over at the same instant. `retired`
 * closed with nothing behind it, so the pair has no price from then on and the
 * gateway refuses requests for it as unpriced.
 *
 * Only the last two are writable: a closed version is never reopened, it is
 * replaced, because reopening one would move the rate a settled charge was
 * reconciled against back into force.
 */
export type PriceVersionStatus = "active" | "superseded" | "retired";
export type PriceVersionTransition = Exclude<PriceVersionStatus, "active">;

export interface PriceVersionFilter {
  provider?: string;
  model?: string;
  limit?: number;
}

/** The rates the server defaults to "0" when they are left out, so an
 *  operator pricing a model with no cache tier does not have to type zeros. */
export interface NewPriceVersion {
  provider: string;
  model: string;
  currency: string;
  input_per_million: string;
  output_per_million: string;
  cached_input_per_million?: string;
  cache_write_per_million?: string;
  reasoning_per_million?: string;
  request_fee?: string;
  source?: string;
}

/**
 * The price proposal queue — a vendor's published list price as the fetcher
 * read it, sitting where it cannot charge anybody.
 *
 * A proposal is a CLAIM, not a rate: the resolver reads
 * billing.provider_price_versions and nothing else, so nothing on this
 * interface can move a quote until an operator publishes it. That is the whole
 * reason the queue exists, and it is why this screen has no "apply" that is
 * not also an audited write.
 */
export interface PriceProposal {
  id: string;
  source_id: string;
  source_slug: string;
  source_name: string;
  provider: string;
  model: string;
  /** The provider's own key for the model, when the vendored map named one
   *  that did not match the catalogue. Empty when it matched. */
  upstream_key?: string;
  currency: string;

  proposed: PriceRates;
  /** Absent when the pair has never been priced. Rendered as "not priced
   *  today" and never as zeroes: a zero rate and no rate are different facts,
   *  and only one of them lets a model serve. */
  in_force?: PriceRates;
  in_force_version_id?: string;
  in_force_effective_from?: string;
  /** The subtraction, done on the server's integer micros. The portal never
   *  computes one: money arithmetic in TypeScript is float arithmetic. */
  deltas: RateDelta[];
  /** The biggest per-dimension move, in basis points, as a MAGNITUDE — the
   *  server has already taken the absolute value, so this is never negative
   *  and must not be printed with a synthesised "+". Which way each dimension
   *  moved is in `deltas`. Absent when nothing is in force to move from. */
  largest_move_bps?: number;

  status: PriceProposalStatus;
  /** The named checks the fetcher raised — the vocabulary is
   *  internal/billing/pricemap/source.go:FlagZeroRate and its siblings. Open
   *  text server-side so a new check needs no migration, so an unknown value
   *  must render as itself rather than be dropped. */
  flags: string[];
  flagged: boolean;
  source_fingerprint: string;
  fetched_at: string;

  proposed_effective_from?: string;
  reviewed_at?: string;
  reviewed_by?: string;
  review_reason_code?: string;
  published_price_version_id?: string;
}

/** Every rate as a decimal string, for the reason PriceVersion gives. */
export interface PriceRates {
  input_per_million: string;
  cached_input_per_million: string;
  cache_write_per_million: string;
  output_per_million: string;
  reasoning_per_million: string;
  request_fee: string;
}

export interface RateDelta {
  /** One of the six PriceRates keys. */
  dimension: string;
  from: string;
  to: string;
  delta: string;
  /** Absent when the in-force rate is zero — a proportion of nothing is not a
   *  number, and printing "∞%" would be worse than printing the two rates. */
  percent_bps?: number;
}

/**
 * `proposed` is the only reviewable state. `superseded` is what a later fetch
 * of a changed rate does to an unreviewed proposal, which is why rejecting is
 * not the only way one leaves the queue.
 */
export type PriceProposalStatus = "proposed" | "published" | "rejected" | "superseded";

export interface PriceProposalFilter {
  /** Omitted means the server's default. "any" asks for the whole history. */
  status?: PriceProposalStatus | "any";
  provider?: string;
  flagged?: boolean;
}

/** An omitted `effective_from` means now. The console never synthesises one
 *  from the browser clock: the server's clock decides when a price changes. */
export interface PublishPriceProposal {
  effective_from?: string;
}

export interface PriceProposalPublished {
  price_proposal: PriceProposal;
  price_version: PriceVersion;
}

export type RoundingMode =
  "down" | "up" | "floor" | "ceil" | "half-up" | "half-even";

export interface MarkupPlan {
  id: string;
  /** Absent on the platform default plan — the one that applies where an
   *  application has not been given its own. */
  application_id?: string;
  application_name?: string;
  is_platform_default: boolean;
  slug: string;
  name: string;
  currency: string;
  /** Basis points: 1500 is 15%. An integer on the wire because a percentage
   *  held as a float is a margin that drifts. */
  percentage_bps: number;
  fixed_request_fee: string;
  minimum_charge: string;
  rounding_mode: RoundingMode;
  effective_from: string;
  effective_to?: string;
  status: MarkupPlanStatus;
  in_force: boolean;
  /** NOT `is_platform_default`. That one says the plan covers every
   *  APPLICATION; this one says it belongs to every TENANT. A plan can be
   *  either, both or neither. */
  inherited: boolean;
}

/**
 * The markup plan's half of the same vocabulary. `archived` rather than
 * `retired` because a plan is withdrawn while its history stays readable, the
 * word `billing.packages` already uses for that act.
 *
 * Archiving is the only writable value, and only for an application-scoped
 * plan: the platform default is what every other application falls back to, so
 * closing it would leave those requests with no plan in force at all. The API
 * refuses it, and the console should not offer the button.
 */
export type MarkupPlanStatus = "active" | "superseded" | "archived";

export interface MarkupPlanFilter {
  application_id?: string;
  limit?: number;
}

export interface NewMarkupPlan {
  /** Omitted or empty means the platform default plan. */
  application_id?: string;
  slug: string;
  name: string;
  currency: string;
  percentage_bps: number;
  fixed_request_fee?: string;
  minimum_charge?: string;
  rounding_mode?: RoundingMode;
}

/**
 * The PATCH body. Every field is optional and an omitted one is CARRIED
 * FORWARD onto the successor version, not reset — so a form that only sends
 * what the operator touched is safe.
 *
 * `application_id`, `slug` and `currency` are the plan's SCOPE, not its
 * content: an edit appends a version into the same scope and closes the last
 * one there, so moving either would leave two plans in force. They are
 * accepted so a form that echoes the whole plan back saves fine, and a body
 * that actually moves one is refused 409 with `field`, `current_value` and
 * `submitted_value`.
 */
export interface MarkupPlanEdit {
  application_id?: string;
  slug?: string;
  currency?: string;

  name?: string;
  percentage_bps?: number;
  fixed_request_fee?: string;
  minimum_charge?: string;
  rounding_mode?: RoundingMode;
}

/** A routing profile named in a response or a refusal — enough to find it on
 *  the console and nothing more. */
export interface RoutingProfileRef {
  id: string;
  slug: string;
  name: string;
  status: string;
}

/**
 * What an edit did: the successor, the row it closed, and the routing profiles
 * moved onto the successor with it.
 *
 * `routing_profiles.markup_plan_id` pins a plan VERSION, so an edit that left
 * them alone would strand every profile on the version it just closed. The
 * re-point happens in the same transaction as the append; it is reported here
 * because it is a write to a resource the caller did not name.
 */
export interface MarkupPlanChange {
  markup_plan: MarkupPlan;
  previous_version: MarkupPlan;
  repointed_routing_profiles: RoutingProfileRef[];
}

/** The status routes answer with the row and whether anything moved, so a
 *  repeated request is visibly a no-op rather than indistinguishable from the
 *  first. */
/**
 * The three clones.
 *
 * A tenant READS the platform's defaults because its own routing and its own
 * catalogue price against them, and it may not edit one — an operator changing
 * an inherited row would move every other tenant's prices. So the only way off
 * a default is to take a copy filed under your own tenant, and that is what
 * these three POSTs do.
 *
 * A price version clone takes NO body: the row is identified by the (provider,
 * model, currency) it already names, and there is nothing about it a tenant
 * gets to choose while copying. The other two take an application, because a
 * markup plan and a package are both sold under one.
 */
export interface CloneMarkupPlanBody {
  /** Omitted means the tenant's own platform-wide default plan — the one every
   *  application of theirs falls back to. */
  application_id?: string;
}

export interface ClonePackageBody {
  /** REQUIRED, unlike the markup plan's: a package is always sold under an
   *  application, and the clone has to land under one of the caller's own. */
  application_id: string;
  /** Optional. The source's slug is reused when it is left out, which is safe
   *  because package slugs are unique per application and the copy lands under
   *  a different one. */
  slug?: string;
}

export interface PriceVersionStatusChange {
  price_version: PriceVersion;
  status: PriceVersionStatus;
  changed: boolean;
}

export interface MarkupPlanStatusChange {
  markup_plan: MarkupPlan;
  status: MarkupPlanStatus;
  changed: boolean;
}

// --------------------------------------------------------------------------
// Subscription packages
// --------------------------------------------------------------------------

export type PackageStatus = "draft" | "active" | "archived";

/**
 * One priced version of a package.
 *
 * Every money field is a DECIMAL STRING beside its currency, exactly as the
 * server formatted it from the integer micros it stores — the same rule the
 * pricing surface follows, and for the same reason: a price this console
 * re-rounded through a JavaScript number is a price nobody agreed to.
 *
 * A version is APPENDED, never edited. `effective_to` is absent on the one
 * in force and set on every superseded row, so the list of versions is the
 * audit trail of what each subscriber was actually sold.
 */
export interface PackageVersion {
  id: string;
  package_id: string;
  currency: string;
  /** What the customer pays each interval. */
  price: string;
  /** What lands in the subscription bucket when an invoice is paid. */
  granted_credit: string;
  /** `granted_credit - price`: what this package gives that spending the same
   *  money at the top-up counter would not. Sent by the server rather than
   *  subtracted here — see the money rule above. */
  bonus: string;
  billing_interval: string;
  interval_count: number;
  /** The provider's PUBLIC handles, so an operator can open the same objects
   *  in the Stripe dashboard. Absent until the seam has minted them, and a
   *  version without them is not subscribable. */
  stripe_product_id?: string;
  stripe_price_id?: string;
  effective_from: string;
  effective_to?: string;
  in_force: boolean;
}

/**
 * A package: the marketing object, plus whichever version is currently in
 * force.
 *
 * `current_version` is nullable and NOT optional, because the server sends it
 * as an explicit null: a package whose only version has been closed is
 * unsellable, and a console that could not tell "no priced version" from "the
 * field did not load" is a console an operator fixes the wrong problem from.
 */
export interface Package {
  id: string;
  application_id: string;
  application_slug: string;
  application_name: string;
  slug: string;
  name: string;
  description?: string;
  status: PackageStatus;
  sort_order: number;
  /** Counted at read time from the live subscriptions, never cached. It is
   *  what decides whether this package can be archived. */
  subscriber_count: number;
  has_subscribers: boolean;
  current_version: PackageVersion | null;
  created_at: string;
  updated_at: string;
  /** A package the PLATFORM published — visible to every tenant and writable
   *  by none of them. The only way off one is `clonePackage`. */
  inherited: boolean;
}

/** Who is on a package right now, as the detail read names them. */
export interface PackageSubscriber {
  subscription_id: string;
  billing_account_id: string;
  subject_type: string;
  /** One or the other, never both — a billing account has a single subject. */
  user_id?: string;
  organization_id?: string;
  display_name: string;
  email?: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_end?: string;
  package_version_id: string;
  created_at: string;
}

export interface PackageDetail {
  package: Package;
  /** Newest first: the history, with the in-force row at the top. */
  versions: PackageVersion[];
  subscribers: PackageSubscriber[];
}

export interface PackageFilter {
  application_id?: string;
  status?: string;
  limit?: number;
  /**
   * Add the platform's ACTIVE default packages to the answer.
   *
   * Off unless asked for, and only one screen asks: the Plans inheritance
   * view, where a platform default is a template offered with a Clone. The
   * Packages console is grouped by application and asks for its own rows —
   * carrying the platform's applications into it showed a tenant operator four
   * products it did not own, with subscriber counts, under cards it could not
   * filter to and rows whose every write is refused.
   */
  include_inherited?: boolean;
}

/**
 * One subscription held by one customer, as the support desk sees it.
 *
 * The terms are the PINNED ones — what this subscriber was sold, taken from
 * the version the row names, not from whatever the package charges today.
 * Quoting today's price at somebody on last quarter's terms is quoting a
 * number their card statement disagrees with.
 *
 * `provider_customer_id` and `provider_subscription_id` are here and are NOT
 * on the customer's own portal, which is the deliberate difference between
 * the two surfaces: they are the provider's public handles for a record an
 * operator has to open in the Stripe dashboard to settle a dispute, and they
 * authenticate nothing. What is absent is the point — no secret, no webhook
 * payload, no checkout URL, and a card that is a brand and four digits
 * because four digits is the whole width this platform can store.
 */
export interface CustomerSubscription {
  id: string;
  billing_account_id: string;
  application_id: string;
  application_slug: string;
  application_name: string;
  /** Where the allowance lands: the link from this card to the ledger that
   *  proves the grant. */
  wallet_id: string;
  currency: string;
  status: string;
  /** The server's answer to "are they still on it" — incomplete, active or
   *  past_due. Sent rather than derived here so this console and the archive
   *  guard cannot drift apart about which rows count. */
  live: boolean;

  package_id: string;
  package_slug: string;
  package_name: string;
  package_version_id: string;
  /** Decimal strings beside `currency`, never numbers — the money rule at the
   *  head of the Amount type. */
  price: string;
  granted_credit: string;
  billing_interval: string;
  interval_count: number;

  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  cancelled_at?: string;

  provider: string;
  provider_customer_id?: string;
  provider_subscription_id?: string;
  payment_method_brand?: string;
  payment_method_last4?: string;

  created_at: string;
  updated_at: string;
}

/**
 * The priced half of a package form.
 *
 * Money goes out as a DECIMAL STRING in major units, exactly as the operator
 * typed it — the same rule the reads follow, and for the same reason: the
 * server parses these with internal/money, and a portal that multiplied one
 * by a hundred on the way out would be inventing a price.
 *
 * `billing_interval` and `interval_count` are optional because the server
 * defaults them to "month, 1", which is every package this platform sells
 * today.
 */
export interface NewPackageVersion {
  currency: string;
  price: string;
  granted_credit: string;
  billing_interval?: string;
  interval_count?: number;
}

/** The create form: the marketing object with its FIRST price embedded. A
 *  package with no version is unsellable, so the two are written together. */
export interface NewPackage extends NewPackageVersion {
  application_id: string;
  slug: string;
  name: string;
  description?: string;
  sort_order?: number;
}

/**
 * What an append answers with: the row just opened AND the row it closed.
 *
 * `previous_version` is null — never absent — when nothing was in force, so a
 * console can tell "this package had no price" apart from "the field did not
 * load". Both are needed to state the change back to the operator, which is
 * the only way an append is distinguishable from an edit in the UI.
 */
export interface PackageVersionChange {
  package: Package;
  version: PackageVersion;
  previous_version: PackageVersion | null;
}

/**
 * What withdrawing a package answers with.
 *
 * `changed` is the whole reason this is a body rather than a 204: archiving a
 * package that is already archived succeeds and moves nothing, and a console
 * that could not tell the two apart would report "withdrawn" to an operator
 * who had just clicked the wrong row twice.
 *
 * The refusal is the interesting half and it is NOT here: a package with live
 * subscribers answers 409 with a sentence naming the count, which arrives as
 * an ApiError and is shown verbatim.
 */
export interface PackageArchive {
  package: Package;
  status: PackageStatus;
  changed: boolean;
}

/**
 * What a subscriber migration did.
 *
 * The DATES are the point of the report, not the count. Nothing changes for a
 * customer today — they keep the period they have already paid for — so
 * "3 subscriptions moved" on its own says nothing about when the old package
 * stops costing money and can be withdrawn. `takes_effect_earliest` and
 * `takes_effect_latest` bracket that, and both are null (never absent) when
 * nothing moved or when everything that moved is still waiting for its first
 * invoice.
 *
 * `pending_first_invoice` is counted apart from the bracket on purpose: those
 * rows change at a date nothing on either side can know yet, and folding them
 * in as "now" would tell an operator the package is free to withdraw before
 * it is.
 */
export interface PackageMigrationReport {
  source_package: Package;
  target_package: Package;
  target_version: PackageVersion;
  migrated: number;
  /** Never null — an empty object when nothing moved, so a console can
   *  iterate it without a special case. */
  migrated_by_status: Record<string, number>;
  takes_effect_earliest: string | null;
  takes_effect_latest: string | null;
  pending_first_invoice: number;
}

// --------------------------------------------------------------------------
// Money: wallets, the ledger, reservations, top-ups and stuck settlements
// --------------------------------------------------------------------------

/**
 * Money on this wire is a DECIMAL STRING with its currency, plus the integer
 * micros the platform actually stores.
 *
 * Never a JSON number: `0.000015` through a float is a rounding error in the
 * worst possible place, and a balance a console has quietly re-rounded is a
 * balance nobody should quote at a customer. Screens render `amount` and
 * compare `micros`; nothing multiplies either.
 */
export interface Amount {
  currency: string;
  amount: string;
  micros: number;
}

export type WalletStatus = "active" | "frozen" | "closed";

export interface Wallet {
  id: string;
  application_id: string;
  application_slug: string;
  application_name: string;
  billing_account_id: string;
  owner_type: "user" | "organization";
  owner_id: string;
  owner_label: string;
  currency: string;
  status: WalletStatus;
  /** Derived from the ledger entries — the authority. `available` is exactly
   *  `subscription_credit + topup_credit`; it keeps its name and its meaning,
   *  because which bucket the money sits in does not change what it can buy. */
  available: Amount;
  /** This cycle's allowance: spent first, and BURNED at the reset. The date
   *  it goes is `subscription_resets_at` on the wallet detail. */
  subscription_credit: Amount;
  /** Credit bought outright, which never expires. */
  topup_credit: Amount;
  reserved: Amount;
  /** What billing.wallets claims. Shown BESIDE the derived figures so the
   *  operator can see the two agree rather than taking it on trust. Per
   *  bucket, because two buckets wrong by opposite amounts sum to a total
   *  that looks right — and the difference between them is what expires. */
  cached_available: Amount;
  cached_subscription_credit: Amount;
  cached_topup_credit: Amount;
  cached_reserved: Amount;
  entry_seq: number;
  cache_entry_seq: number;
  /** Should never be true. When it is, it is a reconciliation job. */
  cache_stale: boolean;
  cached_at?: string;
  held_reservations: number;
  created_at: string;
  updated_at: string;
}

export interface WalletFilter {
  application_id?: string;
  status?: string;
  /** Matches the owner's email address or organisation name. */
  q?: string;
  limit?: number;
}

export type ReservationStatus = "held" | "settled" | "released" | "expired";

export interface Reservation {
  id: string;
  wallet_id: string;
  application_id: string;
  application_slug: string;
  currency: string;
  status: ReservationStatus;
  inference_request_id?: string;
  idempotency_key: string;
  reserved: Amount;
  /** Absent, not zero, until the reservation settles: "charged nothing" and
   *  "not charged yet" are different facts. */
  settled?: Amount;
  released?: Amount;
  reserve_transaction_id?: string;
  settle_transaction_id?: string;
  expires_at: string;
  settled_at?: string;
  released_at?: string;
  created_at: string;
  /** Held, and past its expiry — money nobody is charging for and the
   *  customer cannot spend. Computed from the database clock, not this one. */
  stale: boolean;
}

export interface ReservationFilter {
  wallet_id?: string;
  application_id?: string;
  status?: string;
  stale?: boolean;
  limit?: number;
}

export type TopUpStatus =
  "pending" | "processing" | "succeeded" | "failed" | "cancelled" | "refunded";

/**
 * A top-up, WITHOUT its checkout URL.
 *
 * The Admin API does not send one and this console has nowhere to put one:
 * that URL is the customer's hosted payment page, and a back office that can
 * hand one out is a back office that can be talked into forwarding it. The
 * provider's session and intent ids are what an operator looks a payment up
 * with on the provider's own console.
 */
export interface TopUp {
  id: string;
  wallet_id: string;
  application_id: string;
  application_slug: string;
  billing_account_id: string;
  owner_label: string;
  currency: string;
  amount: Amount;
  status: TopUpStatus;
  provider: string;
  provider_checkout_session_id?: string;
  provider_payment_intent_id?: string;
  ledger_transaction_id?: string;
  failure_reason?: string;
  idempotency_key: string;
  expires_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  /** In flight at the provider with nothing posted to the ledger yet. */
  paid_not_credited: boolean;
}

export interface TopUpFilter {
  wallet_id?: string;
  application_id?: string;
  status?: string;
  /** Everything that took money's shape and never became money. */
  unsettled?: boolean;
  limit?: number;
}

export interface LedgerEntry {
  id: string;
  seq: number;
  account_id: string;
  account_kind: string;
  /** Set only on the two wallet-scoped kinds; a platform revenue leg belongs
   *  to no wallet. */
  wallet_id?: string;
  amount: Amount;
}

export interface LedgerTransaction {
  id: string;
  application_id: string;
  currency: string;
  kind: string;
  idempotency_key?: string;
  reference_type?: string;
  reference_id?: string;
  description?: string;
  occurred_at: string;
  /** The net effect on the wallet this was read for. Zero on a reservation,
   *  which moves money between the wallet's OWN two accounts. */
  wallet_delta: Amount;
  entries: LedgerEntry[];
}

export interface WalletDetail {
  wallet: Wallet;
  reservations: Reservation[];
  top_ups: TopUp[];
  /**
   * When this wallet's subscription bucket is next emptied, ABSENT for a
   * wallet that has never had a subscription.
   *
   * Read off the granted CYCLES, not off the subscription: the sweep acts on
   * cycles, and a customer who has just cancelled still has an unexpired
   * period, is exactly the person an operator is on the phone to, and no
   * longer has a live subscription to read the date off.
   */
  subscription_resets_at?: string;
}

/** The four places money stops moving. A closed set the failures screen
 *  groups by, not free text. */
export type SettlementFailureKind =
  "payment_event" | "usage_event" | "reconciliation_case" | "queue_message";

export interface SettlementFailure {
  kind: SettlementFailureKind;
  id: string;
  occurred_at: string;
  status: string;
  reference?: string;
  subject?: string;
  application_id?: string;
  application_slug?: string;
  detail?: string;
  attempts?: number;
}

// --------------------------------------------------------------------------
// The audit trail
// --------------------------------------------------------------------------

export type AuditOutcome = "allowed" | "denied" | "failed";

export interface AuditAction {
  id: string;
  occurred_at: string;
  admin_email: string;
  admin_user_id?: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  reason_code: string;
  reason_note?: string;
  outcome: AuditOutcome;
  ip?: string;
  request_id?: string;
  detail?: Record<string, unknown>;
}

export interface AuditFilter {
  admin_user_id?: string;
  resource_type?: string;
  outcome?: string;
  action?: string;
  /** RFC 3339, half-open [since, until). The server refuses anything else
   *  rather than guessing a timezone. */
  since?: string;
  until?: string;
  limit?: number;
}

// --------------------------------------------------------------------------
// The client
// --------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// The system failure log
// ---------------------------------------------------------------------------
//
// GET /admin/v1/failures, /failures/summary and /failures/{id}, all behind
// `failures:read`.
//
// What the SOFTWARE did, as opposed to `listActions`, which is what OPERATORS
// did. The two trails answer to different people and have different
// permissions.
//
// ONE ROW IS ONE FAULT, NOT ONE FAILURE. The table is keyed on a fingerprint,
// so a repeat increments `occurrence_count` and moves `last_occurred_at`
// instead of adding a row. A screen that renders a row without its count says
// "one failure" about something that has happened ten thousand times.

/** The deployment a failure happened IN. `edge` holds no database connection of
 *  its own — the Cloudflare worker reports through the platform — so this says
 *  where the fault WAS, not which process wrote the row. */
export type FailureService =
  | "platform-api"
  | "worker"
  | "fallback-inference"
  | "edge"
  | "admin-api";

/**
 * How much a failure MATTERS, which is about consequence rather than about
 * which status code carried it.
 *
 *   warning   the platform absorbed it — a provider that fell back
 *   error     somebody outside got nothing, or a job ran out of attempts
 *   critical  a process died, or money or a reconciliation is at stake
 *
 * There is deliberately no rung below `warning`: a 4xx is a client mistake and
 * never reaches this table.
 */
export type FailureSeverity = "warning" | "error" | "critical";

/** What sort of thing broke. Closed, and `other` is in it — a failure nobody
 *  anticipated has to be recordable, because those are the interesting ones. */
export type FailureKind =
  | "http_5xx"
  | "panic"
  | "job_failed"
  | "provider_error"
  | "settlement_failed"
  | "reconciliation_required"
  | "migration"
  | "other";

/**
 * Which side of the acknowledgement to list. DEFAULTS to `"false"` — the
 * console's job is the open set — and the response echoes what it resolved to,
 * so a screen titles itself from what it received rather than from what it
 * sent.
 */
export type FailureAcknowledgedFilter = "false" | "true" | "all";

/** The window a read ran over. `bounded` is false on the LIST unless a bound
 *  was asked for, and the three fields below it are then null: "the whole
 *  table" is a real answer there and must be tellable from a window that
 *  happens to start at the beginning of time. */
export interface FailureWindow {
  bounded: boolean;
  /** Inclusive ISO dates (YYYY-MM-DD), or null when unbounded. */
  since: string | null;
  until: string | null;
  /** The INCLUSIVE span, so a clamped ask is visible as a number that differs
   *  from the one that was sent. */
  days: number | null;
  /** Written even when false. A shortened answer must not read as the one that
   *  was asked for. */
  clamped: boolean;
}

/**
 * One deduplicated FAULT.
 *
 * `context` is present on the detail route only. It is bounded at 8 KiB in the
 * database, so a two-hundred-row page carrying it would be a megabyte and a
 * half of detail an operator reads one row of; open the row to read it.
 */
export interface Failure {
  id: string;
  /** The 32 bytes the table is keyed on, hex. Two faults whose messages
   *  normalised to the same words are still tellable apart by this, and it is
   *  the thing to quote in a support conversation. */
  fingerprint: string;
  /** When this fault was FIRST seen. It never moves — not on a repeat and not
   *  on an acknowledgement — and it is the column the list is ordered by and
   *  the window filters on. */
  occurred_at: string;
  /** When it last fired. `last_occurred_at > acknowledged_at` is a fault that
   *  has come back; `recurred` below is that comparison already made. */
  last_occurred_at: string;
  /** How many times this fault has fired, EVER — including before any window
   *  the response was asked about. Render it prominently: one row standing for
   *  ten thousand failures must not read like one failure. */
  occurrence_count: number;

  service: FailureService;
  /** Where inside the deployment: a package, a route pattern, a job kind. Part
   *  of the fingerprint, so it is a grouping key rather than a sentence. */
  component: string;
  severity: FailureSeverity;
  kind: FailureKind;
  /** The platform's own error classification where the failure came through
   *  `apperr`, and null for a panic or a dead job. */
  error_code: string | null;
  /**
   * OUR error string. Never a request body and never anything a user typed —
   * the recorder scrubs it and its provider-side entry point takes no message
   * parameter at all. Render it as TEXT: this console is where an operator
   * holding every permission is signed in.
   */
  message: string;
  /** The correlation id the access log and the audit trail carry. */
  request_id: string | null;
  /**
   * The platform request id the client was quoted. It carries NO foreign key —
   * the failure is often the reason that request row does not exist — so it is
   * a value to copy, not a link to follow.
   */
  inference_request_id: string | null;

  /** WHO it happened to. The ids are never withheld: they are what a console
   *  links on. The NAMES behind them are on the detail route and are gated. A
   *  null is a fact — a worker sweep that fails has no user. */
  user_id: string | null;
  device_id: string | null;
  application_id: string | null;

  /** True exactly when `acknowledged_at` is set. A boolean beside the instant,
   *  so a screen deciding how to draw a row does not have to know that a null
   *  timestamp means open. */
  acknowledged: boolean;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  acknowledged_reason: string | null;
  /** This fault has fired again SINCE somebody cleared it. False for an open
   *  row: a fault nobody has dealt with has not recurred, it is still open. An
   *  acknowledgement is never cleared by a repeat, which is why this exists. */
  recurred: boolean;

  /** The scrubbed structured detail — identifiers and classifications only.
   *  Present on the DETAIL route, absent on the list and in the summary's
   *  ranking. Always an object when present, `{}` when there is none. */
  context?: Record<string, unknown>;
}

/** The account a failure happened to. Present on the detail route only, and
 *  ABSENT — not null — for an operator without `users:read`. */
export interface FailureUser {
  id: string;
  email: string;
  display_name: string;
  /** Account state, and it changes what the failure MEANS: a burst of 5xx
   *  against a suspended account is the platform refusing somebody it is
   *  supposed to refuse. */
  status: string;
}

/** The installation a failure happened on. Gated like `FailureUser`. */
export interface FailureDevice {
  id: string;
  device_name: string;
  installation_id: string;
  platform: string;
}

/**
 * One fault whole.
 *
 * `user` and `device` are ABSENT for an operator without `users:read` — never
 * null, because null means "this failure happened to nobody", which is the
 * common case. `identity_visible` says which it is.
 *
 * `application` is NOT gated: an application is a product rather than a person,
 * and every role holding `failures:read` also holds `applications:read`.
 */
export interface FailureDetail extends Failure {
  context: Record<string, unknown>;
  application: AnalyticsApplicationRef | null;
  user?: FailureUser | null;
  device?: FailureDevice | null;
  identity_visible: boolean;
}

export interface FailurePage {
  /** Never null. An empty list is "nothing is failing", which is a fact and a
   *  good one — render it as an answer, not as an empty state. */
  failures: Failure[];
  /** The RESOLVED page size after clamping. */
  limit: number;
  /** Reported even when false: "fifty failures" and "at least fifty failures"
   *  must not be one answer. */
  truncated: boolean;
  /** The RESOLVED acknowledgement filter, which is `"false"` unless asked
   *  otherwise. */
  acknowledged: FailureAcknowledgedFilter;
  /** UNBOUNDED unless a bound was asked for — see FailureWindow. That is the
   *  deliberate difference from the summary, whose window always defaults: an
   *  open fault that started before any window an operator would type must
   *  still be on the console. */
  window: FailureWindow;
  /** The resolved `?application=` filter, or null for every application. */
  application: AnalyticsApplicationRef | null;
  /** Always `"fault"`: what a row of this document is a row OF. */
  row_is: string;
  identity_visible: boolean;
}

/** One day of the failure series, split by severity so a chart can stack it.
 *  `total` is carried rather than left to be summed, so the parts and the total
 *  come off one source. */
export interface FailureDayCount {
  day: string;
  warning: number;
  error: number;
  critical: number;
  total: number;
}

/** One bar of a breakdown: a value from a closed vocabulary, how many distinct
 *  faults carried it, and how many times those faults have fired. */
export interface FailureGroupCount {
  value: string;
  faults: number;
  occurrences: number;
}

/**
 * What the charts read.
 *
 * TWO FIGURES THAT ARE NOT THE SAME NUMBER. `faults` is how many distinct
 * things are broken; `occurrences` is how many times those things have fired.
 * Both bases are stated on the wire because a chart cannot say either out loud:
 * `counts_basis` is `occurred_at`, so `days` is NEW FAULTS per day rather than
 * failures per day, and `occurrences_basis` is `lifetime`, so the totals
 * include firings from before the window.
 */
export interface FailureSummary {
  window: FailureWindow;
  acknowledged: FailureAcknowledgedFilter;
  application: AnalyticsApplicationRef | null;
  faults: number;
  occurrences: number;
  counts_basis: string;
  occurrences_basis: string;
  /** Every day in the window, including the quiet ones. A gap closed by a chart
   *  is a day that never happened drawn as a flat stretch. */
  days: FailureDayCount[];
  /** In each vocabulary's own order rather than by size, so two windows produce
   *  two charts whose bars are in the same places. A value nothing produced is
   *  ABSENT rather than a zero row. */
  services: FailureGroupCount[];
  kinds: FailureGroupCount[];
  severities: FailureGroupCount[];
  /** The noisiest faults, by LIFETIME occurrence count — a different order from
   *  the list's, which is newest-first. */
  top_fingerprints: Failure[];
  /** Reported even when false, so a screen can say "the ten noisiest of N"
   *  rather than implying it has them all. */
  top_fingerprints_truncated: boolean;
  row_is: string;
  identity_visible: boolean;
}

/**
 * What either failure list may be narrowed by.
 *
 * The four vocabulary filters are REFUSED with a 400 naming the accepted set
 * when they carry a value the column could not hold — never answered with an
 * empty list, because "nothing is failing in the worker" is the most dangerous
 * wrong answer this screen can give. `limit` is a quantity and is CLAMPED
 * instead, with the resolved value on the response.
 */
export interface FailureQuery {
  service?: FailureService;
  kind?: FailureKind;
  severity?: FailureSeverity;
  /** Inclusive ISO dates (YYYY-MM-DD) against `occurred_at` — when the fault
   *  STARTED. Optional on the list (no window at all by default) and defaulted
   *  to thirty days on the summary. */
  since?: string;
  until?: string;
  /** UUIDs, as the rows carry them. A malformed one is a 400 rather than an
   *  empty list. */
  user?: string;
  device?: string;
  /** Either the application's uuid or its slug. One that names nothing is a
   *  404: a filter that matched nothing would report "no failures in this
   *  application", which is the sentence a healthy application produces. */
  application?: string;
  /** Defaults to `"false"`. */
  acknowledged?: FailureAcknowledgedFilter;
  limit?: number;
}

/**
 * What one acknowledgement did, in three numbers that are deliberately not one.
 *
 * "Nine cleared" hides the two facts an operator most needs: how many rows the
 * ask found at all, and how many were somebody else's decision already. Render
 * all three, or a colleague having got there first is invisible.
 */
export interface FailureAcknowledgement {
  /** Which form produced this answer. `missing` means nothing on `"filter"`. */
  mode: "ids" | "filter";
  /** Rows the ask identified, whatever state they were in. */
  matched: number;
  /** Rows THIS act wrote. */
  acknowledged: number;
  /** Rows that already carried somebody else's acknowledgement and kept it. */
  already_acknowledged: number;
  /** Ids that named no row. Null on the filter form — a filter cannot name a
   *  row that does not exist, and a zero there would be a real count of
   *  something that cannot happen. */
  missing: number | null;
  /** The two server-side ceilings. Read them rather than keeping a copy in
   *  step by remembering: `max_failure_ids` bounds a selection and
   *  `max_matched` is what a filter is refused for exceeding. */
  max_failure_ids: number;
  max_matched: number;
  /** The RESOLVED filter on the filter form, null on the id form. It carries
   *  the acknowledgement default and the window the server applied, so a
   *  screen can say what it just cleared rather than what it sent. */
  filter: Record<string, unknown> | null;
  application: AnalyticsApplicationRef | null;
}

/** One acknowledged fault: the whole row as it now stands, plus whether THIS
 *  act is what cleared it.
 *
 *  On `changed: false` the acknowledgement fields carry the ORIGINAL operator's
 *  decision, not this caller's — which is the useful answer to a second click:
 *  somebody already dealt with this, and here is who and why. */
export interface AcknowledgedFailure extends Failure {
  changed: boolean;
}

/**
 * The operator tiers — the price list every tenant is sold from.
 *
 * `included_tokens` is a COUNT and never money; `price` is a card charge in
 * whole minor units. There is no exchange rate between the two and none should
 * be inferred from them sitting on the same row.
 */
export type TenantTierStatus = "draft" | "active" | "archived";

export interface TenantTier {
  id: string;
  slug: string;
  name: string;
  description?: string;
  status: TenantTierStatus;
  price: Amount;
  billing_interval: string;
  interval_count: number;
  /** Tokens per period. The free tier's grant is `once` and never expires. */
  included_tokens: number;
  max_operators?: number;
  max_applications?: number;
  /** Dashboard handles only, empty until the catalogue sync has created them. */
  stripe_product_id?: string;
  stripe_price_id?: string;
}

/**
 * A change to one tier. PATCH semantics: an absent key is a field left alone,
 * so this carries only what the form actually changed.
 *
 * `price_minor` is whole minor units (cents) and NOT a decimal string — the
 * form does the multiplication and shows the operator the result before it is
 * sent, because a "49" typed into a field that means cents is a mistake worth
 * failing on rather than one worth guessing about.
 *
 * There is no `slug` and no `currency`. The slug is what the upgrade route
 * names a tier by; re-denominating a tier in place would leave every completed
 * checkout priced in a currency the plan no longer claims.
 */
export interface TenantTierEdit {
  name?: string;
  description?: string;
  status?: TenantTierStatus;
  price_minor?: number;
  included_tokens?: number;
  sort_order?: number;
}

/** One tenant's consumption against the tier it is on. */
export interface TenantTierUsage {
  period_start: string;
  period_end: string;
  tokens_allowance: number;
  tokens_used: number;
  /** The ceiling of every hold live right now — reported apart from
   *  `tokens_used` because one is what the provider billed and the other is
   *  what has been promised. `committed_tokens` is the sum the hard block
   *  compares against the allowance. */
  in_flight_tokens: number;
  committed_tokens: number;
  remaining_tokens: number;
  exhausted: boolean;
}

/**
 * One roster row: a company, its tier and how much of it is gone.
 *
 * `tier`, `subscription` and `usage` are ABSENT rather than zeroed for a tenant
 * on no tier — the platform's own tenant is in exactly that state. An unmetered
 * tenant is not a blocked one, and drawing it as an allowance of zero would
 * make the console disagree with the gateway.
 */
export interface TenantRosterRow {
  id: string;
  slug: string;
  name: string;
  status: string;
  /** The tenant's own app takes its users' payments; SM refuses its payment routes. */
  payments_handled_externally: boolean;
  created_at: string;
  tier?: TenantTier;
  subscription?: {
    id: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
  };
  usage?: TenantTierUsage;
}


/**
 * A tenant's own bill: what tier this company is on, how much of the allowance
 * is gone, and what it could move to.
 *
 * The tenant is NEVER named in the request — it comes off the session's scope,
 * so there is no shape of this call that reads another company's consumption.
 *
 * `plan`, `subscription` and `usage` are ABSENT together, not zeroed, for a
 * tenant on no tier at all. The platform's own tenant is in exactly that state.
 * An unmetered tenant is not a blocked one, and drawing it as an allowance of
 * zero would make this screen disagree with the gateway about who is refused.
 */
export type TenantSubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "cancelled"
  | "expired";

export interface TenantBillingOverview {
  tenant: { id: string; slug: string; name: string; status: string };
  plan?: TenantTier;
  subscription?: {
    id: string;
    status: TenantSubscriptionStatus;
    current_period_start: string;
    current_period_end: string;
    /** Set once the tier is cancelled: access runs to `current_period_end`
     *  and nothing renews. */
    cancel_at_period_end: boolean;
    /** Only while `past_due`: the moment the sweep expires the tier. */
    grace_until?: string | null;
    /** What the next invoice charges — the plan's price as it stands now.
     *  NULL when nothing will renew: a tier no provider bills, or one already
     *  set to end at its period end. */
    next_renewal?: Amount | null;
  };
  usage?: TenantTierUsage;
  /** On no tier because a paid recurring tier ran out — expired after grace,
   *  or cancelled to its end. Refused at reserve; NOT the unmetered state. */
  lapsed: boolean;
  /** What this company could buy: on sale, priced, and not the tier it is
   *  already on. A zero-priced tier is never here — a free grant is something
   *  the platform awards, not something a tenant awards itself. */
  offers: TenantTier[];
}

/**
 * What opening a tier checkout answers with.
 *
 * NOTHING HAS BEEN PAID and the tier has not moved. This is an invitation to
 * pay, exactly as a customer top-up's hosted page is; only the provider's
 * verified webhook activates the tier. A console that treated a 201 here as an
 * upgrade would show an allowance the gateway is still refusing against.
 */
export interface TenantUpgradeCheckout {
  checkout: {
    id: string;
    status: string;
    provider: string;
    session_id: string;
    url: string;
    price: Amount;
    expires_at: string;
  };
  plan: TenantTier;
}

/** The subscription as a cancel or change-tier left it. */
export interface TenantSubscriptionState {
  id: string;
  status: TenantSubscriptionStatus;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  /** Present on a change-tier answer only. */
  plan_id?: string;
}

/**
 * What cancelling answers with. The paid period stays in force: access ends at
 * `access_ends_at`, not now. A repeat is a 200 with `already_cancelling`.
 */
export interface TenantPlanCancelled {
  subscription: TenantSubscriptionState;
  access_ends_at: string;
  already_cancelling: boolean;
}

/**
 * What changing tier answers with. The provider subscription is re-priced now;
 * `applied` says whether our tier has moved yet — on a live provider the
 * proration invoice's `invoice.paid` is what rolls the period, so a console
 * must refetch the overview rather than trust `plan` as the current tier.
 */
export interface TenantPlanChanged {
  subscription: TenantSubscriptionState;
  from_plan: TenantTier;
  plan: TenantTier;
  applied: boolean;
}

export type TenantNotificationKind =
  | "payment_failed"
  | "grace_ending"
  | "subscription_expired";
export type TenantNotificationSeverity = "info" | "warning" | "high";

/**
 * One durable billing notice for the caller's company. Absent `read_at` means
 * unread; marking it read is for the whole tenant, not the one seat.
 */
export interface TenantNotification {
  id: string;
  tenant_id: string;
  kind: TenantNotificationKind;
  severity: TenantNotificationSeverity;
  title: string;
  body: string;
  link?: string;
  created_at: string;
  read_at?: string;
}

export interface TenantNotificationFilter {
  /** Only the unread ones — the banner's read. */
  unread?: boolean;
  /** 1..200; the server defaults to 50. */
  limit?: number;
}


// --------------------------------------------------------------------------
// The alerting spine
// --------------------------------------------------------------------------
//
// GET /admin/v1/alerts and /alerts/{alertID}, both behind `wallets:read`; the
// per-account threshold read under the same grant and WRITTEN behind
// `wallets:adjust`.
//
// The two reads are reads and nothing else. There is no acknowledge here and
// no resolve, deliberately: an alert is evidence that a measured value crossed
// a line, and both edges that can move it are observed by the engine. A
// console button that resolved one would be an operator marking a wallet as no
// longer empty.

export type AlertKind = "wallet_runout" | "usage_drift";
export type AlertSeverity = "info" | "warning" | "critical";
/** What an alert is ABOUT. `wallet` is the only one M1's evaluator raises;
 *  the rest are declared by the server's vocabulary and may appear later. */
export type AlertSubjectType =
  | "wallet"
  | "billing_account"
  | "model"
  | "provider"
  | "tenant";
/** The four paths an alert is recorded against. `portal`, `admin_api` and
 *  `log` are READS marked delivered the moment the row exists; only `email`
 *  is a push that can actually fail. */
export type AlertChannel = "email" | "portal" | "admin_api" | "log";
export type AlertDeliveryStatus = "pending" | "delivered" | "failed";

/**
 * One integer and the unit that says how to read it.
 *
 * `formatted` is present only for money and is the ONLY honest rendering of
 * it: the platform's micro is a millionth of a MINOR unit, so a client that
 * divided `value` by a million would be out by a factor of a hundred. Nothing
 * in this portal does that arithmetic — see screens/money-units.ts, which
 * converts through BigInt at the currency's own exponent, and which is what
 * the threshold editor sends with.
 *
 * `unit` travels with the figure because a consumer that does not recognise it
 * must refuse the number rather than assume one. A `bps` measure rendered as
 * dollars is the failure this pairing exists to prevent.
 */
export interface AlertMeasure {
  value: number;
  unit: string;
  formatted?: string;
  currency?: string;
}

/**
 * One crossing.
 *
 * An alert with no `resolved_at` is OPEN — that absence IS the state, and
 * there is no status column to read instead. `acknowledged_at` is likewise a
 * timestamp rather than a flag, and this console cannot write either.
 *
 * Every subject id is optional because every subject column is nullable: a
 * wallet runout names an account, an application and the wallet's owner, and a
 * future kind may name none of them. Render the absence; do not substitute a
 * zero uuid.
 */
export interface Alert {
  id: string;
  kind: AlertKind;
  severity: AlertSeverity;
  subject_type: AlertSubjectType;
  subject_id: string;

  billing_account_id?: string;
  application_id?: string;
  /** The owner's id and NOT their address. The address is on the email
   *  delivery row, where it is the destination of a send that happened rather
   *  than an attribute of the measurement. */
  user_id?: string;

  observed: AlertMeasure;
  threshold: AlertMeasure;
  /** Burn rate, forecast exhaustion, the window the figure was computed over.
   *  Shape varies by kind and there is no schema for it; it carries no prompt
   *  or completion content. */
  detail?: Record<string, unknown>;

  /** Absent means OPEN. */
  resolved_at?: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  created_at: string;
}

/** One alert's attempt down one channel. `last_error` is the platform's own
 *  error string — the same class of thing /failures already discloses — and
 *  never a customer's data. */
export interface AlertDelivery {
  channel: AlertChannel;
  status: AlertDeliveryStatus;
  destination?: string;
  provider_ref?: string;
  attempts: number;
  last_error?: string;
  next_attempt_at?: string;
  delivered_at?: string;
}

/**
 * One crossing with what happened to it down each channel.
 *
 * The deliveries are the whole point of the detail route: "the customer ran
 * out of money in silence" is the incident this surface exists to answer, and
 * answering it means seeing that the email was attempted, against what, and
 * what the vendor said.
 */
export interface AlertDetail {
  alert: Alert;
  deliveries: AlertDelivery[];
}

/**
 * What the list may be narrowed by.
 *
 * `kind`, `severity` and the two ids are closed vocabularies or identifiers,
 * and an unreadable one is REFUSED with a 400 rather than answered with an
 * empty list — a filter no row could match answers "nothing is wrong", which
 * is the worst thing this screen can say untruthfully. `limit` is a quantity
 * and is CLAMPED instead (50 by default, 200 at most).
 *
 * `state` defaults to `open`: an operator who did not say otherwise is looking
 * at a triage list, and the wallets that are still empty are what belongs on
 * it.
 */
export interface AlertFilter {
  kind?: AlertKind;
  severity?: AlertSeverity;
  state?: "open" | "all";
  account_id?: string;
  application_id?: string;
  limit?: number;
}

/** One account's override of the platform floor. `enabled: false` keeps the
 *  number and stops applying it, so an account can be put back on its tuned
 *  threshold without anybody remembering what it was. */
export interface AlertThreshold {
  id: string;
  kind: AlertKind;
  measure: AlertMeasure;
  enabled: boolean;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * The threshold read and write both answer in this envelope.
 *
 * `threshold: null` means the account is on the PLATFORM FLOOR, which is the
 * normal state of an account nobody has tuned — not a missing row and not an
 * error. That is why the GET answers 200 with a null rather than a 404, and
 * why a screen must say "on the platform floor" rather than draw an empty
 * field that reads as a failed load.
 */
export interface AlertThresholdEnvelope {
  billing_account_id: string;
  kind: AlertKind;
  threshold: AlertThreshold | null;
}

/**
 * The edit.
 *
 * `amount.micros` is a STRING for internal/money's reason: a JSON number is a
 * float64 in the browser that sent it, and is exact only up to about ninety
 * dollars' worth of micros. It is built with `parseUnits`, which does the
 * conversion in BigInt at the currency's own exponent; nothing in this portal
 * multiplies a money figure by a float.
 *
 * `enabled: false` is sent WITH the amount rather than by deleting anything —
 * there is no delete on this surface, because turning an override off and
 * forgetting what it was are different acts.
 */
export interface AlertThresholdEdit {
  kind: AlertKind;
  enabled: boolean;
  amount: { currency: string; micros: string };
}


export interface AdminClient {
  signIn(body: SignInBody): Promise<AdminSession>;
  session(): Promise<AdminSession>;
  signOut(): Promise<void>;
  reasonCodes(): Promise<ReasonVocabulary>;

  /**
   * Provision a whole tenant for a stranger. Unauthenticated, rate limited by
   * the server, and it returns no session — the caller signs in afterwards.
   */
  signUp(body: SignUpBody): Promise<SignUpResult>;
  /**
   * Turn an invitation link into an operator account. Unauthenticated for the
   * same reason signUp is: the person accepting has no session yet, and the
   * tenant and role come off the row the token resolves to, never off the
   * request. Answers 201 and no session.
   */
  acceptInvitation(body: AcceptInviteBody): Promise<{ operator: Operator }>;

  /** The operators of the CALLER's own tenant — not /admin-users with a
   *  filter: the tenant predicate is in the server's WHERE clause. */
  listTeamMembers(): Promise<{ members: Operator[] }>;
  listInvitations(): Promise<{ invitations: TeamInvitation[] }>;
  createInvitation(body: NewInvitation, reason: Reason): Promise<InvitationCreated>;
  /** A POST under the invitation and not a DELETE: the row survives, revoked,
   *  so the trail still says a seat was offered and taken back. */
  revokeInvitation(
    inviteID: string,
    reason: Reason,
  ): Promise<{ invitation: TeamInvitation }>;

  /** The tenant's API keys, behind tenant_api_keys:manage. */
  listTenantAPIKeys(tenantID?: string): Promise<{ api_keys: TenantAPIKey[] }>;
  createTenantAPIKey(body: NewTenantAPIKey, reason: Reason): Promise<TenantAPIKeyCreated>;
  /** A POST under the key, not a DELETE: the row survives as 'revoked'. */
  revokeTenantAPIKey(
    keyID: string,
    reason: Reason,
    tenantID?: string,
  ): Promise<{ api_key: TenantAPIKey }>;

  listApplications(): Promise<{ applications: ApplicationSummary[] }>;
  getApplication(applicationID: string): Promise<ApplicationDetail>;
  /**
   * Register an application. 201 with the whole detail document, because a
   * newly created application IS the page the operator is about to land on.
   */
  createApplication(
    application: NewApplication,
    reason: Reason,
  ): Promise<ApplicationDetail>;
  /** Edit the label and the operator-facing note. See ApplicationEdit. */
  updateApplication(
    applicationID: string,
    edit: ApplicationEdit,
    reason: Reason,
  ): Promise<ApplicationDetail>;
  /**
   * Register a public client under an application. 201 with just the client,
   * not the whole document: the operator is already on the application page and
   * this is a row appended to a list they are looking at.
   */
  createOAuthClient(
    applicationID: string,
    client: NewOAuthClient,
    reason: Reason,
  ): Promise<{ oauth_client: OAuthClient }>;
  /** Edit a client's label and scope allow-list. */
  updateOAuthClient(
    clientID: string,
    edit: OAuthClientEdit,
    reason: Reason,
  ): Promise<{ oauth_client: OAuthClient }>;
  /**
   * Replace a client's redirect URI list. The WHOLE list travels, every time:
   * an add/remove instruction would let two operators in two tabs each apply a
   * delta to a list neither of them last read, and the loser's removal would
   * silently reappear.
   *
   * This is the one edit on a client that can stop a shipped installation from
   * finishing a sign-in, so the answer says what was there before and how many
   * in-flight authorization codes the removal burned — show both.
   */
  replaceRedirectURIs(
    clientID: string,
    redirectURIs: string[],
    reason: Reason,
  ): Promise<RedirectURIReplacement>;
  setApplicationStatus(
    applicationID: string,
    status: EnabledStatus,
    reason: Reason,
  ): Promise<ApplicationStatusResult>;
  setOAuthClientStatus(
    clientID: string,
    status: EnabledStatus,
    reason: Reason,
  ): Promise<OAuthClientStatusResult>;
  revokeEntitlement(
    entitlementID: string,
    reason: Reason,
  ): Promise<EntitlementRevokeResult>;

  /**
   * Moves one subject between release rings for one application.
   *
   * An UPDATE and never an insert: the Admin API holds no INSERT on
   * entitlements, because they are granted when somebody first authorizes into
   * an application rather than by the back office. A revoked or expired
   * entitlement is refused 409 — the ring is only read while the relationship
   * is live, so setting one on a dead row would record a decision nothing acts
   * on.
   */
  setEntitlementTier(
    entitlementID: string,
    tier: AudienceTier,
    reason: Reason,
  ): Promise<{ entitlement: ApplicationEntitlement }>;

  listUsers(filter: UserFilter): Promise<{ users: UserSummary[] }>;
  getUser(userID: string): Promise<UserDetail>;
  /**
   * The account's authentication and device trail, newest first.
   *
   * Behind `users:read` — NOT `audit:read`, which is the admin realm's own
   * trail. Calling it writes an admin_actions row of its own with the filter
   * attached: this is personal data, and taking a copy of it is an act the
   * trail records.
   */
  listUserSecurityEvents(
    userID: string,
    filter?: SecurityEventFilter,
  ): Promise<{ security_events: SecurityEvent[] }>;
  /**
   * What this account's installations have reported about themselves.
   *
   * Behind `users:read` — NOT `devices:read`, which is the other half of the
   * same table (see `deviceAppMetadata`). The list is keyed on the report's
   * `user_id`, so it is "the reports this account was the most recent reporter
   * of" and not "everything reported by every machine this person has ever
   * signed in on". On a shared installation the two differ, and the narrower
   * one is what keeps a colleague's report off this page.
   *
   * Large payloads are ELIDED here and there is no way to ask otherwise:
   * `full` is a 400 on this route. Open the machine to read one whole.
   *
   * Calling it writes an admin_actions row with the filter attached: a
   * metadata payload is arbitrary detail about a customer's own machine, and
   * taking a copy of it is an act the trail records.
   */
  userAppMetadata(
    userID: string,
    filter?: AppMetadataFilter,
  ): Promise<AppMetadataPage>;
  suspendUser(userID: string, reason: Reason): Promise<UserStatusResult>;
  reactivateUser(userID: string, reason: Reason): Promise<UserStatusResult>;
  setUserDeveloperFlag(
    userID: string,
    isEmpiteDeveloper: boolean,
    reason: Reason,
  ): Promise<UserDeveloperFlagResult>;
  /**
   * What ONE installation has reported about itself, newest confirmation
   * first.
   *
   * Behind `devices:read` — the looking permission, which the three device
   * ACTIONS below are deliberately not folded into. It is the only one of the
   * two app-metadata routes that can serve payloads whole (`full: true`), and
   * asking for that shortens the page sharply, because the flag removes the
   * only thing bounding a row's size.
   *
   * A device that has never reported is `reports: []` with a 200. That is a
   * different answer from a 404 (no such installation) and from a single
   * report whose payload is `{}`.
   *
   * Audited, for the same reason the account list is.
   */
  deviceAppMetadata(
    deviceID: string,
    filter?: AppMetadataFilter,
  ): Promise<AppMetadataPage>;
  /** Non-destructive: ends the occupant's session on an installation and
   *  leaves the installation able to be signed in on again. */
  signOutDevice(deviceID: string, reason: Reason): Promise<DeviceSignOutResult>;
  /** Destructive and permanent: blocks the installation for everybody, for
   *  good. There is no unblock — restoreDevice below refuses a block. */
  revokeDevice(deviceID: string, reason: Reason): Promise<DeviceRevokeResult>;
  /** Reopens an installation its OWNER retired, for the support call that
   *  follows retiring the wrong laptop. It is NOT an undo for revokeDevice:
   *  the server honours the same reason list the customer's own restore does,
   *  so a block stays permanent for operators too. */
  restoreDevice(deviceID: string, reason: Reason): Promise<DeviceRestoreResult>;
  revokeBinding(
    bindingID: string,
    reason: Reason,
  ): Promise<BindingRevokeResult>;
  revokeSession(
    sessionID: string,
    reason: Reason,
  ): Promise<SessionRevokeResult>;

  listProviderAccounts(): Promise<{
    provider_accounts: ProviderAccountSummary[];
  }>;
  getProviderAccount(accountID: string): Promise<ProviderAccountDetail>;
  setProviderAccountStatus(
    accountID: string,
    status: ProviderAccountStatus,
    reason: Reason,
  ): Promise<ProviderAccountStatusResult>;

  /**
   * The provider catalogue, serving both audiences from one route.
   *
   * The SERVER decides which rows each sees: a tenant-scoped read is forced to
   * `published`, platform staff see all three statuses. So the operator's
   * add-provider picker and the super admin's console cannot drift apart, and
   * this client has no filter that could make them.
   */
  listProviderTemplates(filter?: {
    status?: ProviderTemplateStatus;
  }): Promise<{ provider_templates: ProviderTemplate[] }>;
  createProviderTemplate(
    template: NewProviderTemplate,
    reason: Reason,
  ): Promise<{ provider_template: ProviderTemplate }>;
  updateProviderTemplate(
    templateID: string,
    edit: ProviderTemplateEdit,
    reason: Reason,
  ): Promise<{ provider_template: ProviderTemplate }>;
  /** Retire, and its inverse. There is no delete: a template whose tenants
   *  hold live accounts cannot be removed without orphaning working traffic. */
  setProviderTemplateStatus(
    templateID: string,
    status: ProviderTemplateStatus,
    reason: Reason,
  ): Promise<{ provider_template: ProviderTemplate }>;
  /**
   * Instantiate a published template as this tenant's own account, sealing the
   * operator's key into its first pool slot. The secret goes in the body of a
   * POST and nowhere else.
   */
  addProviderAccount(
    account: NewProviderAccount,
    reason: Reason,
  ): Promise<AddedProviderAccount>;
  /**
   * rotateProviderCredential is the only place in this portal where a
   * plaintext upstream key exists at all, and it exists for the length of one
   * request body. The response carries safe metadata and nothing else.
   */
  rotateProviderCredential(
    credentialID: string,
    secret: string,
    reason: Reason,
  ): Promise<{ credential: ProviderCredential }>;
  revokeProviderCredential(
    credentialID: string,
    reason: Reason,
  ): Promise<{ credential: ProviderCredential }>;

  /**
   * The whole catalogue, or only what one application routes to.
   *
   * The filter is a narrowing of the same page rather than a different one:
   * models are added platform-wide here, and `application_id` answers "which of
   * these does this product actually use". An id that is not a uuid is refused
   * 400 rather than ignored — a filter that silently falls back to everything
   * shows an operator the whole catalogue while they believe otherwise.
   */
  listProviderModels(filter?: {
    application_id?: string;
  }): Promise<{ models: ProviderModel[] }>;

  /**
   * Asks one provider account what it offers, live.
   *
   * The only call in this API that leaves the deployment. A vendor that refuses
   * or times out comes back 503 with `problem: "provider_unreachable"` and the
   * vendor's own message — the platform is fine, and the operator's next move
   * is to check the credential or wait. `providers:read`, because deciding what
   * to add is reading.
   */
  availableModels(accountID: string): Promise<{
    provider_account_id: string;
    models: DiscoveredModel[];
  }>;

  /**
   * Releases one alias, within one routing profile, to one ring.
   *
   * A PUT: the alias is the resource and the body is its whole state, so
   * sending the same body twice leaves the same world. `live` is the UNDO and
   * removes the restriction entirely rather than storing it — the resolver
   * reads an absent row as live, and two spellings of one state is how a later
   * query comes to disagree with the request path about who can see what.
   *
   * Refused 409 with `problem: "alias_not_permitted"` when the profile serves
   * no such alias: releasing a name nobody can ask for is a decision with no
   * subject.
   */
  /**
   * Sends one prompt to a model and reports what came back.
   *
   * A WRITE — `providers:write` and a reason — even though it changes no row
   * here: it spends real upstream money, so the operator who cannot change the
   * catalogue must not be able to run up its bill, and every run lands in the
   * audit trail. The prompt is NOT audited; this platform stores no prompt
   * anywhere and the trail is not the place to start.
   *
   * A vendor's refusal comes back 200 with `failure` populated: the operator
   * asked whether the model works, and "no, and here is what the provider
   * said" answers that. Only a request the platform could not form, a
   * credential it could not open, or a provider it could not reach is an error.
   */
  testProviderModel(
    modelID: string,
    run: ModelTestRun,
    reason: Reason,
  ): Promise<{ result: ModelTestResult }>;

  /**
   * Ticks catalogue models onto an application's routing profile.
   *
   * Takes catalogue ids and nothing else. The alias comes from the model, the
   * rank appends, and every token ceiling is left unset so the profile's own
   * limits apply — an application-level ceiling is a NARROWING of the
   * profile's and most assignments do not want one.
   *
   * Answers 200 with an outcome per model, skips included: a batch that failed
   * wholesale because the sixth model has no price would lose the five that
   * worked.
   */
  assignModels(
    profileID: string,
    modelIDs: string[],
    reason: Reason,
  ): Promise<{ routing_profile_id: string; assignments: ModelAssignment[] }>;

  /** Takes one catalogue row's route back off a profile. A DELETE of the
   *  policy — the undo of a tick, not the archive that keeps the record. */
  unassignModel(
    profileID: string,
    modelID: string,
    reason: Reason,
  ): Promise<void>;

  /**
   * Turns one model off, or back on, for one application.
   *
   * The model-level twin of `setModelPolicyStatus`, which archives a single
   * ROUTE. Doing this by archiving each provider in turn is how a model ends up
   * half-disabled — still answering through the route somebody missed — so this
   * moves every route for the name in one statement and one audit entry.
   *
   * Enabling restores EVERY route, including one archived individually
   * beforehand. Reports how many actually moved: zero is unremarkable, and
   * means the model was already in that state.
   */
  setModelAliasStatus(
    profileID: string,
    alias: string,
    status: EnabledStatus,
    reason: Reason,
  ): Promise<{ model: string; status: EnabledStatus; routes_changed: number }>;

  setModelAudience(
    profileID: string,
    alias: string,
    audience: AudienceTier,
    reason: Reason,
  ): Promise<ModelAudienceResult>;
  /** Add a row to the model catalogue. 201 with the row as the list renders
   *  it — including the created_at and the trimmed strings the form did not
   *  send — so the console shows the server's view rather than its own. */
  createProviderModel(
    model: NewProviderModel,
    reason: Reason,
  ): Promise<{ model: ProviderModel }>;
  /** Correct a row's recorded facts. See ProviderModelEdit for the three
   *  states an omitted, null and present field mean. */
  updateProviderModel(
    modelID: string,
    edit: ProviderModelEdit,
    reason: Reason,
  ): Promise<{ model: ProviderModel }>;
  /**
   * Move a model along the retirement ladder, optionally saying what callers
   * should switch to. See ModelDeprecationAdvice for what the server checks.
   *
   * `advice` is a fourth argument rather than part of `status` so that the
   * plain reinstate — the call that has nothing to say — stays the short one it
   * has always been.
   */
  setProviderModelStatus(
    modelID: string,
    status: ProviderModelStatus,
    reason: Reason,
    advice?: ModelDeprecationAdvice,
  ): Promise<ModelStatusResult>;
  /**
   * What deprecating a model would cost: requests and an upper bound on people,
   * per (alias, provider, provider_model), over a window ending yesterday.
   *
   * Behind `providers:read`, so the screen that lists the catalogue can show it
   * without a second permission. An empty list is no traffic, not a 404.
   */
  modelUsage(filter?: ModelUsageFilter): Promise<ModelUsageReport>;

  listRoutingProfiles(): Promise<{ routing_profiles: RoutingProfileSummary[] }>;
  getRoutingProfile(profileID: string): Promise<RoutingProfileDetail>;
  /**
   * Onboard an application without running the seed. 201 with the DETAIL
   * document — the same shape getRoutingProfile returns — whose two
   * collections come back empty, which is the honest state of a profile
   * nobody has given a provider chain or a model policy yet.
   */
  createRoutingProfile(
    profile: NewRoutingProfile,
    reason: Reason,
  ): Promise<RoutingProfileDetail>;
  /** Edit the rules a profile admits requests under. See RoutingProfileEdit
   *  for the three states an omitted, null and present field mean. */
  updateRoutingProfile(
    profileID: string,
    edit: RoutingProfileEdit,
    reason: Reason,
  ): Promise<RoutingProfileDetail>;
  setRoutingProfileStatus(
    profileID: string,
    status: EnabledStatus,
    reason: Reason,
  ): Promise<RoutingProfileStatusResult>;
  /**
   * Set the order in which this profile's providers are tried.
   *
   * The WHOLE order goes over the wire, not a move instruction: a "move X up"
   * has to be resolved against whatever the server currently holds, so two
   * operators reordering the same profile produce an order neither asked for.
   * Sending the full sequence makes the request describe an outcome, and the
   * server refuses it outright if the set no longer matches.
   *
   * Returns the profile as the server left it — the reorder cascades into the
   * model policies, which the console cannot compute for itself.
   */
  reorderRoutingProfileProviders(
    profileID: string,
    providerAccountIDs: string[],
    reason: Reason,
  ): Promise<RoutingProfileDetail>;

  /**
   * Authorize this profile to spend one of the tenant's provider accounts.
   *
   * The row this writes is the whole authorization: an account can hold a
   * sealed key and still serve nothing until a profile carries it, which is
   * also why a model policy naming a provider the chain has no account for is
   * skipped rather than written.
   *
   * It APPENDS, at the next free rank, and the rank is not a parameter. A new
   * upstream is a fallback until somebody says otherwise — landing it at the
   * top would move live traffic onto a credential nothing has validated yet.
   * Promoting it is reorderRoutingProfileProviders' job.
   *
   * 409 when the account is already in the chain: one upstream, one rank. 404
   * when the account belongs to another tenant, worded exactly like an id that
   * names nothing.
   */
  attachRoutingProfileProvider(
    profileID: string,
    body: NewProfileProvider,
    reason: Reason,
  ): Promise<RoutingProfileDetail>;
  /**
   * The off switch for ONE account inside ONE profile.
   *
   * Not the account's own status, which stops it everywhere: this drops the
   * link out of resolution while keeping its rank, so putting it back does not
   * mean deciding the order again.
   */
  setRoutingProfileProviderStatus(
    profileID: string,
    accountID: string,
    enabled: boolean,
    reason: Reason,
  ): Promise<RoutingProfileDetail>;
  /**
   * Withdraw the authorization outright.
   *
   * Leaves a gap in the ranks rather than renumbering — priority is a total
   * order and nothing reads it as a dense index. The account itself is
   * untouched and stays in the tenant's pool.
   */
  detachRoutingProfileProvider(
    profileID: string,
    accountID: string,
    reason: Reason,
  ): Promise<RoutingProfileDetail>;

  /**
   * Add one route to a profile. 201 with the policy as the SERVER wrote it —
   * including the priority it computed when the form left one out, which the
   * page could not have known.
   */
  createModelPolicy(
    profileID: string,
    policy: NewModelPolicy,
    reason: Reason,
  ): Promise<{ model_policy: ModelPolicy }>;
  /** Re-rank a route, or change what it may carry. See ModelPolicyEdit for the
   *  three states an omitted, null and present field mean. */
  updateModelPolicy(
    profileID: string,
    policyID: string,
    edit: ModelPolicyEdit,
    reason: Reason,
  ): Promise<{ model_policy: ModelPolicy }>;
  /**
   * Archive a route, and its undo. 'disabled' clears `allowed`, which is the
   * flag resolution filters on, so the route stops serving immediately — and
   * the row stays. There is no delete: the record that this profile once
   * permitted that alias, at that rank, is the thing somebody reads six months
   * later when they ask why an application stopped being able to call a model.
   */
  setModelPolicyStatus(
    profileID: string,
    policyID: string,
    status: EnabledStatus,
    reason: Reason,
  ): Promise<{ model_policy: ModelPolicy }>;

  listPriceVersions(
    filter: PriceVersionFilter,
  ): Promise<{ price_versions: PriceVersion[] }>;
  createPriceVersion(
    body: NewPriceVersion,
    reason: Reason,
  ): Promise<{ price_version: PriceVersion }>;

  /**
   * Close a published version and say why.
   *
   * There is no `updatePriceVersion`, and there never will be, for the reason
   * there is no `updatePackageVersion`: a wallet reservation pins the version
   * id it was priced under and settlement re-reads that exact row, so editing
   * a rate in place would re-price requests already in the air. A correction
   * is `createPriceVersion`, which closes the old row as `superseded` in the
   * same instant.
   *
   * `superseded` is refused 409 when nothing would take over — the extension
   * members carry `provider`, `model`, `currency` and
   * `required_status: "retired"`, which is the transition the operator wanted.
   */
  setPriceVersionStatus(
    versionID: string,
    status: PriceVersionTransition,
    reason: Reason,
  ): Promise<PriceVersionStatusChange>;

  listPriceProposals(
    filter: PriceProposalFilter,
  ): Promise<{ price_proposals: PriceProposal[] }>;

  /**
   * Turn a fetched claim into a rate.
   *
   * Refused 409 when the proposal has already been reviewed, and refused 409
   * when publishing would leave a routable model with no price in force —
   * the gateway cannot serve a model it cannot quote, so the second refusal
   * names the pairs it would have stranded.
   */
  publishPriceProposal(
    proposalID: string,
    body: PublishPriceProposal,
    reason: Reason,
  ): Promise<PriceProposalPublished>;

  /** Close a proposal without pricing anything. The reason code is the record
   *  of why a published vendor rate was not taken. */
  rejectPriceProposal(
    proposalID: string,
    reason: Reason,
  ): Promise<{ price_proposal: PriceProposal }>;

  listMarkupPlans(
    filter: MarkupPlanFilter,
  ): Promise<{ markup_plans: MarkupPlan[] }>;
  createMarkupPlan(
    body: NewMarkupPlan,
    reason: Reason,
  ): Promise<{ markup_plan: MarkupPlan }>;

  /** A PATCH whose effect is an INSERT: the plan changes, the row does not.
   *  See MarkupPlanChange. */
  updateMarkupPlan(
    planID: string,
    body: MarkupPlanEdit,
    reason: Reason,
  ): Promise<MarkupPlanChange>;

  /**
   * Withdraw a plan. Refused 409 while an ACTIVE routing profile still prices
   * against it, with `routing_profile_count` and `routing_profiles` naming
   * them so the operator can re-point them; refused 412 for the platform
   * default, which is never archivable.
   */
  archiveMarkupPlan(
    planID: string,
    reason: Reason,
  ): Promise<MarkupPlanStatusChange>;

  /**
   * Packages: the two reads, and the two writes that define an offer.
   *
   * The rest of the write half — set a status, migrate subscribers, archive —
   * exists on the Admin API and is added here alongside the screens that use
   * it. A method with no caller is a method nobody has checked the reason
   * prompt on.
   *
   * There is no `updatePackageVersion`, and there never will be: a re-price
   * APPENDS a row and closes the one before it, so that what an existing
   * subscriber was sold stays readable. `appendPackageVersion` is that write,
   * named after what it does.
   */
  listPackages(filter: PackageFilter): Promise<{ packages: Package[] }>;
  getPackage(packageID: string): Promise<PackageDetail>;
  createPackage(
    body: NewPackage,
    reason: Reason,
  ): Promise<{ package: Package }>;
  appendPackageVersion(
    packageID: string,
    body: NewPackageVersion,
    reason: Reason,
  ): Promise<PackageVersionChange>;
  /**
   * Put a package on sale, or take it back to draft.
   *
   * The vocabulary is `draft` and `active` and deliberately NOT `archived`:
   * withdrawing has to refuse while subscribers are still on the package, and
   * that guard lives on `archivePackage` rather than being one branch of a
   * status switch that could lose it.
   *
   * A package is born a draft — created, cloned or otherwise — so this is the
   * step between authoring an offer and a customer being able to buy it, and
   * nothing else performs it. It answers with the package as the server left
   * it, and it is audited even when nothing moved: pressing publish on an
   * already-published package is a thing worth being able to see.
   */
  setPackageStatus(
    packageID: string,
    /** `draft` or `active`. `archived` is refused — see archivePackage. */
    status: "draft" | "active",
    reason: Reason,
  ): Promise<{ package: Package; previous: string; changed: boolean }>;
  /**
   * Withdrawal, and its escape hatch.
   *
   * `archivePackage` is a DELETE that deletes nothing: the offer leaves the
   * collection a customer can see, which is what DELETE means to the caller,
   * and the history stays readable for everyone who was ever sold it. It
   * REFUSES while anybody is still on the package, which is why
   * `migratePackageSubscribers` exists next to it — moving every live
   * subscriber onto another offer is the supported way to make that count
   * zero.
   */
  archivePackage(packageID: string, reason: Reason): Promise<PackageArchive>;
  migratePackageSubscribers(
    packageID: string,
    targetPackageID: string,
    reason: Reason,
  ): Promise<PackageMigrationReport>;
  /**
   * What one account is subscribed to.
   *
   * Behind `packages:read`, which is a SEPARATE grant from `users:read` — the
   * same split `getWalletLedger` makes. So this is its own call and its own
   * card on the user page: a support role that may end a stolen laptop's
   * session gets a "not your permission" notice in that one card and a
   * working page everywhere else, rather than a 403 for the whole account.
   */
  listUserSubscriptions(
    userID: string,
  ): Promise<{ subscriptions: CustomerSubscription[] }>;

  /**
   * The money surface is READ ONLY, and that is the design rather than an
   * unfinished half of it. The ledger is append-only in the database; a
   * correction is a compensating transaction posted by the platform, not an
   * edit made from a console. There is no adjust method here because there is
   * no adjust route to call.
   */
  listWallets(filter: WalletFilter): Promise<{ wallets: Wallet[] }>;
  getWallet(walletID: string): Promise<WalletDetail>;
  /** Behind `ledger:read`, which is a separate grant from `wallets:read`:
   *  "is this customer funded" and "every charge they have ever made" are
   *  different things to be trusted with. */
  getWalletLedger(
    walletID: string,
    limit?: number,
  ): Promise<{ transactions: LedgerTransaction[] }>;
  listReservations(
    filter: ReservationFilter,
  ): Promise<{ reservations: Reservation[] }>;
  listTopUps(filter: TopUpFilter): Promise<{ top_ups: TopUp[] }>;
  listSettlementFailures(
    limit?: number,
  ): Promise<{ failures: SettlementFailure[] }>;

  listAuditActions(filter: AuditFilter): Promise<{ actions: AuditAction[] }>;

  /**
   * The platform dashboard in ONE request: totals, the daily series behind
   * them, the equal window before them, the device fleet and the account
   * population.
   *
   * One call rather than five because a screen assembled from five would paint
   * five times, in five orders, and each part would have its own window to get
   * wrong. Behind `analytics:read`; the money fields inside need `wallets:read`
   * on top and are ABSENT without it.
   *
   * Reads the worker's per-day rollups only, so leaving the dashboard open on
   * a wall monitor costs index range scans rather than a walk of the request
   * log.
   */
  analyticsOverview(query?: AnalyticsWindowQuery): Promise<AnalyticsOverview>;

  /**
   * The leaderboard: which accounts are behind the overview's headline.
   *
   * Behind `analytics:read`, with the columns that NAME the person gated on
   * `users:read` and the money columns on `wallets:read`; both classes are
   * ABSENT rather than zeroed when withheld. Sorting by `cost`, `revenue` or
   * `margin` needs `wallets:read` too, because the ranking is the figure.
   *
   * Reads the per-user rollup ALONE, so a window over the whole account base
   * is an index range scan rather than a walk of the request log.
   */
  analyticsUsers(query?: AnalyticsTopUsersQuery): Promise<AnalyticsTopUsers>;

  /**
   * One account's usage broken down by day, application and model — the row of
   * the leaderboard opened up.
   *
   * Needs `analytics:read` AND `users:read`: the whole document is one named
   * person's behaviour, so unlike the leaderboard there is no version of it
   * left once the name is withheld. The money fields inside need `wallets:read`
   * on top and are ABSENT without it, and so is the wallet list.
   *
   * AGGREGATES ONLY — no request list, no prompt, no completion, no message
   * count. A `userID` no account has is a 404, not an empty document.
   */
  analyticsUserDetail(
    userID: string,
    query?: AnalyticsWindowQuery,
  ): Promise<AnalyticsUserDetail>;

  /**
   * The same window split by PRODUCT rather than by person: one row per
   * application, with its daily series and its settled revenue.
   *
   * Behind `analytics:read` ALONE — nothing in the document names a person, so
   * `users:read` buys nothing here. The money fields still need `wallets:read`
   * and are ABSENT rather than zeroed without it, which is a state a
   * provider-ops operator really reaches.
   *
   * An application with NO traffic comes back as a row of ZEROES rather than
   * not at all. That is the row the screen exists for. `?application=<slug>`
   * narrows to one, for the application detail page; a slug no application has
   * is a 404.
   */
  analyticsApplications(
    query?: AnalyticsApplicationsQuery,
  ): Promise<AnalyticsApplications>;

  /**
   * Revenue, cost and margin over time, per currency and per day.
   *
   * The ONE analytics route that refuses a caller holding `analytics:read`:
   * every figure in the document is money, so the omission rule the rest of the
   * surface runs on would leave a window and a list of codes. It needs
   * `analytics:read` AND `wallets:read`, and a caller without the second gets a
   * 403 rather than an empty shell.
   *
   * Reads the revenue rollup ALONE — not the ledger it was derived from, and
   * not the catalogue, which is why there is no `application` filter here:
   * "which product earns" is `analyticsApplications`, which reads the catalogue
   * precisely so it can list a product that earned nothing.
   */
  analyticsRevenue(query?: AnalyticsWindowQuery): Promise<AnalyticsRevenue>;

  /**
   * Where the PROVIDER's own accounting disagrees with ours: one row per
   * provider account, model and UTC day, worst disagreement first.
   *
   * Behind `analytics:read` alone — nothing in the document names a person.
   * The cost fields need `wallets:read` on top and are ABSENT rather than
   * zeroed without it.
   *
   * READ ONLY and it corrects nothing. Every row is evidence for a human to act
   * on outside this console; a settled request is never repriced.
   *
   * `drifted` is left FALSE by default on purpose. The `not_reported` rows are
   * the ones that say a provider has gone quiet, and hiding them is how a
   * broken feed looks like a healthy one.
   */
  analyticsDrift(query?: UsageDriftQuery): Promise<UsageDriftReport>;

  /**
   * What is BREAKING: the fingerprinted system failure log, newest fault first.
   *
   * Behind `failures:read`. Defaults to the OPEN set (`acknowledged: "false"`)
   * and to NO window, so an unacknowledged fault that started months ago is
   * still on the console — the page size and the database's own partial index
   * are what bound it.
   *
   * One row is one FAULT. Render `occurrence_count` beside the message or the
   * screen says "one failure" about something that has happened ten thousand
   * times. `context` is not on this route; open a row for it.
   */
  listFailures(query?: FailureQuery): Promise<FailurePage>;

  /**
   * The same set counted four ways, plus the noisiest faults — what the charts
   * on the failures screen read.
   *
   * Takes the SAME filter as `listFailures`, so the charts and the table below
   * them are two views of one set. The one thing that differs is the window: it
   * DEFAULTS to thirty days here, because a daily series has to name the days
   * it is counting. Both responses report the window they used, so a screen
   * showing them together can see when they differ.
   */
  failureSummary(query?: FailureQuery): Promise<FailureSummary>;

  /**
   * One fault whole: every column, the scrubbed `context`, and the three
   * subject ids resolved.
   *
   * `user` and `device` are ABSENT without `users:read`; `application` is not
   * gated. An id no row has is a 404, and so is a malformed one.
   */
  getFailure(failureID: string): Promise<FailureDetail>;

  /**
   * Mark ONE fault as dealt with.
   *
   * Behind `failures:clear`, which is a SMALLER set of holders than
   * `failures:read`: four roles read the log and two may take a row off it.
   * Hide the control on `permissions`, and expect a 403 anyway — a hidden
   * button is a courtesy, not an authorisation control.
   *
   * Acknowledging a row somebody has already dealt with is a reported NO-OP,
   * not an error: 200 with `changed: false` and the ORIGINAL operator's
   * decision on the row. Render that rather than a success message; the useful
   * answer to a second click is "somebody already closed this, and here is who".
   *
   * The reason travels in the headers and lands on the ROW as well as in the
   * audit trail — the failure row is the durable copy.
   */
  acknowledgeFailure(
    failureID: string,
    reason: Reason,
  ): Promise<AcknowledgedFailure>;

  /**
   * Mark a SELECTION of faults as dealt with: the rows an operator ticked.
   *
   * At most `max_failure_ids` (200) of them, and a longer list is REFUSED
   * rather than truncated — acknowledging the first two hundred and reporting
   * success would leave rows the operator believes are cleared sitting open.
   * Ids naming no row come back as `missing` rather than failing the batch, so
   * a bulk act on a stale page still works.
   */
  acknowledgeFailures(
    failureIDs: string[],
    reason: Reason,
  ): Promise<FailureAcknowledgement>;

  /**
   * Mark everything a FILTER matches as dealt with.
   *
   * Takes the same query as `listFailures`, through the same builder, so "clear
   * what I am looking at" is structural rather than two parsers agreeing: send
   * the filter the table is currently drawn from.
   *
   * A filter matching more than `max_matched` (10,000) rows is REFUSED with a
   * 422 naming the count — clearing an ongoing incident in bulk is how it stops
   * being visible. Show the count and let the operator narrow; nothing is
   * written on that path.
   */
  acknowledgeMatchingFailures(
    query: FailureQuery,
    reason: Reason,
  ): Promise<FailureAcknowledgement>;

  /**
   * What has FIRED: one row per crossing, newest first.
   *
   * Behind `wallets:read` and deliberately not a permission of its own. A
   * wallet runout alert discloses exactly what `listWallets` does — is this
   * customer funded — at one moment in the past, so a second grant would be a
   * second copy of one authorization rule with nothing extra to decide.
   *
   * Defaults to the OPEN set. An alert with no `resolved_at` is open; ask for
   * `state: "all"` to see the history, which is a different question.
   */
  listAlerts(filter: AlertFilter): Promise<{ alerts: Alert[] }>;

  /**
   * One crossing, and what happened to it down each of the four channels.
   *
   * The deliveries are why this is a separate call: the list cannot carry them
   * without a query per row, and they are the evidence that the person whose
   * money ran out was actually told.
   */
  getAlert(alertID: string): Promise<AlertDetail>;

  /**
   * The line ONE account is measured against, or null for the platform floor.
   *
   * Behind `wallets:read`. `kind` may be omitted — the server defaults it to
   * `wallet_runout`, the only kind with an evaluator in this build — but the
   * portal sends it anyway, so a second kind arriving does not silently
   * re-point an editor that never named one.
   */
  getAlertThreshold(
    accountID: string,
    kind?: AlertKind,
  ): Promise<AlertThresholdEnvelope>;

  /**
   * Move that line. The one WRITE on the alerting surface, and it touches no
   * alert.
   *
   * Behind `wallets:adjust`, the grant that already governs changing a
   * customer's money position: deciding at what balance we warn somebody is
   * the same class of act as adjusting the balance itself. It is an UNSAFE
   * method, so it goes through RequireReason like every other mutation here —
   * the server writes the `admin.alert_threshold.set` audit row itself.
   */
  setAlertThreshold(
    accountID: string,
    body: AlertThresholdEdit,
    reason: Reason,
  ): Promise<AlertThresholdEnvelope>;

  /**
   * The tier price list, unfiltered — drafts and archived rows included.
   *
   * Platform-only: `tenants:manage` is refused to any tenant-confined subject
   * whatever its roles say, because this one list is what every tenant is sold
   * from. A tenant that could read it as an editor is a tenant one PATCH away
   * from setting its own allowance.
   */
  listTenantTiers(): Promise<{ tiers: TenantTier[] }>;
  /**
   * Re-price a tier, change its allowance, or withdraw it from sale.
   *
   * NOBODY ALREADY ON THE TIER IS AFFECTED. The allowance a subscriber is
   * spending was copied onto its usage period when that period opened, so this
   * moves what the NEXT period opens with. Archiving likewise cancels nothing:
   * existing subscribers stay, the tier just stops being offered.
   */
  updateTenantTier(
    tierID: string,
    edit: TenantTierEdit,
    reason: Reason,
  ): Promise<{ tier: TenantTier }>;
  /**
   * Every company, its tier and its consumption — the cross-tenant read, gated
   * on `tenants:read_all` rather than on the write grant above: seeing who is
   * about to run out is support work, changing what they pay is not.
   */
  listTenantRoster(): Promise<{ tenants: TenantRosterRow[] }>;
  /** One company — the same row the roster draws. `tenants:read_all`. */
  getTenant(tenantID: string): Promise<{ tenant: TenantRosterRow }>;
  /**
   * Mark a company whose own application takes its end users' payments.
   *
   * While it is on, the platform refuses its own top-up checkout and
   * subscription routes for that company's applications; the tenant signal
   * routes (credits, subscription references, cycles) are unaffected.
   * `tenants:manage`, reason-coded, and answers with the updated row.
   */
  setTenantPaymentsHandledExternally(
    tenantID: string,
    enabled: boolean,
    reason: Reason,
  ): Promise<{ tenant: TenantRosterRow }>;

  /**
   * The CALLER's own company: its tier, its consumption and what it could buy.
   *
   * No tenant argument, ever — see TenantBillingOverview. Platform staff carry
   * no tenant and are refused with a sentence rather than a row.
   */
  tenantBilling(): Promise<TenantBillingOverview>;
  /**
   * Open the hosted page that buys a bigger tier. Answers 201 with a URL and
   * moves nothing: the tier changes when the provider's signed webhook says the
   * money arrived. `tenant_billing:manage`, which only a tenant owner holds.
   */
  startTenantUpgrade(planSlug: string, reason: Reason): Promise<TenantUpgradeCheckout>;
  /**
   * Stop the caller's company's tier renewing. The paid period stays in force;
   * a repeat changes nothing. `tenant_billing:manage`.
   */
  cancelTenantPlan(reason: Reason): Promise<TenantPlanCancelled>;
  /**
   * Move the caller's company's recurring tier onto another one now, named by
   * slug. `tenant_billing:manage`.
   */
  changeTenantPlan(planSlug: string, reason: Reason): Promise<TenantPlanChanged>;
  /** The caller's company's billing notices, newest first. `tenant_billing:read`. */
  listTenantNotifications(
    filter?: TenantNotificationFilter,
  ): Promise<{ notifications: TenantNotification[] }>;
  /**
   * Mark one notice read for the whole company. Session-only — dismissing a
   * banner grants nothing — but a write, so it carries a reason code. Another
   * tenant's id is a 404.
   */
  markTenantNotificationRead(
    notificationID: string,
    reason: Reason,
  ): Promise<{ notification: TenantNotification }>;

  /**
   * Take a copy of an inherited platform default, filed under the caller's own
   * tenant.
   *
   * A POST under the SOURCE row, not under a collection: that is the resource
   * the operator has in hand and the id the audit trail records the descent
   * from. All three refuse a row that is already yours (422) and refuse a
   * caller in no tenant at all (403) — platform staff have nothing to clone
   * INTO.
   */
  clonePriceVersion(
    versionID: string,
    reason: Reason,
  ): Promise<{ price_version: PriceVersion }>;
  cloneMarkupPlan(
    planID: string,
    body: CloneMarkupPlanBody,
    reason: Reason,
  ): Promise<{ markup_plan: MarkupPlan }>;
  clonePackage(
    packageID: string,
    body: ClonePackageBody,
    reason: Reason,
  ): Promise<{ package: Package }>;
}

/**
 * createAdminClient binds the surface to one transport.
 *
 * The transport already sends `credentials: "include"`, which is what carries
 * the HttpOnly `esm_admin_session` cookie. Nothing here reads or writes that
 * cookie: the browser holds it, the Admin API sets it, and a portal that
 * could see it would be a portal an XSS could steal a back-office session
 * from.
 *
 * Every path segment interpolated below is a server-issued id, and every one
 * is encoded anyway — an id that ever grows a slash must not be able to
 * address a different route.
 */
export function createAdminClient(transport: Transport): AdminClient {
  const id = (value: string) => encodeURIComponent(value);

  return {
    signIn: (body) =>
      transport.request<AdminSession>({
        operationId: "adminSignIn",
        method: "POST",
        path: `${ADMIN}/session`,
        body,
      }),

    session: () =>
      transport.request<AdminSession>({
        operationId: "adminWhoAmI",
        method: "GET",
        path: `${ADMIN}/session`,
      }),

    signOut: () =>
      transport.request<void>({
        operationId: "adminSignOut",
        method: "DELETE",
        path: `${ADMIN}/session`,
      }),

    reasonCodes: () =>
      transport.request<ReasonVocabulary>({
        operationId: "adminReasonCodes",
        method: "GET",
        path: `${ADMIN}/reason-codes`,
      }),

    signUp: (body) =>
      transport.request<SignUpResult>({
        operationId: "adminSignUp",
        method: "POST",
        path: `${ADMIN}/signup`,
        body,
      }),

    acceptInvitation: (body) =>
      transport.request<{ operator: Operator }>({
        operationId: "adminAcceptInvitation",
        method: "POST",
        path: `${ADMIN}/invites/accept`,
        body,
      }),

    listTeamMembers: () =>
      transport.request<{ members: Operator[] }>({
        operationId: "adminListTeamMembers",
        method: "GET",
        path: `${ADMIN}/tenant/members`,
      }),

    listInvitations: () =>
      transport.request<{ invitations: TeamInvitation[] }>({
        operationId: "adminListInvitations",
        method: "GET",
        path: `${ADMIN}/tenant/invites`,
      }),

    createInvitation: (body, reason) =>
      transport.request<InvitationCreated>({
        operationId: "adminCreateInvitation",
        method: "POST",
        path: `${ADMIN}/tenant/invites`,
        body,
        headers: reasonHeaders(reason),
      }),

    revokeInvitation: (inviteID, reason) =>
      transport.request<{ invitation: TeamInvitation }>({
        operationId: "adminRevokeInvitation",
        method: "POST",
        path: `${ADMIN}/tenant/invites/${id(inviteID)}/revoke`,
        headers: reasonHeaders(reason),
      }),

    listTenantAPIKeys: (tenantID) =>
      transport.request<{ api_keys: TenantAPIKey[] }>({
        operationId: "adminListTenantAPIKeys",
        method: "GET",
        path: `${ADMIN}/tenant/api-keys${tenantID ? `?tenant_id=${id(tenantID)}` : ""}`,
      }),

    createTenantAPIKey: (body, reason) =>
      transport.request<TenantAPIKeyCreated>({
        operationId: "adminCreateTenantAPIKey",
        method: "POST",
        path: `${ADMIN}/tenant/api-keys`,
        body,
        headers: reasonHeaders(reason),
      }),

    revokeTenantAPIKey: (keyID, reason, tenantID) =>
      transport.request<{ api_key: TenantAPIKey }>({
        operationId: "adminRevokeTenantAPIKey",
        method: "POST",
        path: `${ADMIN}/tenant/api-keys/${id(keyID)}/revoke${tenantID ? `?tenant_id=${id(tenantID)}` : ""}`,
        headers: reasonHeaders(reason),
      }),

    listApplications: () =>
      transport.request<{ applications: ApplicationSummary[] }>({
        operationId: "adminListApplications",
        method: "GET",
        path: `${ADMIN}/applications`,
      }),

    getApplication: (applicationID) =>
      transport.request<ApplicationDetail>({
        operationId: "adminGetApplication",
        method: "GET",
        path: `${ADMIN}/applications/${id(applicationID)}`,
      }),

    createApplication: (application, reason) =>
      transport.request<ApplicationDetail>({
        operationId: "adminCreateApplication",
        method: "POST",
        path: `${ADMIN}/applications`,
        headers: reasonHeaders(reason),
        body: application,
      }),

    updateApplication: (applicationID, edit, reason) =>
      transport.request<ApplicationDetail>({
        operationId: "adminUpdateApplication",
        method: "PATCH",
        path: `${ADMIN}/applications/${id(applicationID)}`,
        headers: reasonHeaders(reason),
        body: edit,
      }),

    createOAuthClient: (applicationID, client, reason) =>
      transport.request<{ oauth_client: OAuthClient }>({
        operationId: "adminCreateOAuthClient",
        method: "POST",
        path: `${ADMIN}/applications/${id(applicationID)}/oauth-clients`,
        headers: reasonHeaders(reason),
        body: client,
      }),

    updateOAuthClient: (clientID, edit, reason) =>
      transport.request<{ oauth_client: OAuthClient }>({
        operationId: "adminUpdateOAuthClient",
        method: "PATCH",
        path: `${ADMIN}/oauth-clients/${id(clientID)}`,
        headers: reasonHeaders(reason),
        body: edit,
      }),

    replaceRedirectURIs: (clientID, redirectURIs, reason) =>
      transport.request<RedirectURIReplacement>({
        operationId: "adminReplaceOAuthClientRedirectURIs",
        method: "PUT",
        path: `${ADMIN}/oauth-clients/${id(clientID)}/redirect-uris`,
        headers: reasonHeaders(reason),
        body: { redirect_uris: redirectURIs },
      }),

    setApplicationStatus: (applicationID, status, reason) =>
      transport.request<ApplicationStatusResult>({
        operationId: "adminSetApplicationStatus",
        method: "POST",
        path: `${ADMIN}/applications/${id(applicationID)}/status`,
        headers: reasonHeaders(reason),
        body: { status },
      }),

    setOAuthClientStatus: (clientID, status, reason) =>
      transport.request<OAuthClientStatusResult>({
        operationId: "adminSetOAuthClientStatus",
        method: "POST",
        path: `${ADMIN}/oauth-clients/${id(clientID)}/status`,
        headers: reasonHeaders(reason),
        body: { status },
      }),

    revokeEntitlement: (entitlementID, reason) =>
      transport.request<EntitlementRevokeResult>({
        operationId: "adminRevokeEntitlement",
        method: "POST",
        path: `${ADMIN}/entitlements/${id(entitlementID)}/revoke`,
        headers: reasonHeaders(reason),
      }),

    setEntitlementTier: (entitlementID, tier, reason) =>
      transport.request<{ entitlement: ApplicationEntitlement }>({
        operationId: "adminSetEntitlementTier",
        method: "PATCH",
        path: `${ADMIN}/entitlements/${id(entitlementID)}/tier`,
        headers: reasonHeaders(reason),
        body: { tier },
      }),

    listUsers: (filter) =>
      transport.request<{ users: UserSummary[] }>({
        operationId: "adminListUsers",
        method: "GET",
        path: `${ADMIN}/users`,
        // `developer` is absent from the query string unless it was set:
        // encodeQuery drops undefined but keeps false, which is exactly the
        // three cases the Admin API parses — true, false, and not asked.
        query: {
          q: filter.q,
          status: filter.status,
          developer: filter.developer,
          limit: filter.limit,
        },
      }),

    getUser: (userID) =>
      transport.request<UserDetail>({
        operationId: "adminGetUser",
        method: "GET",
        path: `${ADMIN}/users/${id(userID)}`,
      }),

    listUserSecurityEvents: (userID, filter) =>
      transport.request<{ security_events: SecurityEvent[] }>({
        operationId: "adminListUserSecurityEvents",
        method: "GET",
        path: `${ADMIN}/users/${id(userID)}/security-events`,
        // encodeQuery drops undefined, so an omitted filter sends no query
        // string at all and the server applies its own default page.
        query: {
          limit: filter?.limit,
          outcome: filter?.outcome,
          event_type: filter?.event_type,
          since: filter?.since,
          until: filter?.until,
        },
      }),

    userAppMetadata: (userID, filter) =>
      transport.request<AppMetadataPage>({
        operationId: "adminUserAppMetadata",
        method: "GET",
        path: `${ADMIN}/users/${id(userID)}/app-metadata`,
        // No `full` here, and not by omission: the server refuses it on this
        // route, so offering it would be a 400 the console produced itself.
        query: { limit: filter?.limit, source: filter?.source },
      }),

    deviceAppMetadata: (deviceID, filter) =>
      transport.request<AppMetadataPage>({
        operationId: "adminDeviceAppMetadata",
        method: "GET",
        path: `${ADMIN}/devices/${id(deviceID)}/app-metadata`,
        // encodeQuery drops undefined but keeps false, so `full: false` is
        // sent as such — which the server accepts and echoes, and which is
        // what lets a screen turn the flag off again without reloading.
        query: {
          limit: filter?.limit,
          source: filter?.source,
          full: filter?.full,
        },
      }),

    suspendUser: (userID, reason) =>
      transport.request<UserStatusResult>({
        operationId: "adminSuspendUser",
        method: "POST",
        path: `${ADMIN}/users/${id(userID)}/suspend`,
        headers: reasonHeaders(reason),
      }),

    reactivateUser: (userID, reason) =>
      transport.request<UserStatusResult>({
        operationId: "adminReactivateUser",
        method: "POST",
        path: `${ADMIN}/users/${id(userID)}/reactivate`,
        headers: reasonHeaders(reason),
      }),

    // SET, not toggle: the caller sends the state it wants. The Admin API
    // rejects a body that omits the field, so this always sends it — passing
    // `undefined` through would turn a portal bug into a 400 rather than into
    // a silently revoked developer.
    setUserDeveloperFlag: (userID, isEmpiteDeveloper, reason) =>
      transport.request<UserDeveloperFlagResult>({
        operationId: "adminSetUserDeveloperFlag",
        method: "POST",
        path: `${ADMIN}/users/${id(userID)}/developer-flag`,
        headers: reasonHeaders(reason),
        body: { is_empite_developer: isEmpiteDeveloper },
      }),

    signOutDevice: (deviceID, reason) =>
      transport.request<DeviceSignOutResult>({
        operationId: "adminSignOutDevice",
        method: "POST",
        path: `${ADMIN}/devices/${id(deviceID)}/sign-out`,
        headers: reasonHeaders(reason),
      }),

    revokeDevice: (deviceID, reason) =>
      transport.request<DeviceRevokeResult>({
        operationId: "adminRevokeDevice",
        method: "POST",
        path: `${ADMIN}/devices/${id(deviceID)}/revoke`,
        headers: reasonHeaders(reason),
      }),

    restoreDevice: (deviceID, reason) =>
      transport.request<DeviceRestoreResult>({
        operationId: "adminRestoreDevice",
        method: "POST",
        path: `${ADMIN}/devices/${id(deviceID)}/restore`,
        headers: reasonHeaders(reason),
      }),

    revokeBinding: (bindingID, reason) =>
      transport.request<BindingRevokeResult>({
        operationId: "adminRevokeBinding",
        method: "POST",
        path: `${ADMIN}/bindings/${id(bindingID)}/revoke`,
        headers: reasonHeaders(reason),
      }),

    revokeSession: (sessionID, reason) =>
      transport.request<SessionRevokeResult>({
        operationId: "adminRevokeSession",
        method: "POST",
        path: `${ADMIN}/sessions/${id(sessionID)}/revoke`,
        headers: reasonHeaders(reason),
      }),

    listProviderAccounts: () =>
      transport.request<{ provider_accounts: ProviderAccountSummary[] }>({
        operationId: "adminListProviderAccounts",
        method: "GET",
        path: `${ADMIN}/providers`,
      }),

    getProviderAccount: (accountID) =>
      transport.request<ProviderAccountDetail>({
        operationId: "adminGetProviderAccount",
        method: "GET",
        path: `${ADMIN}/providers/${id(accountID)}`,
      }),

    setProviderAccountStatus: (accountID, status, reason) =>
      transport.request<ProviderAccountStatusResult>({
        operationId: "adminSetProviderAccountStatus",
        method: "POST",
        path: `${ADMIN}/providers/${id(accountID)}/status`,
        headers: reasonHeaders(reason),
        body: { status },
      }),

    listTenantTiers: () =>
      transport.request<{ tiers: TenantTier[] }>({
        operationId: "adminListTenantTiers",
        method: "GET",
        path: `${ADMIN}/platform/tiers`,
      }),

    updateTenantTier: (tierID, edit, reason) =>
      transport.request<{ tier: TenantTier }>({
        operationId: "adminUpdateTenantTier",
        method: "PATCH",
        path: `${ADMIN}/platform/tiers/${id(tierID)}`,
        headers: reasonHeaders(reason),
        body: edit,
      }),

    listTenantRoster: () =>
      transport.request<{ tenants: TenantRosterRow[] }>({
        operationId: "adminListTenantRoster",
        method: "GET",
        path: `${ADMIN}/platform/tenants`,
      }),

    getTenant: (tenantID) =>
      transport.request<{ tenant: TenantRosterRow }>({
        operationId: "adminGetTenant",
        method: "GET",
        path: `${ADMIN}/platform/tenants/${id(tenantID)}`,
      }),

    setTenantPaymentsHandledExternally: (tenantID, enabled, reason) =>
      transport.request<{ tenant: TenantRosterRow }>({
        operationId: "adminSetTenantPaymentsHandledExternally",
        method: "PUT",
        path: `${ADMIN}/platform/tenants/${id(tenantID)}/payments-handled-externally`,
        headers: reasonHeaders(reason),
        body: { enabled },
      }),

    tenantBilling: () =>
      transport.request<TenantBillingOverview>({
        operationId: "adminTenantBilling",
        method: "GET",
        path: `${ADMIN}/tenant/billing`,
      }),

    startTenantUpgrade: (planSlug, reason) =>
      transport.request<TenantUpgradeCheckout>({
        operationId: "adminStartTenantUpgrade",
        method: "POST",
        path: `${ADMIN}/tenant/billing/upgrade`,
        headers: reasonHeaders(reason),
        // The tier is named by SLUG and not by id, which is why the tiers
        // console cannot rename one: the slug is the handle the checkout is
        // opened against.
        body: { plan_slug: planSlug },
      }),

    cancelTenantPlan: (reason) =>
      transport.request<TenantPlanCancelled>({
        operationId: "adminCancelTenantPlan",
        method: "POST",
        path: `${ADMIN}/tenant/billing/cancel`,
        headers: reasonHeaders(reason),
      }),

    changeTenantPlan: (planSlug, reason) =>
      transport.request<TenantPlanChanged>({
        operationId: "adminChangeTenantPlan",
        method: "POST",
        path: `${ADMIN}/tenant/billing/change-tier`,
        headers: reasonHeaders(reason),
        body: { plan_slug: planSlug },
      }),

    listTenantNotifications: (filter = {}) =>
      transport.request<{ notifications: TenantNotification[] }>({
        operationId: "adminListTenantNotifications",
        method: "GET",
        path: `${ADMIN}/tenant/billing/notifications`,
        query: {
          unread: filter.unread ? "true" : undefined,
          limit: filter.limit,
        },
      }),

    markTenantNotificationRead: (notificationID, reason) =>
      transport.request<{ notification: TenantNotification }>({
        operationId: "adminMarkTenantNotificationRead",
        method: "POST",
        path: `${ADMIN}/tenant/billing/notifications/${id(notificationID)}/read`,
        headers: reasonHeaders(reason),
      }),

    clonePriceVersion: (versionID, reason) =>
      transport.request<{ price_version: PriceVersion }>({
        operationId: "adminClonePriceVersion",
        method: "POST",
        path: `${ADMIN}/pricing/price-versions/${id(versionID)}/clone`,
        headers: reasonHeaders(reason),
      }),

    cloneMarkupPlan: (planID, body, reason) =>
      transport.request<{ markup_plan: MarkupPlan }>({
        operationId: "adminCloneMarkupPlan",
        method: "POST",
        path: `${ADMIN}/pricing/markup-plans/${id(planID)}/clone`,
        headers: reasonHeaders(reason),
        body,
      }),

    clonePackage: (packageID, body, reason) =>
      transport.request<{ package: Package }>({
        operationId: "adminClonePackage",
        method: "POST",
        path: `${ADMIN}/packages/${id(packageID)}/clone`,
        headers: reasonHeaders(reason),
        body,
      }),

    listProviderTemplates: (filter) =>
      transport.request<{ provider_templates: ProviderTemplate[] }>({
        operationId: "adminListProviderTemplates",
        method: "GET",
        path: `${ADMIN}/provider-templates`,
        // encodeQuery drops undefined, so an unfiltered call sends no query
        // string at all and takes whatever the caller's scope is allowed.
        query: { status: filter?.status },
      }),

    createProviderTemplate: (template, reason) =>
      transport.request<{ provider_template: ProviderTemplate }>({
        operationId: "adminCreateProviderTemplate",
        method: "POST",
        path: `${ADMIN}/provider-templates`,
        headers: reasonHeaders(reason),
        body: template,
      }),

    updateProviderTemplate: (templateID, edit, reason) =>
      transport.request<{ provider_template: ProviderTemplate }>({
        operationId: "adminUpdateProviderTemplate",
        method: "PATCH",
        path: `${ADMIN}/provider-templates/${id(templateID)}`,
        headers: reasonHeaders(reason),
        body: edit,
      }),

    setProviderTemplateStatus: (templateID, status, reason) =>
      transport.request<{ provider_template: ProviderTemplate }>({
        operationId: "adminSetProviderTemplateStatus",
        method: "POST",
        path: `${ADMIN}/provider-templates/${id(templateID)}/status`,
        headers: reasonHeaders(reason),
        body: { status },
      }),

    // The key is in the BODY, for rotateProviderCredential's reason: a secret
    // in a path or a query is a secret in the browser history, in the next
    // request's referrer and in every access log along the way.
    addProviderAccount: (account, reason) =>
      transport.request<AddedProviderAccount>({
        operationId: "adminAddProviderAccount",
        method: "POST",
        path: `${ADMIN}/providers`,
        headers: reasonHeaders(reason),
        body: account,
      }),

    // The secret goes in the BODY of a POST and nowhere else: not in the
    // path, not in a query parameter. A key in a URL is a key in the browser
    // history, in the referrer of the next request and in every access log
    // between here and the server.
    rotateProviderCredential: (credentialID, secret, reason) =>
      transport.request<{ credential: ProviderCredential }>({
        operationId: "adminRotateProviderCredential",
        method: "POST",
        path: `${ADMIN}/provider-credentials/${id(credentialID)}/rotate`,
        headers: reasonHeaders(reason),
        body: { secret },
      }),

    revokeProviderCredential: (credentialID, reason) =>
      transport.request<{ credential: ProviderCredential }>({
        operationId: "adminRevokeProviderCredential",
        method: "POST",
        path: `${ADMIN}/provider-credentials/${id(credentialID)}/revoke`,
        headers: reasonHeaders(reason),
      }),

    listProviderModels: (filter) =>
      transport.request<{ models: ProviderModel[] }>({
        operationId: "adminListProviderModels",
        method: "GET",
        path: `${ADMIN}/models`,
        query: filter?.application_id
          ? { application_id: filter.application_id }
          : undefined,
      }),

    availableModels: (accountID) =>
      transport.request<{
        provider_account_id: string;
        models: DiscoveredModel[];
      }>({
        operationId: "adminAvailableModels",
        method: "GET",
        path: `${ADMIN}/providers/${id(accountID)}/available-models`,
      }),

    testProviderModel: (modelID, run, reason) =>
      transport.request<{ result: ModelTestResult }>({
        operationId: "adminTestProviderModel",
        method: "POST",
        path: `${ADMIN}/models/${id(modelID)}/test`,
        headers: reasonHeaders(reason),
        body: run,
      }),

    assignModels: (profileID, modelIDs, reason) =>
      transport.request<{
        routing_profile_id: string;
        assignments: ModelAssignment[];
      }>({
        operationId: "adminAssignModels",
        method: "POST",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/model-assignments`,
        headers: reasonHeaders(reason),
        body: { model_ids: modelIDs },
      }),

    unassignModel: (profileID, modelID, reason) =>
      transport.request<void>({
        operationId: "adminUnassignModel",
        method: "DELETE",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/model-assignments/${id(modelID)}`,
        headers: reasonHeaders(reason),
      }),

    setModelAliasStatus: (profileID, alias, status, reason) =>
      transport.request<{
        model: string;
        status: EnabledStatus;
        routes_changed: number;
      }>({
        operationId: "adminSetModelAliasStatus",
        method: "POST",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/model-aliases/${encodeURIComponent(alias)}/status`,
        headers: reasonHeaders(reason),
        body: { status },
      }),

    setModelAudience: (profileID, alias, audience, reason) =>
      transport.request<ModelAudienceResult>({
        operationId: "adminSetModelAudience",
        method: "PUT",
        // The alias is a path segment and is encoded: it is operator-chosen
        // text, and one containing a slash would otherwise address a route
        // nobody wrote.
        path: `${ADMIN}/routing-profiles/${id(profileID)}/model-audiences/${encodeURIComponent(alias)}`,
        headers: reasonHeaders(reason),
        body: { audience },
      }),

    createProviderModel: (model, reason) =>
      transport.request<{ model: ProviderModel }>({
        operationId: "adminCreateProviderModel",
        method: "POST",
        path: `${ADMIN}/models`,
        headers: reasonHeaders(reason),
        body: model,
      }),

    updateProviderModel: (modelID, edit, reason) =>
      transport.request<{ model: ProviderModel }>({
        operationId: "adminUpdateProviderModel",
        method: "PATCH",
        path: `${ADMIN}/models/${id(modelID)}`,
        headers: reasonHeaders(reason),
        body: edit,
      }),

    setProviderModelStatus: (modelID, status, reason, advice) =>
      transport.request<ModelStatusResult>({
        operationId: "adminSetProviderModelStatus",
        method: "POST",
        path: `${ADMIN}/models/${id(modelID)}/status`,
        headers: reasonHeaders(reason),
        // Spread rather than always sending the two keys: the server reads an
        // explicit null the same way as an absent key, but a reinstate that
        // posted `deprecation_message: null` would still read, to anybody
        // watching the wire, as a request that had something to say.
        body: { status, ...advice },
      }),

    modelUsage: (filter) =>
      transport.request<ModelUsageReport>({
        operationId: "adminModelUsage",
        method: "GET",
        path: `${ADMIN}/models/usage`,
        // encodeQuery drops undefined, so an omitted filter sends no query
        // string and the server applies its own 30-day default.
        query: { window_days: filter?.window_days, alias: filter?.alias },
      }),

    listRoutingProfiles: () =>
      transport.request<{ routing_profiles: RoutingProfileSummary[] }>({
        operationId: "adminListRoutingProfiles",
        method: "GET",
        path: `${ADMIN}/routing-profiles`,
      }),

    getRoutingProfile: (profileID) =>
      transport.request<RoutingProfileDetail>({
        operationId: "adminGetRoutingProfile",
        method: "GET",
        path: `${ADMIN}/routing-profiles/${id(profileID)}`,
      }),

    createRoutingProfile: (profile, reason) =>
      transport.request<RoutingProfileDetail>({
        operationId: "adminCreateRoutingProfile",
        method: "POST",
        path: `${ADMIN}/routing-profiles`,
        headers: reasonHeaders(reason),
        body: profile,
      }),

    updateRoutingProfile: (profileID, edit, reason) =>
      transport.request<RoutingProfileDetail>({
        operationId: "adminUpdateRoutingProfile",
        method: "PATCH",
        path: `${ADMIN}/routing-profiles/${id(profileID)}`,
        headers: reasonHeaders(reason),
        body: edit,
      }),

    setRoutingProfileStatus: (profileID, status, reason) =>
      transport.request<RoutingProfileStatusResult>({
        operationId: "adminSetRoutingProfileStatus",
        method: "POST",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/status`,
        headers: reasonHeaders(reason),
        body: { status },
      }),

    reorderRoutingProfileProviders: (profileID, providerAccountIDs, reason) =>
      transport.request<RoutingProfileDetail>({
        operationId: "adminReorderRoutingProfileProviders",
        method: "POST",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/provider-order`,
        headers: reasonHeaders(reason),
        body: { provider_account_ids: providerAccountIDs },
      }),

    attachRoutingProfileProvider: (profileID, body, reason) =>
      transport.request<RoutingProfileDetail>({
        operationId: "adminAttachRoutingProfileProvider",
        method: "POST",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/providers`,
        headers: reasonHeaders(reason),
        body,
      }),

    setRoutingProfileProviderStatus: (profileID, accountID, enabled, reason) =>
      transport.request<RoutingProfileDetail>({
        operationId: "adminSetRoutingProfileProviderStatus",
        method: "POST",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/providers/${id(accountID)}/status`,
        headers: reasonHeaders(reason),
        body: { enabled },
      }),

    detachRoutingProfileProvider: (profileID, accountID, reason) =>
      transport.request<RoutingProfileDetail>({
        operationId: "adminDetachRoutingProfileProvider",
        method: "DELETE",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/providers/${id(accountID)}`,
        headers: reasonHeaders(reason),
      }),

    createModelPolicy: (profileID, policy, reason) =>
      transport.request<{ model_policy: ModelPolicy }>({
        operationId: "adminCreateModelPolicy",
        method: "POST",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/model-policies`,
        headers: reasonHeaders(reason),
        body: policy,
      }),

    updateModelPolicy: (profileID, policyID, edit, reason) =>
      transport.request<{ model_policy: ModelPolicy }>({
        operationId: "adminUpdateModelPolicy",
        method: "PATCH",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/model-policies/${id(policyID)}`,
        headers: reasonHeaders(reason),
        body: edit,
      }),

    setModelPolicyStatus: (profileID, policyID, status, reason) =>
      transport.request<{ model_policy: ModelPolicy }>({
        operationId: "adminSetModelPolicyStatus",
        method: "POST",
        path: `${ADMIN}/routing-profiles/${id(profileID)}/model-policies/${id(policyID)}/status`,
        headers: reasonHeaders(reason),
        body: { status },
      }),

    listPriceVersions: (filter) =>
      transport.request<{ price_versions: PriceVersion[] }>({
        operationId: "adminListPriceVersions",
        method: "GET",
        path: `${ADMIN}/pricing/price-versions`,
        query: {
          provider: filter.provider,
          model: filter.model,
          limit: filter.limit,
        },
      }),

    // The body is handed over as the operator typed it. Every rate is a
    // decimal string and stays one all the way to internal/money.
    createPriceVersion: (body, reason) =>
      transport.request<{ price_version: PriceVersion }>({
        operationId: "adminCreatePriceVersion",
        method: "POST",
        path: `${ADMIN}/pricing/price-versions`,
        headers: reasonHeaders(reason),
        body,
      }),

    listPriceProposals: (filter) =>
      transport.request<{ price_proposals: PriceProposal[] }>({
        operationId: "adminListPriceProposals",
        method: "GET",
        path: `${ADMIN}/pricing/price-proposals`,
        query: {
          status: filter.status,
          provider: filter.provider,
          // The server reads the literal string "true", and an absent
          // parameter is "show everything" — sending "false" would be a
          // filter nobody asked for.
          flagged: filter.flagged ? "true" : undefined,
        },
      }),

    publishPriceProposal: (proposalID, body, reason) =>
      transport.request<PriceProposalPublished>({
        operationId: "adminPublishPriceProposal",
        method: "POST",
        path: `${ADMIN}/pricing/price-proposals/${id(proposalID)}/publish`,
        headers: reasonHeaders(reason),
        body,
      }),

    rejectPriceProposal: (proposalID, reason) =>
      transport.request<{ price_proposal: PriceProposal }>({
        operationId: "adminRejectPriceProposal",
        method: "POST",
        path: `${ADMIN}/pricing/price-proposals/${id(proposalID)}/reject`,
        headers: reasonHeaders(reason),
      }),

    listMarkupPlans: (filter) =>
      transport.request<{ markup_plans: MarkupPlan[] }>({
        operationId: "adminListMarkupPlans",
        method: "GET",
        path: `${ADMIN}/pricing/markup-plans`,
        query: { application_id: filter.application_id, limit: filter.limit },
      }),

    // Only the window moves. Every amount on the row stays exactly as it was
    // published — see the interface comment.
    setPriceVersionStatus: (versionID, status, reason) =>
      transport.request<PriceVersionStatusChange>({
        operationId: "adminSetPriceVersionStatus",
        method: "POST",
        path: `${ADMIN}/pricing/price-versions/${id(versionID)}/status`,
        headers: reasonHeaders(reason),
        body: { status },
      }),

    createMarkupPlan: (body, reason) =>
      transport.request<{ markup_plan: MarkupPlan }>({
        operationId: "adminCreateMarkupPlan",
        method: "POST",
        path: `${ADMIN}/pricing/markup-plans`,
        headers: reasonHeaders(reason),
        body,
      }),

    updateMarkupPlan: (planID, body, reason) =>
      transport.request<MarkupPlanChange>({
        operationId: "adminUpdateMarkupPlan",
        method: "PATCH",
        path: `${ADMIN}/pricing/markup-plans/${id(planID)}`,
        headers: reasonHeaders(reason),
        body,
      }),

    // The status is sent explicitly even though "archived" is the only value
    // the route accepts: a body-less POST that withdrew a pricing rule is a
    // request a mis-wired form could make by accident.
    archiveMarkupPlan: (planID, reason) =>
      transport.request<MarkupPlanStatusChange>({
        operationId: "adminArchiveMarkupPlan",
        method: "POST",
        path: `${ADMIN}/pricing/markup-plans/${id(planID)}/status`,
        headers: reasonHeaders(reason),
        body: { status: "archived" },
      }),

    listPackages: (filter) =>
      transport.request<{ packages: Package[] }>({
        operationId: "adminListPackages",
        method: "GET",
        path: `${ADMIN}/packages`,
        // An unset filter field is left off the query string entirely. An
        // empty `status=` is a value the server validates and refuses, and
        // the operator would read the 400 as "there are no packages".
        query: {
          application_id: filter.application_id,
          status: filter.status,
          limit: filter.limit,
          // Sent only when true. The server reads the exact string "true", so
          // a `false` on the wire would be noise that means the same as the
          // absence it is longer than.
          include_inherited: filter.include_inherited ? "true" : undefined,
        },
      }),

    getPackage: (packageID) =>
      transport.request<PackageDetail>({
        operationId: "adminGetPackage",
        method: "GET",
        path: `${ADMIN}/packages/${id(packageID)}`,
      }),

    setPackageStatus: (packageID, status, reason) =>
      transport.request<{ package: Package; previous: string; changed: boolean }>({
        operationId: "adminSetPackageStatus",
        method: "POST",
        path: `${ADMIN}/packages/${id(packageID)}/status`,
        headers: reasonHeaders(reason),
        body: { status },
      }),

    createPackage: (body, reason) =>
      transport.request<{ package: Package }>({
        operationId: "adminCreatePackage",
        method: "POST",
        path: `${ADMIN}/packages`,
        headers: reasonHeaders(reason),
        body,
      }),

    // A POST under the package rather than a PUT on it: the one thing that
    // must not be expressible from this console is "change this price".
    appendPackageVersion: (packageID, body, reason) =>
      transport.request<PackageVersionChange>({
        operationId: "adminAppendPackageVersion",
        method: "POST",
        path: `${ADMIN}/packages/${id(packageID)}/versions`,
        headers: reasonHeaders(reason),
        body,
      }),

    // A POST under the SOURCE package rather than under the target: the
    // operator's decision is "retire this offer", and that is the row the
    // audit trail has to hang on.
    migratePackageSubscribers: (packageID, targetPackageID, reason) =>
      transport.request<PackageMigrationReport>({
        operationId: "adminMigratePackageSubscribers",
        method: "POST",
        path: `${ADMIN}/packages/${id(packageID)}/migrate`,
        headers: reasonHeaders(reason),
        body: { target_package_id: targetPackageID },
      }),

    listUserSubscriptions: (userID) =>
      transport.request<{ subscriptions: CustomerSubscription[] }>({
        operationId: "adminListUserSubscriptions",
        method: "GET",
        path: `${ADMIN}/users/${id(userID)}/subscriptions`,
      }),

    archivePackage: (packageID, reason) =>
      transport.request<PackageArchive>({
        operationId: "adminArchivePackage",
        method: "DELETE",
        path: `${ADMIN}/packages/${id(packageID)}`,
        headers: reasonHeaders(reason),
      }),

    listWallets: (filter) =>
      transport.request<{ wallets: Wallet[] }>({
        operationId: "adminListWallets",
        method: "GET",
        path: `${ADMIN}/wallets`,
        query: {
          application_id: filter.application_id,
          status: filter.status,
          q: filter.q,
          limit: filter.limit,
        },
      }),

    getWallet: (walletID) =>
      transport.request<WalletDetail>({
        operationId: "adminGetWallet",
        method: "GET",
        path: `${ADMIN}/wallets/${id(walletID)}`,
      }),

    getWalletLedger: (walletID, limit) =>
      transport.request<{ transactions: LedgerTransaction[] }>({
        operationId: "adminGetWalletLedger",
        method: "GET",
        path: `${ADMIN}/wallets/${id(walletID)}/ledger`,
        query: { limit },
      }),

    listReservations: (filter) =>
      transport.request<{ reservations: Reservation[] }>({
        operationId: "adminListReservations",
        method: "GET",
        path: `${ADMIN}/reservations`,
        query: {
          wallet_id: filter.wallet_id,
          application_id: filter.application_id,
          status: filter.status,
          // Only sent when true. `stale=false` would be a filter the server
          // has to have an opinion about; absent is simply "do not filter".
          stale: filter.stale ? "true" : undefined,
          limit: filter.limit,
        },
      }),

    listTopUps: (filter) =>
      transport.request<{ top_ups: TopUp[] }>({
        operationId: "adminListTopUps",
        method: "GET",
        path: `${ADMIN}/top-ups`,
        query: {
          wallet_id: filter.wallet_id,
          application_id: filter.application_id,
          status: filter.status,
          unsettled: filter.unsettled ? "true" : undefined,
          limit: filter.limit,
        },
      }),

    listSettlementFailures: (limit) =>
      transport.request<{ failures: SettlementFailure[] }>({
        operationId: "adminListSettlementFailures",
        method: "GET",
        path: `${ADMIN}/settlement-failures`,
        query: { limit },
      }),

    listAuditActions: (filter) =>
      transport.request<{ actions: AuditAction[] }>({
        operationId: "adminListAuditActions",
        method: "GET",
        path: `${ADMIN}/audit/actions`,
        query: {
          admin_user_id: filter.admin_user_id,
          resource_type: filter.resource_type,
          outcome: filter.outcome,
          action: filter.action,
          since: filter.since,
          until: filter.until,
          limit: filter.limit,
        },
      }),

    analyticsOverview: (query) =>
      transport.request<AnalyticsOverview>({
        operationId: "adminAnalyticsOverview",
        method: "GET",
        path: `${ADMIN}/analytics/overview`,
        // encodeQuery drops undefined, so an omitted argument sends no query
        // string and the server applies its own 30-day default.
        query: { since: query?.since, until: query?.until, days: query?.days },
      }),

    analyticsUsers: (query) =>
      transport.request<AnalyticsTopUsers>({
        operationId: "adminAnalyticsUsers",
        method: "GET",
        path: `${ADMIN}/analytics/users`,
        query: {
          since: query?.since,
          until: query?.until,
          days: query?.days,
          application: query?.application,
          sort: query?.sort,
          order: query?.order,
          limit: query?.limit,
        },
      }),

    analyticsUserDetail: (userID, query) =>
      transport.request<AnalyticsUserDetail>({
        operationId: "adminAnalyticsUserDetail",
        method: "GET",
        path: `${ADMIN}/analytics/users/${id(userID)}`,
        query: { since: query?.since, until: query?.until, days: query?.days },
      }),

    analyticsApplications: (query) =>
      transport.request<AnalyticsApplications>({
        operationId: "adminAnalyticsApplications",
        method: "GET",
        path: `${ADMIN}/analytics/applications`,
        query: {
          since: query?.since,
          until: query?.until,
          days: query?.days,
          application: query?.application,
        },
      }),

    analyticsRevenue: (query) =>
      transport.request<AnalyticsRevenue>({
        operationId: "adminAnalyticsRevenue",
        method: "GET",
        path: `${ADMIN}/analytics/revenue`,
        query: { since: query?.since, until: query?.until, days: query?.days },
      }),

    analyticsDrift: (query) =>
      transport.request<UsageDriftReport>({
        operationId: "adminAnalyticsDrift",
        method: "GET",
        path: `${ADMIN}/analytics/drift`,
        // encodeQuery drops undefined, so an unfiltered ask sends no query
        // string and the server applies its own defaults. `drifted` is sent
        // only when it is true for the same reason `state` is on the alert
        // list: `?drifted=false` and no parameter are the same question.
        query: {
          from: query?.from,
          to: query?.to,
          provider: query?.provider,
          drifted: query?.drifted ? "true" : undefined,
          limit: query?.limit,
        },
      }),

    listFailures: (query) =>
      transport.request<FailurePage>({
        operationId: "adminListFailures",
        method: "GET",
        path: `${ADMIN}/failures`,
        // encodeQuery drops undefined, so an omitted filter sends no query
        // string at all and the server applies its own defaults: the open set,
        // no window, fifty rows.
        query: failureQuery(query),
      }),

    failureSummary: (query) =>
      transport.request<FailureSummary>({
        operationId: "adminFailureSummary",
        method: "GET",
        path: `${ADMIN}/failures/summary`,
        // The SAME parameters as the list, off the same builder: the charts
        // and the table have to be reading one set, and two query builders is
        // where that quietly stops being true.
        query: failureQuery(query),
      }),

    getFailure: (failureID) =>
      transport.request<FailureDetail>({
        operationId: "adminGetFailure",
        method: "GET",
        path: `${ADMIN}/failures/${id(failureID)}`,
      }),

    acknowledgeFailure: (failureID, reason) =>
      transport.request<AcknowledgedFailure>({
        operationId: "adminAcknowledgeFailure",
        method: "POST",
        path: `${ADMIN}/failures/${id(failureID)}/acknowledge`,
        headers: reasonHeaders(reason),
      }),

    acknowledgeFailures: (failureIDs, reason) =>
      transport.request<FailureAcknowledgement>({
        operationId: "adminAcknowledgeFailures",
        method: "POST",
        path: `${ADMIN}/failures/acknowledge`,
        headers: reasonHeaders(reason),
        // `match_filter` is deliberately NOT sent alongside: the server refuses
        // a body carrying both, because they are two different asks with no
        // sensible intersection.
        body: { failure_ids: failureIDs },
      }),

    acknowledgeMatchingFailures: (query, reason) =>
      transport.request<FailureAcknowledgement>({
        operationId: "adminAcknowledgeMatchingFailures",
        method: "POST",
        path: `${ADMIN}/failures/acknowledge`,
        headers: reasonHeaders(reason),
        // The SAME builder the two lists use. A second query builder here is
        // exactly where "clear what I am looking at" would quietly stop being
        // true — and the symptom would be rows disappearing that were never on
        // screen. `limit` is dropped: it is a page size, it does not bound an
        // act, and the server refuses it rather than ignoring it.
        query: { ...failureQuery(query), limit: undefined },
        // The discriminator. A bare POST with neither form is REFUSED, so
        // "acknowledge everything that matches" is always a sentence somebody
        // typed rather than an empty body's default.
        body: { match_filter: true },
      }),

    listAlerts: (filter) =>
      transport.request<{ alerts: Alert[] }>({
        operationId: "adminListAlerts",
        method: "GET",
        path: `${ADMIN}/alerts`,
        // encodeQuery drops undefined, so an unfiltered ask sends no query
        // string at all and the server applies its own defaults: the open set,
        // fifty rows. `state` is sent only when it is "all" for that reason —
        // `?state=open` and no parameter are the same question.
        query: {
          kind: filter.kind,
          severity: filter.severity,
          state: filter.state === "all" ? "all" : undefined,
          account_id: filter.account_id,
          application_id: filter.application_id,
          limit: filter.limit,
        },
      }),

    getAlert: (alertID) =>
      transport.request<AlertDetail>({
        operationId: "adminGetAlert",
        method: "GET",
        path: `${ADMIN}/alerts/${id(alertID)}`,
      }),

    getAlertThreshold: (accountID, kind = "wallet_runout") =>
      transport.request<AlertThresholdEnvelope>({
        operationId: "adminGetAlertThreshold",
        method: "GET",
        path: `${ADMIN}/alerts/thresholds/${id(accountID)}`,
        query: { kind },
      }),

    setAlertThreshold: (accountID, body, reason) =>
      transport.request<AlertThresholdEnvelope>({
        operationId: "adminSetAlertThreshold",
        method: "PUT",
        path: `${ADMIN}/alerts/thresholds/${id(accountID)}`,
        // The kind travels in the BODY here and in the query string on the
        // read, because that is what the server takes on each. It is not a
        // detail a caller should have to know twice, which is why both are
        // written here once rather than at every screen.
        body,
        headers: reasonHeaders(reason),
      }),
  };
}

/**
 * failureQuery renders one filter for both failure lists.
 *
 * ONE builder for the two routes, on purpose. The charts and the table beneath
 * them are two views of a single set, and the moment each route builds its own
 * query string is the moment one of them can quietly stop carrying a filter the
 * other does — which shows up as a headline that disagrees with the rows under
 * it, and as nothing that looks like a bug.
 */
function failureQuery(query?: FailureQuery): Record<string, unknown> {
  return {
    service: query?.service,
    kind: query?.kind,
    severity: query?.severity,
    since: query?.since,
    until: query?.until,
    user: query?.user,
    device: query?.device,
    application: query?.application,
    acknowledged: query?.acknowledged,
    limit: query?.limit,
  };
}

/**
 * auditExportUrl is the CSV export's address, for an anchor the operator
 * presses — NOT a fetch.
 *
 * The export is a file download, and the browser already does downloads: it
 * carries the HttpOnly `esm_admin_session` cookie on a same-origin navigation,
 * names the file from the server's Content-Disposition, and streams it to disk
 * without the whole audit trail passing through this tab's memory. Fetching it
 * into a blob would do all of that worse.
 *
 * Relative on purpose, like every other path here: same origin, first-party
 * cookie, and no way for this function to be pointed at another host.
 */
export function auditExportUrl(filter: AuditFilter): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined && value !== "") {
      query.set(key, String(value));
    }
  }
  const search = query.toString();
  return `${ADMIN}/audit/actions.csv${search === "" ? "" : `?${search}`}`;
}

/**
 * revocationSummary turns the counts into the sentence an operator reads.
 *
 * "Ended 3 sessions, revoked 2 refresh families" rather than "done", because
 * the counts ARE the result: they are how an operator learns that suspending
 * one account signed eleven devices out, and how they answer the customer who
 * asks what just happened. A revocation that reports nothing leaves them
 * clicking the button again to find out whether it worked.
 */
export function revocationSummary(
  subject: string,
  counts: RevocationCounts,
): string {
  const parts: string[] = [];
  const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;
  if (counts.bindings_revoked) {
    parts.push(
      `revoked ${plural(counts.bindings_revoked, "application binding")}`,
    );
  }
  if (counts.sessions_ended) {
    parts.push(`ended ${plural(counts.sessions_ended, "application session")}`);
  }
  if (counts.sso_sessions_ended) {
    parts.push(`ended ${plural(counts.sso_sessions_ended, "browser sign-in")}`);
  }
  if (counts.refresh_families_revoked) {
    parts.push(
      `revoked ${plural(counts.refresh_families_revoked, "refresh family")}`,
    );
  }
  return parts.length === 0
    ? `${subject}: done.`
    : `${subject} — ${parts.join(", ")}.`;
}
